import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import PasswordConfirmModal from '../PasswordConfirmModal';
import { openDocEnhanced, setApiUrl } from '../../utils/openDocEnhanced';


const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
setApiUrl(API_URL);

const ALL_PERMISSIONS = [
  { key: 'manage_houses', label: 'Manage Houses', icon: '' },
  { key: 'manage_users', label: 'Manage Users', icon: '' },
  { key: 'manage_applications', label: 'Manage Applications', icon: '' },
  { key: 'manage_payments', label: 'Manage Payments', icon: '' },
  { key: 'manage_waitlist', label: 'Manage Interest Forms', icon: '' },
  { key: 'view_reports', label: 'View Reports', icon: '' },
  { key: 'manage_notifications', label: 'Manage Notifications', icon: '' },
  { key: 'manage_tickets', label: 'Manage Tickets', icon: '' },
  { key: 'manage_rentals', label: 'Manage Rentals', icon: '' },
  { key: 'manage_tenants', label: 'Manage Tenant Logs', icon: '' },
  { key: 'manage_admins', label: 'Manage Admins', icon: '' },
];

const APPLICATION_STAGE_META = {
  application: { label: 'Application Review', tone: 'bg-slate-100 text-slate-700', next: 'verification', nextLabel: 'Move to Verification' },
  verification: { label: 'Verification', tone: 'bg-blue-100 text-blue-700', next: 'upfront_payment', nextLabel: 'Request 30% Payment' },
  upfront_payment: { label: 'Pay 30%', tone: 'bg-amber-100 text-amber-700', next: 'direct_debit', nextLabel: 'Request Direct Debit Authorisation' },
  direct_debit: { label: 'Direct Debit', tone: 'bg-purple-100 text-purple-700', next: null, nextLabel: null }, // tenant must authorise  no admin skip
  active: { label: 'Active', tone: 'bg-green-100 text-green-700', next: null, nextLabel: null },
  rejected: { label: 'Rejected', tone: 'bg-red-100 text-red-700', next: null, nextLabel: null },
};

const VERIFICATION_CHECK_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'verified', label: 'Verified' },
  { value: 'flagged', label: 'Flagged' },
];

function verificationTone(status) {
  return status === 'verified'
    ? 'bg-emerald-100 text-emerald-700'
    : status === 'flagged'
      ? 'bg-red-100 text-red-700'
      : 'bg-amber-100 text-amber-700';
}


/**
 * Enhanced Document Viewer
 * Provides inline viewing for PDFs, Word documents, and other file types
 * with a modal interface similar to the image lightbox
 */

//  openDoc delegates to openDocEnhanced for inline modal viewing 
function openDoc(rawUrl) {
  openDocEnhanced(rawUrl);
}



//  DebitAuditLog  direct debit changes visible to admin 
function DebitAuditLog({ API_URL, headers }) {
  const [logs, setLogs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    axios.get(`${API_URL}/api/admin/direct-debit/audit`, { headers })
      .then(res => setLogs(res.data.logs || []))
      .catch(() => setError('Could not load audit log.'))
      .finally(() => setLoading(false));
  }, []);

  const eventLabel = type => ({
    mandate_authorised: { label: 'Mandate Authorised', color: 'bg-green-100 text-green-700', icon: '' },
    account_changed:    { label: 'Account Changed',    color: 'bg-blue-100 text-blue-700',  icon: '' },
  }[type] || { label: type, color: 'bg-gray-100 text-gray-600', icon: '' });

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-spin h-10 w-10 border-4 border-[#294c79] border-t-transparent rounded-full" /></div>;
  if (error)   return <div className="bg-red-50 border border-red-200 p-8 rounded-2xl text-center text-red-600">{error}</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold text-[#294c79]">Direct Debit Log</h1>
          <p className="text-sm text-gray-500 mt-1">All mandate authorisations and account changes across all tenants</p>
        </div>
        <span className="px-4 py-2 bg-[#e8f2d1] text-[#294c79] rounded-xl text-sm font-semibold">{logs.length} event{logs.length !== 1 ? 's' : ''}</span>
      </div>

      {logs.length === 0 ? (
        <div className="bg-white p-12 rounded-2xl shadow-sm text-center">
          <p className="text-5xl mb-4"></p>
          <p className="text-xl text-gray-500">No direct debit events yet</p>
          <p className="text-sm text-gray-400 mt-2">Events will appear here when tenants authorise or change their direct debit accounts</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-max w-full text-sm">
              <thead className="bg-[#e8f2d1]">
                <tr>
                  {['Date & Time', 'Tenant', 'Property', 'Event', 'Details', 'Reference'].map(h => (
                    <th key={h} className="px-5 py-4 text-left text-xs font-semibold text-[#294c79] uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.map(log => {
                  const ev = eventLabel(log.event_type);
                  return (
                    <tr key={log.id} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-5 py-4 text-gray-500 whitespace-nowrap text-xs">
                        {new Date(log.created_at).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' })}
                        <br />
                        <span className="text-gray-400">{new Date(log.created_at).toLocaleTimeString('en-NG', { hour: '2-digit', minute: '2-digit' })}</span>
                      </td>
                      <td className="px-5 py-4">
                        <p className="font-semibold text-[#294c79]">{log.tenant_name || ''}</p>
                        <p className="text-xs text-gray-400">{log.tenant_email}</p>
                      </td>
                      <td className="px-5 py-4 text-gray-700">{log.house_title || ''}</td>
                      <td className="px-5 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${ev.color}`}>
                          {ev.icon} {ev.label}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-xs text-gray-500 max-w-xs">
                        {log.event_type === 'account_changed' ? (
                          <div>
                            <p className="text-red-500 line-through">Old: ****{String(log.old_auth_code || '').slice(-6)}</p>
                            <p className="text-green-600">New: ****{String(log.new_auth_code || '').slice(-6)}</p>
                          </div>
                        ) : (
                          <p>{log.notes || ''}</p>
                        )}
                      </td>
                      <td className="px-5 py-4 font-mono text-xs text-gray-400 whitespace-nowrap">{log.reference || ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

//  DebitChangeRequests  admin reviews tenant account change requests 
function DebitChangeRequests({ API_URL, headers }) {
  const [requests, setRequests] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [actionLoading, setActionLoading] = React.useState({});
  const [adminNote, setAdminNote] = React.useState({});

  const load = () => {
    setLoading(true);
    axios.get(`${API_URL}/api/admin/debit-change-requests`, { headers })
      .then(r => setRequests(r.data.requests || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  React.useEffect(() => { load(); }, []);

  const handleAction = async (id, action) => {
    setActionLoading(p => ({ ...p, [id]: action }));
    try {
      await axios.post(`${API_URL}/api/admin/debit-change-requests/${id}/${action}`,
        { adminNote: adminNote[id] || '' }, { headers });
      load();
    } catch (err) {
      alert(err.response?.data?.error || `Failed to ${action} request.`);
    } finally {
      setActionLoading(p => ({ ...p, [id]: null }));
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-spin h-10 w-10 border-4 border-[#294c79] border-t-transparent rounded-full" /></div>;

  const pending = requests.filter(r => r.status === 'pending');
  const reviewed = requests.filter(r => r.status !== 'pending');

  return (
    <div>
      <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold text-[#294c79]">Account Change Requests</h1>
          <p className="text-sm text-gray-500 mt-1">Tenant requests to change their direct debit bank account</p>
        </div>
        {pending.length > 0 && <span className="px-4 py-2 bg-amber-100 text-amber-700 rounded-xl text-sm font-semibold">{pending.length} pending review</span>}
      </div>

      {requests.length === 0 ? (
        <div className="bg-white p-12 rounded-2xl shadow-sm text-center">
          <p className="text-5xl mb-4"></p>
          <p className="text-xl text-gray-500">No account change requests yet</p>
        </div>
      ) : (
        <div className="space-y-6">
          {pending.length > 0 && (
            <div>
              <h2 className="text-lg font-bold text-[#294c79] mb-3"> Pending Review ({pending.length})</h2>
              <div className="space-y-4">
                {pending.map(r => (
                  <div key={r.id} className="bg-white rounded-2xl shadow-sm border-2 border-amber-200 p-6">
                    <div className="flex flex-wrap justify-between gap-4 mb-4">
                      <div>
                        <p className="font-bold text-[#294c79] text-base">{r.tenant_name}</p>
                        <p className="text-sm text-gray-500">{r.tenant_email}  {r.tenant_phone}</p>
                        <p className="text-sm text-gray-600 mt-1">{r.house_title}</p>
                        <p className="text-xs text-gray-400 mt-1">Requested {new Date(r.created_at).toLocaleString('en-NG')}</p>
                      </div>
                      <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold h-fit">Pending</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4 text-sm">
                      <div className="bg-gray-50 rounded-xl p-3"><p className="text-xs text-gray-400 mb-1">New Bank</p><p className="font-semibold">{r.new_bank_name||''}</p></div>
                      <div className="bg-gray-50 rounded-xl p-3"><p className="text-xs text-gray-400 mb-1">New Account</p><p className="font-semibold">{r.new_account_name||''}  ****{String(r.new_account_number||'').slice(-4)}</p></div>
                      <div className="bg-gray-50 rounded-xl p-3"><p className="text-xs text-gray-400 mb-1">Reason</p><p className="font-semibold">{r.reason||'Not specified'}</p></div>
                    </div>
                    <div className="mb-3">
                      <input type="text" placeholder="Admin note (optional  will be sent to tenant)" value={adminNote[r.id]||''} onChange={e => setAdminNote(p => ({ ...p, [r.id]: e.target.value }))} className="w-full px-4 py-2.5 border border-[#d9e5ef] rounded-xl text-sm focus:outline-none focus:border-[#294c79]" />
                    </div>
                    <div className="flex gap-3">
                      <button onClick={() => handleAction(r.id, 'approve')} disabled={!!actionLoading[r.id]} className="flex-1 px-5 py-2.5 bg-[#294c79] text-white rounded-xl font-semibold text-sm hover:bg-[#1f3a5c] disabled:opacity-50 transition">
                        {actionLoading[r.id]==='approve' ? ' Approving' : ' Approve  Send Paystack Link to Tenant'}
                      </button>
                      <button onClick={() => handleAction(r.id, 'reject')} disabled={!!actionLoading[r.id]} className="px-5 py-2.5 bg-red-600 text-white rounded-xl font-semibold text-sm hover:bg-red-700 disabled:opacity-50 transition">
                        {actionLoading[r.id]==='reject' ? '' : ' Reject'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {reviewed.length > 0 && (
            <div>
              <h2 className="text-lg font-bold text-[#294c79] mb-3">History ({reviewed.length})</h2>
              <div className="bg-white rounded-2xl shadow-sm overflow-x-auto" data-table-scroll>
                <table className="min-w-max w-full text-sm">
                  <thead className="bg-gray-50"><tr>{['Date','Tenant','Property','New Bank','New Account','Reason','Status','Reviewed By'].map(h=><th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>)}</tr></thead>
                  <tbody>
                    {reviewed.map(r => (
                      <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">{new Date(r.created_at).toLocaleDateString('en-NG')}</td>
                        <td className="px-4 py-3"><p className="font-semibold text-[#294c79]">{r.tenant_name}</p><p className="text-xs text-gray-400">{r.tenant_email}</p></td>
                        <td className="px-4 py-3 text-sm text-gray-600">{r.house_title}</td>
                        <td className="px-4 py-3 text-sm">{r.new_bank_name||''}</td>
                        <td className="px-4 py-3 text-sm">{r.new_account_name||''}</td>
                        <td className="px-4 py-3 text-xs text-gray-500 max-w-xs">{r.reason||''}</td>
                        <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs font-semibold ${r.status==='approved'?'bg-green-100 text-green-700':'bg-red-100 text-red-700'}`}>{r.status}</span></td>
                        <td className="px-4 py-3 text-xs text-gray-500">{r.reviewed_by_name||''}<br/>{r.reviewed_at?new Date(r.reviewed_at).toLocaleDateString('en-NG'):''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

//  TenantActivityLogs  full activity log per tenant 
function TenantActivityLogs({ API_URL, headers }) {
  const [tenants, setTenants] = React.useState([]);
  const [selectedTenant, setSelectedTenant] = React.useState(null);
  const [logs, setLogs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [logLoading, setLogLoading] = React.useState(false);
  const [search, setSearch] = React.useState('');

  React.useEffect(() => {
    axios.get(`${API_URL}/api/admin/users`, { headers })
      .then(r => setTenants((r.data.users || []).filter(u => u.role === 'tenant')))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const loadLogs = async (tenant) => {
    setSelectedTenant(tenant);
    setLogLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/tenants/${tenant.id}/activity`, { headers });
      setLogs(r.data.logs || []);
    } catch { setLogs([]); }
    finally { setLogLoading(false); }
  };

  const downloadLog = (tenant) => {
    const token = headers.Authorization?.split(' ')[1];
    if (!token) return;
    const url = `${API_URL}/api/admin/tenants/${tenant.id}/activity/download?token=${encodeURIComponent(token)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const filtered = tenants.filter(t => !search || t.full_name?.toLowerCase().includes(search.toLowerCase()) || t.email?.toLowerCase().includes(search.toLowerCase()));

  const eventIcon = type => ({ application_submitted:'', upfront_payment_completed:'', direct_debit_authorised:'', monthly_debit_success:'', monthly_debit_failed:'', debit_change_requested:'', debit_change_approved:'', debit_change_rejected:'' }[type] || '');

  return (
    <div className="flex gap-6 h-full">
      {/* Tenant list */}
      <div className="w-72 flex-shrink-0">
        <h1 className="text-xl font-bold text-[#294c79] mb-4">Tenant Activity Logs</h1>
        <input type="text" placeholder="Search tenant" value={search} onChange={e => setSearch(e.target.value)} className="w-full px-4 py-2.5 border border-[#d9e5ef] rounded-xl text-sm focus:outline-none focus:border-[#294c79] mb-4" />
        {loading ? <div className="text-center py-8 text-gray-400">Loading</div> : (
          <div className="space-y-2 max-h-[70vh] overflow-y-auto">
            {filtered.map(t => (
              <button key={t.id} onClick={() => loadLogs(t)} className={`w-full text-left px-4 py-3 rounded-xl border transition ${selectedTenant?.id===t.id?'border-[#294c79] bg-[#f0f5ff]':'border-gray-200 bg-white hover:border-[#294c79]/40'}`}>
                <p className="font-semibold text-[#294c79] text-sm truncate">{t.full_name}</p>
                <p className="text-xs text-gray-400 truncate">{t.email}</p>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Activity log panel */}
      <div className="flex-1 min-w-0">
        {!selectedTenant ? (
          <div className="bg-white rounded-2xl shadow-sm p-12 text-center h-full flex items-center justify-center">
            <div><p className="text-5xl mb-4"></p><p className="text-xl text-gray-500">Select a tenant to view their activity log</p></div>
          </div>
        ) : (
          <div>
            <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
              <div>
                <h2 className="text-xl font-bold text-[#294c79]">{selectedTenant.full_name}</h2>
                <p className="text-sm text-gray-500">{selectedTenant.email}  {logs.length} events</p>
              </div>
              <button onClick={() => downloadLog(selectedTenant)} className="px-5 py-2.5 bg-[#294c79] text-white rounded-xl text-sm font-semibold hover:bg-[#1f3a5c] transition">
                 Download Full Report
              </button>
            </div>
            {logLoading ? <div className="flex justify-center py-20"><div className="animate-spin h-10 w-10 border-4 border-[#294c79] border-t-transparent rounded-full" /></div> : logs.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-sm p-10 text-center"><p className="text-gray-400">No activity logged yet for this tenant.</p></div>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <div className="overflow-y-auto max-h-[65vh]">
                  <table className="min-w-max w-full text-sm">
                    <thead className="bg-[#e8f2d1] sticky top-0">
                      <tr>{['Date & Time','Event','Detail'].map(h=><th key={h} className="px-4 py-3 text-left text-xs font-semibold text-[#294c79] uppercase">{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {logs.map(l => (
                        <tr key={l.id} className="border-t border-gray-100 hover:bg-gray-50">
                          <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">
                            {new Date(l.created_at).toLocaleDateString('en-NG',{day:'numeric',month:'short',year:'numeric'})}<br/>
                            <span className="text-gray-300">{new Date(l.created_at).toLocaleTimeString('en-NG',{hour:'2-digit',minute:'2-digit'})}</span>
                          </td>
                          <td className="px-4 py-3 font-semibold text-[#294c79] whitespace-nowrap">
                            {eventIcon(l.event_type)} {l.event_title}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 max-w-md">{l.event_detail||''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function AuditLogsTab({ API_URL, headers }) {
  const [logs, setLogs] = React.useState([]);
  const [summary, setSummary] = React.useState({ total: 0, failed: 0, security_events: 0, last_24h: 0 });
  const [filters, setFilters] = React.useState({ role: 'all', status: 'all', eventType: '', q: '' });
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        role: filters.role,
        status: filters.status,
        eventType: filters.eventType,
        q: filters.q,
        limit: '500',
      });
      const res = await axios.get(`${API_URL}/api/admin/audit-logs?${params.toString()}`, { headers });
      setLogs(res.data.logs || []);
      setSummary(res.data.summary || {});
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load audit logs.');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.role, filters.status, filters.eventType, filters.q]);

  const downloadCsv = async () => {
    try {
      const params = new URLSearchParams({
        role: filters.role,
        status: filters.status,
        eventType: filters.eventType,
        q: filters.q,
        limit: '5000',
      });
      const res = await axios.get(`${API_URL}/api/admin/audit-logs/download?${params.toString()}`, {
        headers,
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `literent-audit-logs-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not download audit logs.');
    }
  };

  const statusTone = (status) => status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700';

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold text-[#294c79]">Security Audit Logs</h1>
          <p className="text-sm text-gray-500 mt-1">Track logins, failed attempts, permissions, OTPs, tenant actions, and admin actions.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={downloadCsv} className="px-4 py-2 bg-white border border-[#294c79] text-[#294c79] rounded-xl text-sm font-semibold hover:bg-[#f3f7fb]">Download CSV</button>
          <button onClick={load} className="px-4 py-2 bg-[#294c79] text-white rounded-xl text-sm font-semibold hover:bg-[#1f3a5c]">Refresh</button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {[
          ['Total Logs', summary.total || 0, 'bg-white'],
          ['Failed Events', summary.failed || 0, 'bg-red-50'],
          ['Security Events', summary.security_events || 0, 'bg-amber-50'],
          ['Last 24 Hours', summary.last_24h || 0, 'bg-blue-50'],
        ].map(([label, value, tone]) => (
          <div key={label} className={`${tone} rounded-2xl p-4 shadow-sm border border-gray-100`}>
            <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
            <div className="text-2xl font-bold text-[#294c79] mt-1">{Number(value).toLocaleString()}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-4 mb-6 grid grid-cols-1 md:grid-cols-4 gap-3">
        <select value={filters.role} onChange={e => setFilters(f => ({ ...f, role: e.target.value }))} className="px-3 py-2 border border-gray-200 rounded-xl text-sm">
          <option value="all">All roles</option>
          <option value="guest">Guest</option>
          <option value="tenant">Tenant</option>
          <option value="admin">Admin</option>
          <option value="super_admin">Super Admin</option>
        </select>
        <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))} className="px-3 py-2 border border-gray-200 rounded-xl text-sm">
          <option value="all">All statuses</option>
          <option value="success">Success</option>
          <option value="failed">Failed</option>
        </select>
        <input value={filters.eventType} onChange={e => setFilters(f => ({ ...f, eventType: e.target.value }))} placeholder="Event type" className="px-3 py-2 border border-gray-200 rounded-xl text-sm" />
        <input value={filters.q} onChange={e => setFilters(f => ({ ...f, q: e.target.value }))} placeholder="Search user, detail, reason" className="px-3 py-2 border border-gray-200 rounded-xl text-sm" />
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">{error}</div>}

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-gray-400">Loading audit logs...</div>
        ) : logs.length === 0 ? (
          <div className="p-10 text-center text-gray-400">No audit logs found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-max w-full text-sm">
              <thead className="bg-[#e8f2d1]">
                <tr>{['Time', 'Actor', 'Event', 'Status', 'Target', 'IP / Device', 'Detail'].map(h => <th key={h} className="px-4 py-3 text-left font-semibold text-[#294c79] whitespace-nowrap">{h}</th>)}</tr>
              </thead>
              <tbody>
                {logs.map(log => (
                  <tr key={log.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{new Date(log.created_at).toLocaleString('en-NG', { dateStyle: 'medium', timeStyle: 'short' })}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-[#294c79]">{log.actor_name || log.actor_email || 'Guest'}</div>
                      <div className="text-xs text-gray-400">{log.actor_role || 'guest'}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{log.event_title}</div>
                      <div className="text-xs text-gray-400">{log.event_type}</div>
                    </td>
                    <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusTone(log.status)}`}>{log.status}</span></td>
                    <td className="px-4 py-3 text-xs text-gray-500">{log.target_type || ''}{log.target_id ? ` #${log.target_id}` : ''}</td>
                    <td className="px-4 py-3 text-xs text-gray-500 max-w-[220px]">
                      <div>{log.ip_address || ''}</div>
                      <div className="truncate">{log.user_agent || ''}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 max-w-md">
                      <div className="line-clamp-2">{log.failure_reason || log.event_detail || ''}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

//  LandlordsTab 
function LandlordsTab({ API_URL, headers, landlords, setLandlords, selectedLandlord, setSelectedLandlord, landlordPayments, setLandlordPayments, loadLandlords, loadLandlordPayments, setSensitiveConfirm }) {
  const [form, setForm] = React.useState({ fullName:'', phone:'', email:'', address:'', bankName:'', accountNumber:'', accountName:'', notes:'' });
  const [showForm, setShowForm] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [paymentForm, setPaymentForm] = React.useState({ houseId:'', amount:'', description:'', status:'pending', dueDate:'', paymentReference:'', notes:'' });
  const [showPaymentForm, setShowPaymentForm] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [msg, setMsg] = React.useState('');

  const resetForm = () => setForm({ fullName:'', phone:'', email:'', address:'', bankName:'', accountNumber:'', accountName:'', notes:'' });

  const openAdd = () => { resetForm(); setEditing(null); setShowForm(true); setMsg(''); };
  const openEdit = (l) => {
    setForm({ fullName: l.full_name||'', phone: l.phone||'', email: l.email||'', address: l.address||'', bankName: l.bank_name||'', accountNumber: l.account_number||'', accountName: l.account_name||'', notes: l.notes||'' });
    setEditing(l);
    setShowForm(true);
    setMsg('');
  };

  const handleSave = async () => {
    if (!form.fullName.trim()) { setMsg('Full name is required.'); return; }
    setSaving(true); setMsg('');
    try {
      if (editing) {
        await axios.put(`${API_URL}/api/admin/landlords/${editing.id}`, { fullName: form.fullName, phone: form.phone, email: form.email, address: form.address, bankName: form.bankName, accountNumber: form.accountNumber, accountName: form.accountName, notes: form.notes }, { headers });
      } else {
        await axios.post(`${API_URL}/api/admin/landlords`, { fullName: form.fullName, phone: form.phone, email: form.email, address: form.address, bankName: form.bankName, accountNumber: form.accountNumber, accountName: form.accountName, notes: form.notes }, { headers });
      }
      await loadLandlords();
      setShowForm(false);
      resetForm();
    } catch (err) { setMsg(err.response?.data?.error || 'Save failed.'); }
    finally { setSaving(false); }
  };

  const handleSelectLandlord = async (l) => {
    setSelectedLandlord(l);
    setShowPaymentForm(false);
    await loadLandlordPayments(l.id);
  };

  const handleAddPayment = async () => {
    if (!paymentForm.amount) { setMsg('Amount is required.'); return; }
    setSaving(true); setMsg('');
    try {
      await axios.post(`${API_URL}/api/admin/landlords/${selectedLandlord.id}/payments`, { amount: paymentForm.amount, description: paymentForm.description, status: paymentForm.status, dueDate: paymentForm.dueDate || undefined, paymentReference: paymentForm.paymentReference, notes: paymentForm.notes }, { headers });
      await loadLandlordPayments(selectedLandlord.id);
      await loadLandlords();
      setShowPaymentForm(false);
      setPaymentForm({ houseId:'', amount:'', description:'', status:'pending', dueDate:'', paymentReference:'', notes:'' });
    } catch (err) { setMsg(err.response?.data?.error || 'Failed to add payment.'); }
    finally { setSaving(false); }
  };

  const markPaid = async (paymentId) => {
    try {
      await axios.put(`${API_URL}/api/admin/landlord-payments/${paymentId}`, { status: 'paid' }, { headers });
      await loadLandlordPayments(selectedLandlord.id);
      await loadLandlords();
    } catch (err) { alert('Could not update payment.'); }
  };

  const handleDeleteLandlord = (landlord) => {
    setSensitiveConfirm({
      title: 'Delete Landlord',
      message: `Permanently delete ${landlord.full_name || 'this landlord'}? Linked houses will remain but will no longer be attached to this landlord.`,
      confirmButtonLabel: 'Delete Landlord',
      onConfirm: async (sensitiveActionToken) => {
        try {
          await axios.delete(`${API_URL}/api/admin/landlords/${landlord.id}`, {
            headers: { ...headers, 'X-Sensitive-Token': sensitiveActionToken },
          });
          setSelectedLandlord(null);
          setLandlordPayments([]);
          await loadLandlords();
          setSensitiveConfirm(null);
        } catch (err) {
          setMsg(err.response?.data?.error || 'Could not delete landlord.');
        }
      },
    });
  };

  const fmtN = (n) => `${Number(n||0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}`;
  const statusBadge = (s) => ({ paid: 'bg-green-100 text-green-700', pending: 'bg-amber-100 text-amber-700', overdue: 'bg-red-100 text-red-700' }[s] || 'bg-gray-100 text-gray-600');

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold text-[#294c79]">Landlords</h1>
          <p className="text-sm text-gray-500 mt-1">Track landlords whose properties you list and manage payments to them.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={loadLandlords} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 text-sm font-medium"> Refresh</button>
          <button onClick={openAdd} className="px-4 py-2 bg-[#294c79] text-white rounded-xl hover:bg-[#1f3a5c] text-sm font-medium">+ Add Landlord</button>
        </div>
      </div>

      {msg && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">{msg}</div>}

      {/* Add/Edit Form */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-[#294c79] mb-4">{editing ? 'Edit Landlord' : 'Add Landlord'}</h2>
            {[['Full Name *','fullName','text'],['Phone','phone','tel'],['Email','email','email'],['Address','address','text'],['Bank Name','bankName','text'],['Account Number','accountNumber','text'],['Account Name','accountName','text']].map(([label, key, type]) => (
              <div key={key} className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                <input type={type} value={form[key]} onChange={e => setForm(f => ({...f, [key]: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#294c79]" />
              </div>
            ))}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} rows={3}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#294c79]" />
            </div>
            {msg && <p className="text-red-600 text-sm mb-3">{msg}</p>}
            <div className="flex gap-3">
              <button onClick={() => setShowForm(false)} className="flex-1 px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="flex-1 px-4 py-2 bg-[#294c79] text-white rounded-xl text-sm font-medium hover:bg-[#1f3a5c] disabled:opacity-50">{saving ? 'Saving' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Landlord List */}
        <div className="lg:col-span-1 space-y-3">
          {landlords.length === 0 ? (
            <div className="bg-white rounded-2xl p-10 text-center shadow-sm">
              <div className="text-5xl mb-3"></div>
              <p className="text-gray-500 font-semibold">No landlords yet</p>
              <p className="text-sm text-gray-400 mt-1">Add landlords whose properties you manage</p>
            </div>
          ) : landlords.map(l => (
            <div key={l.id} onClick={() => handleSelectLandlord(l)}
              className={`bg-white rounded-2xl p-4 shadow-sm cursor-pointer border-2 transition hover:shadow-md ${selectedLandlord?.id === l.id ? 'border-[#294c79]' : 'border-transparent'}`}>
              <div className="flex justify-between items-start gap-2">
                <div>
                  <div className="font-semibold text-[#294c79]">{l.full_name}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{l.phone || l.email || ''}</div>
                </div>
                <div className="flex items-center gap-1">
                  <span className={`text-xs px-2 py-1 rounded-full font-semibold ${l.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{l.status}</span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleDeleteLandlord(l); }}
                    className="px-2 py-1 rounded-lg bg-red-50 text-red-600 text-xs font-semibold hover:bg-red-100"
                  >
                    Delete
                  </button>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div className="bg-gray-50 rounded-lg p-2"><div className="text-gray-400">Properties</div><div className="font-bold text-[#294c79]">{l.house_count || 0}</div></div>
                <div className="bg-amber-50 rounded-lg p-2"><div className="text-amber-600">Pending</div><div className="font-bold text-amber-700">{fmtN(l.total_pending)}</div></div>
              </div>
              <div className="mt-2 text-xs bg-green-50 rounded-lg p-2"><span className="text-gray-400">Total Paid:</span> <span className="font-bold text-green-700">{fmtN(l.total_paid)}</span></div>
            </div>
          ))}
        </div>

        {/* Landlord Detail */}
        <div className="lg:col-span-2">
          {!selectedLandlord ? (
            <div className="bg-white rounded-2xl p-10 text-center shadow-sm">
              <div className="text-4xl mb-3"></div>
              <p className="text-gray-400">Select a landlord to view details and payments</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="bg-[#294c79] p-5 text-white flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-bold">{selectedLandlord.full_name}</h2>
                  <p className="text-sm opacity-80 mt-0.5">{selectedLandlord.email} {selectedLandlord.phone ? ` ${selectedLandlord.phone}` : ''}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => openEdit(selectedLandlord)} className="px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white rounded-lg text-xs font-medium"> Edit</button>
                  <button onClick={() => handleDeleteLandlord(selectedLandlord)} className="px-3 py-1.5 bg-red-500/90 hover:bg-red-600 text-white rounded-lg text-xs font-medium">Delete</button>
                  <button onClick={() => { setShowPaymentForm(true); setMsg(''); }} className="px-3 py-1.5 bg-[#a5c250] hover:bg-[#8aaa30] text-white rounded-lg text-xs font-medium">+ Record Payment</button>
                </div>
              </div>

              {/* Bank info */}
              {selectedLandlord.bank_name && (
                <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex flex-wrap gap-4 text-sm">
                  <span><span className="text-gray-400">Bank:</span> <strong>{selectedLandlord.bank_name}</strong></span>
                  <span><span className="text-gray-400">Account:</span> <strong>{selectedLandlord.account_name}</strong>  {selectedLandlord.account_number}</span>
                </div>
              )}

              {/* Record payment form */}
              {showPaymentForm && (
                <div className="p-5 border-b border-gray-100 bg-amber-50">
                  <h3 className="font-semibold text-[#294c79] mb-3">Record Payment to Landlord</h3>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    {[['Amount (NGN) *','amount','number'],['Description','description','text'],['Due Date','dueDate','date'],['Payment Reference','paymentReference','text']].map(([label, key, type]) => (
                      <div key={key}>
                        <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
                        <input type={type} value={paymentForm[key]} onChange={e => setPaymentForm(f => ({...f, [key]: e.target.value}))}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#294c79]" />
                      </div>
                    ))}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                      <select value={paymentForm.status} onChange={e => setPaymentForm(f => ({...f, status: e.target.value}))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#294c79]">
                        <option value="pending">Pending</option>
                        <option value="paid">Paid</option>
                        <option value="overdue">Overdue</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                      <input type="text" value={paymentForm.notes} onChange={e => setPaymentForm(f => ({...f, notes: e.target.value}))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#294c79]" />
                    </div>
                  </div>
                  {msg && <p className="text-red-600 text-xs mb-2">{msg}</p>}
                  <div className="flex gap-2">
                    <button onClick={() => setShowPaymentForm(false)} className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
                    <button onClick={handleAddPayment} disabled={saving} className="px-4 py-2 bg-[#294c79] text-white rounded-lg text-sm font-medium hover:bg-[#1f3a5c] disabled:opacity-50">{saving ? 'Saving' : 'Record Payment'}</button>
                  </div>
                </div>
              )}

              {/* Payments list */}
              <div className="p-5">
                <h3 className="font-semibold text-[#294c79] mb-3">Payment History</h3>
                {landlordPayments.length === 0 ? (
                  <p className="text-gray-400 text-sm text-center py-8">No payments recorded yet</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-max w-full text-sm">
                      <thead><tr className="bg-gray-50 border-b border-gray-100">
                        {['Date','Description','Amount','Due Date','Reference','Status','Action'].map(h => (
                          <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-600">{h}</th>
                        ))}
                      </tr></thead>
                      <tbody>
                        {landlordPayments.map(p => (
                          <tr key={p.id} className="border-b border-gray-50 hover:bg-gray-50">
                            <td className="px-3 py-2 text-gray-500 whitespace-nowrap text-xs">{p.created_at ? new Date(p.created_at).toLocaleDateString('en-NG') : ''}</td>
                            <td className="px-3 py-2 text-gray-700">{p.description || ''}</td>
                            <td className="px-3 py-2 font-semibold text-[#294c79] whitespace-nowrap">{fmtN(p.amount)}</td>
                            <td className="px-3 py-2 text-gray-500 whitespace-nowrap text-xs">{p.due_date ? new Date(p.due_date).toLocaleDateString('en-NG') : ''}</td>
                            <td className="px-3 py-2 text-gray-400 text-xs">{p.payment_reference || ''}</td>
                            <td className="px-3 py-2"><span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusBadge(p.status)}`}>{p.status}</span></td>
                            <td className="px-3 py-2">
                              {p.status !== 'paid' && (
                                <button onClick={() => markPaid(p.id)} className="px-2 py-1 bg-green-600 text-white rounded-lg text-xs hover:bg-green-700 font-medium">Mark Paid</button>
                              )}
                              {p.status === 'paid' && p.paid_at && <span className="text-xs text-gray-400"> {new Date(p.paid_at).toLocaleDateString('en-NG')}</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

//  MicrofinanceTab 
function MicrofinanceTab({ API_URL, headers, mfbs, setMFBs, selectedMFB, setSelectedMFB, mfbDisbursements, setMFBDisbursements, loadMFBs, loadMFBDisbursements, setSensitiveConfirm }) {
  const [form, setForm] = React.useState({ name:'', contactPerson:'', phone:'', email:'', address:'', partnershipType:'', creditLimit:'', interestRate:'', notes:'' });
  const [showForm, setShowForm] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [disbForm, setDisbForm] = React.useState({ amount:'', description:'', status:'pending', notes:'' });
  const [showDisbForm, setShowDisbForm] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [msg, setMsg] = React.useState('');

  const resetForm = () => setForm({ name:'', contactPerson:'', phone:'', email:'', address:'', partnershipType:'', creditLimit:'', interestRate:'', notes:'' });

  const openAdd = () => { resetForm(); setEditing(null); setShowForm(true); setMsg(''); };
  const openEdit = (m) => {
    setForm({ name: m.name||'', contactPerson: m.contact_person||'', phone: m.phone||'', email: m.email||'', address: m.address||'', partnershipType: m.partnership_type||'', creditLimit: m.credit_limit||'', interestRate: m.interest_rate||'', notes: m.notes||'' });
    setEditing(m); setShowForm(true); setMsg('');
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setMsg('Bank name is required.'); return; }
    setSaving(true); setMsg('');
    try {
      const payload = { name: form.name, contactPerson: form.contactPerson, phone: form.phone, email: form.email, address: form.address, partnershipType: form.partnershipType, creditLimit: form.creditLimit || undefined, interestRate: form.interestRate || undefined, notes: form.notes };
      if (editing) {
        await axios.put(`${API_URL}/api/admin/mfbs/${editing.id}`, payload, { headers });
      } else {
        await axios.post(`${API_URL}/api/admin/mfbs`, payload, { headers });
      }
      await loadMFBs();
      setShowForm(false);
      resetForm();
    } catch (err) { setMsg(err.response?.data?.error || 'Save failed.'); }
    finally { setSaving(false); }
  };

  const handleSelectMFB = async (m) => {
    setSelectedMFB(m);
    setShowDisbForm(false);
    await loadMFBDisbursements(m.id);
  };

  const handleAddDisb = async () => {
    if (!disbForm.amount) { setMsg('Amount is required.'); return; }
    setSaving(true); setMsg('');
    try {
      await axios.post(`${API_URL}/api/admin/mfbs/${selectedMFB.id}/disbursements`, { amount: disbForm.amount, description: disbForm.description, status: disbForm.status, notes: disbForm.notes }, { headers });
      await loadMFBDisbursements(selectedMFB.id);
      await loadMFBs();
      setShowDisbForm(false);
      setDisbForm({ amount:'', description:'', status:'pending', notes:'' });
    } catch (err) { setMsg(err.response?.data?.error || 'Failed.'); }
    finally { setSaving(false); }
  };

  const updateDisbursement = async (id, status, repaymentAmount) => {
    try {
      await axios.put(`${API_URL}/api/admin/mfb-disbursements/${id}`, { status, repaymentAmount: repaymentAmount || undefined }, { headers });
      await loadMFBDisbursements(selectedMFB.id);
      await loadMFBs();
    } catch (err) { alert('Could not update.'); }
  };

  const handleDeleteMFB = (mfb) => {
    setSensitiveConfirm({
      title: 'Delete MFB',
      message: `Permanently delete ${mfb.name || 'this MFB'} and its disbursement records? This cannot be undone.`,
      confirmButtonLabel: 'Delete MFB',
      onConfirm: async (sensitiveActionToken) => {
        try {
          await axios.delete(`${API_URL}/api/admin/mfbs/${mfb.id}`, {
            headers: { ...headers, 'X-Sensitive-Token': sensitiveActionToken },
          });
          setSelectedMFB(null);
          setMFBDisbursements([]);
          await loadMFBs();
          setSensitiveConfirm(null);
        } catch (err) {
          setMsg(err.response?.data?.error || 'Could not delete MFB.');
        }
      },
    });
  };

  const fmtN = (n) => `${Number(n||0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}`;
  const disbBadge = (s) => ({ disbursed: 'bg-blue-100 text-blue-700', repaid: 'bg-green-100 text-green-700', pending: 'bg-amber-100 text-amber-700', defaulted: 'bg-red-100 text-red-700' }[s] || 'bg-gray-100 text-gray-600');

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold text-[#294c79]">Microfinance Banks</h1>
          <p className="text-sm text-gray-500 mt-1">Track MFB partnerships, credit lines, and disbursements/repayments.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={loadMFBs} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 text-sm font-medium"> Refresh</button>
          <button onClick={openAdd} className="px-4 py-2 bg-[#294c79] text-white rounded-xl hover:bg-[#1f3a5c] text-sm font-medium">+ Add MFB</button>
        </div>
      </div>

      {msg && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">{msg}</div>}

      {/* Add/Edit Form */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-[#294c79] mb-4">{editing ? 'Edit MFB' : 'Add Microfinance Bank'}</h2>
            {[['Bank Name *','name','text'],['Contact Person','contactPerson','text'],['Phone','phone','tel'],['Email','email','email'],['Address','address','text'],['Partnership Type','partnershipType','text'],['Credit Limit (NGN)','creditLimit','number'],['Interest Rate (%)','interestRate','number']].map(([label, key, type]) => (
              <div key={key} className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                <input type={type} value={form[key]} onChange={e => setForm(f => ({...f, [key]: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#294c79]" />
              </div>
            ))}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} rows={3}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#294c79]" />
            </div>
            {msg && <p className="text-red-600 text-sm mb-3">{msg}</p>}
            <div className="flex gap-3">
              <button onClick={() => setShowForm(false)} className="flex-1 px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="flex-1 px-4 py-2 bg-[#294c79] text-white rounded-xl text-sm font-medium hover:bg-[#1f3a5c] disabled:opacity-50">{saving ? 'Saving' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* MFB List */}
        <div className="lg:col-span-1 space-y-3">
          {mfbs.length === 0 ? (
            <div className="bg-white rounded-2xl p-10 text-center shadow-sm">
              <div className="text-5xl mb-3"></div>
              <p className="text-gray-500 font-semibold">No MFBs yet</p>
              <p className="text-sm text-gray-400 mt-1">Add microfinance bank partners here</p>
            </div>
          ) : mfbs.map(m => (
            <div key={m.id} onClick={() => handleSelectMFB(m)}
              className={`bg-white rounded-2xl p-4 shadow-sm cursor-pointer border-2 transition hover:shadow-md ${selectedMFB?.id === m.id ? 'border-[#294c79]' : 'border-transparent'}`}>
              <div className="flex justify-between items-start gap-2">
                <div>
                  <div className="font-semibold text-[#294c79]">{m.name}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{m.contact_person || m.phone || ''}</div>
                </div>
                <div className="flex items-center gap-1">
                  <span className={`text-xs px-2 py-1 rounded-full font-semibold ${m.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{m.status}</span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleDeleteMFB(m); }}
                    className="px-2 py-1 rounded-lg bg-red-50 text-red-600 text-xs font-semibold hover:bg-red-100"
                  >
                    Delete
                  </button>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div className="bg-blue-50 rounded-lg p-2"><div className="text-blue-500">Disbursed</div><div className="font-bold text-blue-700">{fmtN(m.total_disbursed)}</div></div>
                <div className="bg-green-50 rounded-lg p-2"><div className="text-green-600">Repaid</div><div className="font-bold text-green-700">{fmtN(m.total_repaid)}</div></div>
              </div>
              {m.credit_limit && <div className="mt-2 text-xs text-gray-400">Credit limit: <span className="font-semibold text-gray-600">{fmtN(m.credit_limit)}</span>{m.interest_rate ? `  ${m.interest_rate}% interest` : ''}</div>}
            </div>
          ))}
        </div>

        {/* MFB Detail */}
        <div className="lg:col-span-2">
          {!selectedMFB ? (
            <div className="bg-white rounded-2xl p-10 text-center shadow-sm">
              <div className="text-4xl mb-3"></div>
              <p className="text-gray-400">Select a microfinance bank to view disbursements</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="bg-[#294c79] p-5 text-white flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-bold">{selectedMFB.name}</h2>
                  <p className="text-sm opacity-80 mt-0.5">{selectedMFB.partnership_type || 'Partner MFB'}{selectedMFB.interest_rate ? `  ${selectedMFB.interest_rate}% interest` : ''}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => openEdit(selectedMFB)} className="px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white rounded-lg text-xs font-medium"> Edit</button>
                  <button onClick={() => handleDeleteMFB(selectedMFB)} className="px-3 py-1.5 bg-red-500/90 hover:bg-red-600 text-white rounded-lg text-xs font-medium">Delete</button>
                  <button onClick={() => { setShowDisbForm(true); setMsg(''); }} className="px-3 py-1.5 bg-[#a5c250] hover:bg-[#8aaa30] text-white rounded-lg text-xs font-medium">+ Record Disbursement</button>
                </div>
              </div>

              {/* Summary bar */}
              <div className="grid grid-cols-3 divide-x divide-gray-100 border-b border-gray-100 text-sm">
                <div className="p-4 text-center"><div className="text-xs text-gray-400 mb-1">Active Disbursements</div><div className="font-bold text-[#294c79] text-lg">{selectedMFB.active_disbursements || 0}</div></div>
                <div className="p-4 text-center"><div className="text-xs text-gray-400 mb-1">Total Disbursed</div><div className="font-bold text-blue-700 text-lg">{fmtN(selectedMFB.total_disbursed)}</div></div>
                <div className="p-4 text-center"><div className="text-xs text-gray-400 mb-1">Total Repaid</div><div className="font-bold text-green-700 text-lg">{fmtN(selectedMFB.total_repaid)}</div></div>
              </div>

              {/* Record disbursement form */}
              {showDisbForm && (
                <div className="p-5 border-b border-gray-100 bg-blue-50">
                  <h3 className="font-semibold text-[#294c79] mb-3">Record Disbursement</h3>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    {[['Amount (NGN) *','amount','number'],['Description','description','text'],['Notes','notes','text']].map(([label, key, type]) => (
                      <div key={key}>
                        <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
                        <input type={type} value={disbForm[key]} onChange={e => setDisbForm(f => ({...f, [key]: e.target.value}))}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#294c79]" />
                      </div>
                    ))}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                      <select value={disbForm.status} onChange={e => setDisbForm(f => ({...f, status: e.target.value}))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#294c79]">
                        <option value="pending">Pending</option>
                        <option value="disbursed">Disbursed</option>
                      </select>
                    </div>
                  </div>
                  {msg && <p className="text-red-600 text-xs mb-2">{msg}</p>}
                  <div className="flex gap-2">
                    <button onClick={() => setShowDisbForm(false)} className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
                    <button onClick={handleAddDisb} disabled={saving} className="px-4 py-2 bg-[#294c79] text-white rounded-lg text-sm font-medium disabled:opacity-50">{saving ? 'Saving' : 'Record'}</button>
                  </div>
                </div>
              )}

              {/* Disbursements list */}
              <div className="p-5">
                <h3 className="font-semibold text-[#294c79] mb-3">Disbursements & Repayments</h3>
                {mfbDisbursements.length === 0 ? (
                  <p className="text-gray-400 text-sm text-center py-8">No disbursements recorded yet</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-max w-full text-sm">
                      <thead><tr className="bg-gray-50 border-b border-gray-100">
                        {['Date','Description','Amount','Repayment','Tenant','Status','Actions'].map(h => (
                          <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-600">{h}</th>
                        ))}
                      </tr></thead>
                      <tbody>
                        {mfbDisbursements.map(d => (
                          <tr key={d.id} className="border-b border-gray-50 hover:bg-gray-50">
                            <td className="px-3 py-2 text-gray-500 text-xs whitespace-nowrap">{new Date(d.created_at).toLocaleDateString('en-NG')}</td>
                            <td className="px-3 py-2 text-gray-700 max-w-[160px] truncate">{d.description || ''}</td>
                            <td className="px-3 py-2 font-semibold text-blue-700 whitespace-nowrap">{fmtN(d.amount)}</td>
                            <td className="px-3 py-2 font-semibold text-green-700 whitespace-nowrap">{d.repayment_amount ? fmtN(d.repayment_amount) : ''}</td>
                            <td className="px-3 py-2 text-gray-600 text-xs">{d.tenant_name || ''}</td>
                            <td className="px-3 py-2"><span className={`px-2 py-1 rounded-full text-xs font-semibold ${disbBadge(d.status)}`}>{d.status}</span></td>
                            <td className="px-3 py-2">
                              <div className="flex gap-1 flex-wrap">
                                {d.status === 'pending' && <button onClick={() => updateDisbursement(d.id, 'disbursed')} className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700">Disburse</button>}
                                {d.status === 'disbursed' && (
                                  <button onClick={() => {
                                    const amt = window.prompt('Enter repayment amount (NGN):', d.amount);
                                    if (amt) updateDisbursement(d.id, 'repaid', parseFloat(amt));
                                  }} className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700">Mark Repaid</button>
                                )}
                                {d.status === 'disbursed' && <button onClick={() => updateDisbursement(d.id, 'defaulted')} className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs hover:bg-red-200">Default</button>}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

//  PaymentsByRental  grouped payments tab 
//  TenantPdfViewer  shown inside admin docs modal 
function TenantPdfViewer({ tenantId, API_URL, headers }) {
  const [pdfs, setPdfs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!tenantId) return;
    setLoading(true);
    axios.get(`${API_URL}/api/admin/users/${tenantId}/pdfs`, { headers })
      .then(res => setPdfs(res.data.pdfs || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [tenantId]);

  if (loading) return <div className="border-t border-gray-100 pt-5 text-sm text-gray-400">Loading tenant PDFs</div>;
  if (pdfs.length === 0) return (
    <div className="border-t border-gray-100 pt-5">
      <p className="text-xs font-semibold text-[#294c79] uppercase tracking-wide mb-2"> Tenant-Uploaded PDFs</p>
      <p className="text-sm text-gray-400 italic">No PDFs uploaded by this tenant yet.</p>
    </div>
  );

  return (
    <div className="border-t border-gray-100 pt-5">
      <p className="text-xs font-semibold text-[#294c79] uppercase tracking-wide mb-3"> Tenant-Uploaded PDFs ({pdfs.length})</p>
      <div className="space-y-2">
        {pdfs.map(log => {
          const urlMatch = log.event_detail ? log.event_detail.match(/(?:https?:\/\/|gdrive:\/\/)\S+/) : null;
          const pdfUrl = urlMatch ? urlMatch[0] : null;
          const title = log.event_title ? log.event_title.replace('PDF Uploaded: ', '') : 'Document';
          return (
            <div key={log.id} className="flex items-center justify-between gap-3 bg-gray-50 rounded-xl px-4 py-3">
              <div className="min-w-0">
                <div className="font-medium text-sm text-[#294c79] truncate"> {title}</div>
                <div className="text-xs text-gray-400">{log.created_at ? new Date(log.created_at).toLocaleString('en-NG', { year: 'numeric', month: 'short', day: 'numeric' }) : ''}</div>
              </div>
              {pdfUrl ? (
                <button
                  onClick={() => openDoc(pdfUrl)}
                  className="px-3 py-1.5 bg-[#294c79] text-white rounded-lg text-xs font-semibold hover:bg-[#1f3a5c] whitespace-nowrap flex-shrink-0"
                >
                   View
                </button>
              ) : (
                <span className="text-xs text-gray-400 italic">URL unavailable</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PaymentsByRental({ API_URL, headers, isSuperAdmin, setSensitiveConfirm }) {
  const [groups, setGroups] = React.useState([]);
  const [allPayments, setAllPayments] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [expandedRental, setExpandedRental] = React.useState(null);
  const [expandedTenantPayment, setExpandedTenantPayment] = React.useState(null);
  const [search, setSearch] = React.useState('');
  const [preRentalGroups, setPreRentalGroups] = React.useState([]);

  const load = React.useCallback(() => {
    // Always read token fresh from localStorage rather than relying on stale closure
    const token = localStorage.getItem('adminAuthToken');
    if (!token) { setError('Not authenticated  please log in again.'); setLoading(false); return; }
    setLoading(true);
    setError('');
    Promise.all([
      axios.get(`${API_URL}/api/admin/payments/by-rental`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API_URL}/api/admin/payments?limit=500`, { headers: { Authorization: `Bearer ${token}` } }),
    ])
      .then(([groupRes, allRes]) => {
        setGroups(groupRes.data.groups || []);
        setPreRentalGroups(groupRes.data.preRentalGroups || []);
        setAllPayments(allRes.data.payments || []);
      })
      .catch(err => {
        const detail = err.response?.data?.detail || err.response?.data?.error || err.message || 'Unknown error';
        setError(`Could not load payments: ${detail}`);
      })
      .finally(() => setLoading(false));
  }, [API_URL]);

  React.useEffect(() => { load(); }, [load]);

  const retryDirectDebit = async (payment) => {
    if (!window.confirm(`Retry direct debit for ${money(payment.amount)}?`)) return;
    try {
      const token = localStorage.getItem('adminAuthToken');
      const res = await axios.post(
        `${API_URL}/api/admin/direct-debit/payments/${payment.id}/retry`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(`Retry result: ${res.data?.result?.status || 'submitted'}`);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Direct debit retry failed.');
    }
  };

  const filtered = groups.filter(g =>
    !search || g.tenant_name?.toLowerCase().includes(search.toLowerCase()) ||
    g.house_title?.toLowerCase().includes(search.toLowerCase()) ||
    g.tenant_email?.toLowerCase().includes(search.toLowerCase())
  );
  const filteredPayments = allPayments.filter(p =>
    !search ||
    p.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    p.house_title?.toLowerCase().includes(search.toLowerCase()) ||
    p.description?.toLowerCase().includes(search.toLowerCase()) ||
    p.receipt_number?.toLowerCase().includes(search.toLowerCase())
  );
  const tenantPaymentGroups = React.useMemo(() => {
    const map = new Map();
    filteredPayments.forEach(payment => {
      const key = payment.user_id || payment.email || payment.full_name || `payment-${payment.id}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          tenantName: payment.full_name || 'Unknown tenant',
          tenantEmail: payment.email || '',
          payments: [],
          totalPaid: 0,
          totalPending: 0,
          totalFailed: 0,
          totalAmount: 0,
        });
      }
      const group = map.get(key);
      group.payments.push(payment);
      group.totalAmount += Number(payment.amount || 0);
      if (payment.status === 'completed') group.totalPaid += Number(payment.amount || 0);
      if (payment.status === 'pending') group.totalPending += Number(payment.amount || 0);
      if (payment.status === 'failed') group.totalFailed += Number(payment.amount || 0);
    });
    return Array.from(map.values()).sort((a, b) => a.tenantName.localeCompare(b.tenantName));
  }, [filteredPayments]);

  const money = (value) => `NGN ${Number(value || 0).toLocaleString()}`;
  const statusClass = (status) => ({
    completed: 'bg-green-100 text-green-700',
    pending: 'bg-amber-100 text-amber-700',
    failed: 'bg-red-100 text-red-700',
    cancelled: 'bg-gray-100 text-gray-600',
  }[status] || 'bg-gray-100 text-gray-600');
  const statusLabel = (status) => status === 'completed' ? 'Paid' : String(status || 'pending').replace(/_/g, ' ');

  const openReceipt = (paymentId) => {
    const token = localStorage.getItem('adminAuthToken');
    if (!token) return;
    window.open(`${API_URL}/api/admin/payments/${paymentId}/receipt?token=${encodeURIComponent(token)}`, '_blank', 'noopener,noreferrer');
  };

  const cancelPayment = async (payment) => {
    const reason = window.prompt('Reason for cancellation. This will be shown to the tenant:');
    if (reason === null) return;
    try {
      await axios.put(`${API_URL}/api/admin/payments/${payment.id}/cancel`, { reason: reason || undefined }, { headers });
      await load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to cancel payment.');
    }
  };

  const deletePayment = (payment) => {
    setSensitiveConfirm({
      title: 'Delete payment record',
      message: `Permanently remove this ${payment.status} payment of ${money(payment.amount)}? Only cancelled or failed payments can be deleted.`,
      confirmButtonLabel: 'Delete Payment',
      onConfirm: async (sensitiveActionToken) => {
        await axios.delete(`${API_URL}/api/admin/payments/${payment.id}`, {
          headers: { ...headers, 'X-Sensitive-Token': sensitiveActionToken },
        });
        setSensitiveConfirm(null);
        await load();
      },
    });
  };

  const downloadHistory = (rentalId, tenantName) => {
    const token = localStorage.getItem('adminAuthToken');
    if (!token) return;
    const url = `${API_URL}/api/admin/rentals/${rentalId}/payment-history?token=${encodeURIComponent(token)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-spin h-10 w-10 border-4 border-[#294c79] border-t-transparent rounded-full" /></div>;
  if (error) return (
    <div className="bg-red-50 border border-red-200 p-8 rounded-2xl text-center">
      <p className="text-red-600 font-medium mb-4">{error}</p>
      <button onClick={load} className="px-6 py-2.5 bg-[#294c79] text-white rounded-xl font-medium text-sm hover:bg-[#1f3a5c]"> Retry</button>
    </div>
  );

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
        <h1 className="text-2xl sm:text-4xl font-bold text-[#294c79]">Tenant Payments</h1>
        <div className="flex gap-3 items-center">
          <input
            type="text"
            placeholder="Search tenant or property"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="px-4 py-2 rounded-xl border border-[#d9e5ef] text-sm focus:outline-none focus:border-[#294c79] w-52"
          />
          <span className="text-sm text-gray-500">{filtered.length} active lease{filtered.length !== 1 ? 's' : ''}</span>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-[#e8f2d1] overflow-hidden mb-8">
        <div className="px-5 py-4 border-b border-[#eef3e5] flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-[#294c79]">Tenant Payment Records</h2>
            <p className="text-xs text-gray-500">Click a tenant to view their due, paid, failed, or cancelled payment records.</p>
          </div>
          <button onClick={load} className="px-4 py-2 rounded-xl border border-[#294c79] text-[#294c79] text-xs font-semibold hover:bg-[#f3f7fb]">Refresh</button>
        </div>
        {tenantPaymentGroups.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No tenant payment records found.</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {tenantPaymentGroups.map(group => {
              const open = expandedTenantPayment === group.key;
              return (
                <div key={group.key}>
                  <button
                    onClick={() => setExpandedTenantPayment(open ? null : group.key)}
                    className="w-full px-5 py-4 text-left hover:bg-[#f8fbf1] transition flex flex-wrap items-center gap-4"
                  >
                    <div className="w-10 h-10 rounded-full bg-[#294c79] text-white flex items-center justify-center font-bold flex-shrink-0">
                      {group.tenantName?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div className="flex-1 min-w-[180px]">
                      <p className="font-bold text-[#294c79]">{group.tenantName}</p>
                      <p className="text-xs text-gray-500">{group.tenantEmail || `${group.payments.length} payment record${group.payments.length !== 1 ? 's' : ''}`}</p>
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-right text-xs sm:text-sm">
                      <div><p className="text-gray-400">Paid</p><p className="font-bold text-green-700">{money(group.totalPaid)}</p></div>
                      <div><p className="text-gray-400">Pending</p><p className="font-bold text-amber-700">{money(group.totalPending)}</p></div>
                      <div><p className="text-gray-400">Failed</p><p className="font-bold text-red-700">{money(group.totalFailed)}</p></div>
                    </div>
                    <span className="text-xs font-semibold text-[#294c79]">{open ? 'Hide' : 'View'}</span>
                  </button>
                  {open && (
                    <div className="overflow-x-auto border-t border-[#eef3e5]" data-table-scroll>
                      <table className="min-w-max w-full text-sm">
                        <thead className="bg-[#f7f9fc]">
                          <tr>
                            {['Property', 'Type', 'Description', 'Amount', 'Status', 'Receipt', 'Created', 'Paid', 'Actions'].map(h => (
                              <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-[#294c79] uppercase tracking-wide whitespace-nowrap">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {group.payments.map(payment => (
                            <tr key={payment.id} className="border-t border-gray-100 hover:bg-gray-50">
                              <td className="px-4 py-3 text-gray-600 max-w-xs truncate">{payment.house_title || '-'}</td>
                              <td className="px-4 py-3 capitalize whitespace-nowrap">{String(payment.category || payment.type || '-').replace(/_/g, ' ')}</td>
                              <td className="px-4 py-3 text-gray-600 max-w-sm truncate">{payment.description || '-'}</td>
                              <td className="px-4 py-3 font-bold text-[#294c79] whitespace-nowrap">{money(payment.amount)}</td>
                              <td className="px-4 py-3">
                                <span className={`px-2 py-1 rounded-full text-xs font-semibold capitalize ${statusClass(payment.status)}`}>{statusLabel(payment.status)}</span>
                              </td>
                              <td className="px-4 py-3 font-mono text-xs text-gray-500 whitespace-nowrap">{payment.receipt_number || '-'}</td>
                              <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{payment.created_at ? new Date(payment.created_at).toLocaleDateString('en-NG') : '-'}</td>
                              <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{payment.paid_at ? new Date(payment.paid_at).toLocaleDateString('en-NG') : '-'}</td>
                              <td className="px-4 py-3">
                                <div className="flex flex-wrap gap-2">
                                  {payment.status === 'completed' && (
                                    <button onClick={() => openReceipt(payment.id)} className="px-3 py-1.5 bg-[#e8f2d1] text-[#294c79] rounded-lg text-xs font-semibold hover:bg-[#d4e9a8] whitespace-nowrap">Receipt</button>
                                  )}
                                  {payment.status !== 'completed' && payment.status !== 'cancelled' && (
                                    <button onClick={() => cancelPayment(payment)} className="px-3 py-1.5 bg-amber-100 text-amber-700 rounded-lg text-xs font-semibold hover:bg-amber-200 whitespace-nowrap">Cancel</button>
                                  )}
                                  {['cancelled', 'failed'].includes(payment.status) && (
                                    <button onClick={() => deletePayment(payment)} className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-xs font-semibold hover:bg-red-200 whitespace-nowrap">Delete</button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/*  Pre-rental Applications (verification_fee / upfront pending)  */}
      {preRentalGroups.filter(g =>
        !search ||
        g.tenant_name?.toLowerCase().includes(search.toLowerCase()) ||
        g.house_title?.toLowerCase().includes(search.toLowerCase())
      ).length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-[#294c79] mb-3"> Pre-Lease Payments (Verification & Upfront)</h2>
          <div className="space-y-3">
            {preRentalGroups.filter(g =>
              !search ||
              g.tenant_name?.toLowerCase().includes(search.toLowerCase()) ||
              g.house_title?.toLowerCase().includes(search.toLowerCase())
            ).map(g => (
              <div key={g.application_id} className="bg-white rounded-2xl border border-amber-200 overflow-hidden">
                <div className="px-6 py-4 flex flex-wrap items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center text-white font-bold flex-shrink-0">
                    {g.tenant_name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-[#294c79]">{g.tenant_name}</p>
                    <p className="text-sm text-gray-500">{g.house_title}  Stage: <span className="font-semibold capitalize">{(g.workflow_stage || '').replace(/_/g,' ')}</span></p>
                  </div>
                  <div className="flex gap-4 text-right">
                    <div><p className="text-xs text-gray-400">Paid</p><p className="font-bold text-green-600">{g.total_paid.toLocaleString()}</p></div>
                    <div><p className="text-xs text-gray-400">Pending</p><p className="font-bold text-amber-600">{g.total_pending.toLocaleString()}</p></div>
                  </div>
                </div>
                {g.payments.length > 0 && (
                  <div className="border-t border-amber-100 overflow-x-auto">
                    <table className="min-w-max w-full text-sm">
                      <thead className="bg-amber-50">
                        <tr>{['Type', 'Description', 'Amount', 'Receipt No.', 'Status', 'Date'].map(h => (
                          <th key={h} className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">{h}</th>
                        ))}</tr>
                      </thead>
                      <tbody>
                        {g.payments.map(p => (
                          <tr key={p.id} className="border-t border-amber-50 hover:bg-amber-50/40">
                            <td className="px-4 py-2">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${p.category === 'verification_fee' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                                {p.category === 'verification_fee' ? 'Verif. Fee' : '30% Upfront'}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-gray-700 max-w-xs truncate">{p.description || p.type}</td>
                            <td className="px-4 py-2 font-bold text-[#294c79] whitespace-nowrap">{Number(p.amount || 0).toLocaleString()}</td>
                            <td className="px-4 py-2 font-mono text-xs text-gray-400">{p.receipt_number || ''}</td>
                            <td className="px-4 py-2">
                              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${p.status === 'completed' ? 'bg-green-100 text-green-700' : p.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                                {p.status === 'completed' ? ' Paid' : p.status}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-xs text-gray-400 whitespace-nowrap">
                              {p.paid_at ? new Date(p.paid_at).toLocaleDateString('en-NG', {day:'numeric',month:'short',year:'numeric'}) : p.created_at ? new Date(p.created_at).toLocaleDateString('en-NG') : ''}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/*  Active Leases  */}
      {filtered.length === 0 ? (
        <div className="bg-white p-12 rounded-2xl shadow-sm text-center">
          <p className="text-5xl mb-4"></p>
          <p className="text-xl text-gray-500">{search ? 'No results found' : 'No active leases yet'}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map(group => {
            const isExpanded = expandedRental === group.rental_id;
            const paidCount = group.payments.filter(p => p.status === 'completed').length;
            const failedCount = group.payments.filter(p => p.status === 'failed').length;
            const pctPaid = group.total_instalments > 0 ? Math.round((group.instalments_paid / group.total_instalments) * 100) : 0;

            return (
              <div key={group.rental_id} className="bg-white rounded-2xl shadow-sm border border-[#e8f2d1] overflow-hidden">
                {/*  Tenant row (click to expand)  */}
                <button
                  onClick={() => setExpandedRental(isExpanded ? null : group.rental_id)}
                  className="w-full text-left px-6 py-5 hover:bg-[#fafff5] transition flex flex-wrap items-center gap-4"
                >
                  {/* Avatar */}
                  <div className="w-11 h-11 rounded-full bg-[#294c79] flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                    {group.tenant_name?.[0]?.toUpperCase() || '?'}
                  </div>

                  {/* Name + property */}
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-[#294c79] text-base truncate">{group.tenant_name}</p>
                    <p className="text-sm text-gray-500 truncate">{group.house_title}  {group.house_location}</p>
                    <p className="text-xs text-gray-400">{group.tenant_email}  {group.tenant_phone}</p>
                  </div>

                  {/* Progress bar */}
                  <div className="w-40 hidden sm:block">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>{group.instalments_paid}/{group.total_instalments} paid</span>
                      <span>{pctPaid}%</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div className="h-2 rounded-full bg-[#a5c250] transition-all" style={{ width: `${pctPaid}%` }} />
                    </div>
                  </div>

                  {/* Totals */}
                  <div className="flex gap-4 text-right flex-shrink-0">
                    <div>
                      <p className="text-xs text-gray-400">Collected</p>
                      <p className="font-bold text-[#294c79]">{group.total_paid.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Outstanding</p>
                      <p className="font-bold text-amber-600">{Number(group.remaining_balance || 0).toLocaleString()}</p>
                    </div>
                  </div>

                  {/* Badges */}
                  <div className="flex gap-2 flex-shrink-0">
                    {failedCount > 0 && <span className="px-2 py-1 rounded-lg bg-red-100 text-red-700 text-xs font-semibold">{failedCount} failed</span>}
                    <span className="px-2 py-1 rounded-lg bg-[#e8f2d1] text-[#294c79] text-xs font-semibold">{paidCount} receipts</span>
                    <span className="text-gray-400 text-xl">{isExpanded ? '' : ''}</span>
                  </div>
                </button>

                {/*  Expanded payment table  */}
                {isExpanded && (
                  <div className="border-t border-[#e8f2d1]">
                    {/* Download button */}
                    <div className="px-6 py-3 bg-[#f8fbf1] flex flex-wrap gap-3 items-center justify-between">
                      <p className="text-sm text-gray-600">
                        Lease: <strong>{group.lease_start_date ? new Date(group.lease_start_date).toLocaleDateString('en-NG') : ''}</strong>  <strong>{group.lease_end_date ? new Date(group.lease_end_date).toLocaleDateString('en-NG') : ''}</strong>
                        {'  '}Direct Debit: <span className="font-semibold text-[#294c79]">{(group.direct_debit_status || 'pending').replace(/_/g,' ').toUpperCase()}</span>
                      </p>
                      <button
                        onClick={() => downloadHistory(group.rental_id, group.tenant_name)}
                        className="px-5 py-2 bg-[#294c79] text-white rounded-xl text-xs font-semibold hover:bg-[#1f3a5c] transition"
                      >
                         Download Payment History
                      </button>
                    </div>

                    {group.payments.length === 0 ? (
                      <p className="px-6 py-6 text-gray-400 text-sm">No payment records yet for this lease.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-max w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              {['#', 'Date', 'Description', 'Amount', 'Receipt No.', 'Status', 'Method', ''].map(h => (
                                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {group.payments.map((p, i) => (
                              <tr key={p.id} className="border-t border-gray-100 hover:bg-gray-50">
                                <td className="px-4 py-3 text-gray-400 text-xs">{p.instalment_number || (i + 1)}</td>
                                <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">
                                  {p.paid_at
                                    ? new Date(p.paid_at).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' })
                                    : p.due_date
                                    ? `Due: ${new Date(p.due_date).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' })}`
                                    : ''}
                                </td>
                                <td className="px-4 py-3 text-gray-700 max-w-xs">
                                  <div className="truncate">{p.description || p.type}</div>
                                  {p.last_direct_debit_failure_reason && (
                                    <div className="mt-1 text-xs text-red-600">
                                      Reason: {p.last_direct_debit_failure_reason}
                                    </div>
                                  )}
                                  {p.next_retry_at && p.status === 'failed' && (
                                    <div className="mt-0.5 text-xs text-amber-600">
                                      Next auto retry: {new Date(p.next_retry_at).toLocaleString('en-NG', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                    </div>
                                  )}
                                </td>
                                <td className="px-4 py-3 font-bold text-[#294c79] whitespace-nowrap">{Number(p.amount || 0).toLocaleString()}</td>
                                <td className="px-4 py-3 font-mono text-xs text-gray-400 whitespace-nowrap">{p.receipt_number || ''}</td>
                                <td className="px-4 py-3">
                                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                    p.status === 'completed' ? 'bg-green-100 text-green-700' :
                                    p.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                                    p.status === 'failed' ? 'bg-red-100 text-red-700' :
                                    'bg-gray-100 text-gray-600'
                                  }`}>
                                    {p.status === 'completed' ? ' Paid' : p.status}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">
                                  {p.paystack_authorization_code || p.direct_debit_attempts ? 'Direct Debit' : p.paystack_reference ? 'Paystack' : ''}
                                </td>
                                <td className="px-4 py-3">
                                  {p.status === 'completed' && (
                                    <button
                                      onClick={() => {
                                        const token = localStorage.getItem('adminAuthToken');
                                        if (!token) return;
                                        window.open(`${API_URL}/api/admin/payments/${p.id}/receipt?token=${encodeURIComponent(token)}`, '_blank', 'noopener,noreferrer');
                                      }}
                                      className="px-3 py-1.5 bg-[#e8f2d1] text-[#294c79] rounded-lg text-xs font-semibold hover:bg-[#d4e9a8] transition whitespace-nowrap"
                                    >
                                      Receipt
                                    </button>
                                  )}
                                  {['pending', 'failed'].includes(p.status) && p.category === 'instalment' && (
                                    <button
                                      onClick={() => retryDirectDebit(p)}
                                      className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs font-semibold hover:bg-red-700 transition whitespace-nowrap"
                                    >
                                      Retry Debit
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-[#f8fbf1]">
                            <tr>
                              <td colSpan={3} className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Totals</td>
                              <td className="px-4 py-3 font-bold text-[#294c79]">
                                {group.payments.filter(p => p.status === 'completed').reduce((s, p) => s + Number(p.amount || 0), 0).toLocaleString()} collected
                              </td>
                              <td colSpan={3} className="px-4 py-3 text-xs text-gray-400">
                                {Number(group.remaining_balance || 0).toLocaleString()} remaining
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ContactReplyForm({ itemId, headers, API_URL, onReplied }) {
  const [open, setOpen] = React.useState(false);
  const [reply, setReply] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const handleReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await fetch(`${API_URL}/api/admin/contact-messages/${itemId}/reply`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ replyMessage: reply }),
      });
      setReply(''); setOpen(false); onReplied();
    } catch { alert('Failed to send reply'); }
    finally { setSending(false); }
  };
  if (!open) return (
    <button onClick={() => setOpen(true)} className="mt-3 px-4 py-2 bg-[#294c79] text-white rounded-xl text-xs font-semibold hover:bg-[#1f3a5c] transition">
       Reply to Sender
    </button>
  );
  return (
    <div className="mt-3 space-y-2">
      <textarea value={reply} onChange={e => setReply(e.target.value)} rows={3} placeholder="Type your reply" className="w-full px-4 py-3 border-2 border-[#afc09e] rounded-xl text-sm focus:outline-none focus:border-[#a5c250] resize-none" />
      <div className="flex gap-2">
        <button onClick={handleReply} disabled={sending || !reply.trim()} className="px-5 py-2.5 bg-[#294c79] text-white rounded-xl text-xs font-semibold hover:bg-[#1f3a5c] disabled:opacity-50">
          {sending ? 'Sending' : ' Send Reply'}
        </button>
        <button onClick={() => { setOpen(false); setReply(''); }} className="px-4 py-2.5 border border-gray-300 rounded-xl text-xs text-gray-600 hover:bg-gray-50">Cancel</button>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState('overview');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const token = localStorage.getItem('adminAuthToken');
  const headers = { Authorization: `Bearer ${token}` };

  //  Intercept 401 responses  token expired mid-session 
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      res => res,
      err => {
        if (err.response?.status === 401 && token) {
          localStorage.removeItem('adminAuthToken');
                    navigate('/admin/login');
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [token, navigate]);

  useEffect(() => {
    const selectors = '.overflow-x-auto, [data-table-scroll]';
    const tableScrollers = Array.from(document.querySelectorAll(selectors)).filter(el => el.querySelector('table'));
    tableScrollers.forEach(el => {
      el.classList.add('admin-table-scroll');
      if (!el.getAttribute('tabindex')) el.setAttribute('tabindex', '0');
      if (!el.getAttribute('aria-label')) el.setAttribute('aria-label', 'Scrollable table');
    });

    const handleWheel = (event) => {
      const scroller = event.target.closest(selectors);
      if (!scroller || !scroller.querySelector('table')) return;
      if (scroller.scrollWidth <= scroller.clientWidth) return;

      const horizontalIntent = Math.abs(event.deltaX) > Math.abs(event.deltaY);
      const delta = horizontalIntent ? event.deltaX : event.deltaY;
      if (!delta) return;

      const atStart = scroller.scrollLeft <= 0;
      const atEnd = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 1;
      if ((delta < 0 && atStart) || (delta > 0 && atEnd)) return;

      scroller.scrollLeft += delta;
      event.preventDefault();
    };

    document.addEventListener('wheel', handleWheel, { passive: false });
    return () => document.removeEventListener('wheel', handleWheel);
  }, [activeTab]);

  const [currentUser, setCurrentUser] = useState(null);
  const isSuperAdmin = currentUser?.role === 'super_admin';

  const [stats, setStats] = useState({ users: 0, houses: 0, applications: 0, revenue: 0, waitlist: 0, openTickets: 0 });
  const [systemStatus, setSystemStatus] = useState({});
  const [systemStatusLoading, setSystemStatusLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [houses, setHouses] = useState([]);
  const [applications, setApplications] = useState([]);
  const [payments, setPayments] = useState([]);
  // FIX #4: Admin-side rentals state
  const [rentals, setRentals] = useState([]);
  const [rentalStatusFilter, setRentalStatusFilter] = useState('active');
  const [rentalTenantFilter, setRentalTenantFilter] = useState('');
  const [waitlist, setWaitlist] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [sentNotifications, setSentNotifications] = useState([]);
  const [contactMessages, setContactMessages] = useState([]);
  const [adminLogs, setAdminLogs] = useState([]);
  const [adminLogsLoading, setAdminLogsLoading] = useState(false);
  // Landlords
  const [landlords, setLandlords] = useState([]);
  const [selectedLandlord, setSelectedLandlord] = useState(null);
  const [landlordPayments, setLandlordPayments] = useState([]);
  const [showLandlordForm, setShowLandlordForm] = useState(false);
  const [editLandlord, setEditLandlord] = useState(null);
  // Microfinance Banks
  const [mfbs, setMFBs] = useState([]);
  const [selectedMFB, setSelectedMFB] = useState(null);
  const [mfbDisbursements, setMFBDisbursements] = useState([]);
  const [showMFBForm, setShowMFBForm] = useState(false);
  const [editMFB, setEditMFB] = useState(null);
  const [contactStatusFilter, setContactStatusFilter] = useState('all');
  const [notifSubTab, setNotifSubTab] = useState('received');
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketReplies, setTicketReplies] = useState([]);
  const [replyMessage, setReplyMessage] = useState('');
  const [ticketDetailLoading, setTicketDetailLoading] = useState(false);
  const [sendingReply, setSendingReply] = useState(false);
  const [notifFilter, setNotifFilter] = useState('all');
  const [ticketFilter, setTicketFilter] = useState('open');
  const [tenantSubmissions, setTenantSubmissions] = useState([]);
  const [submissionsFilter, setSubmissionsFilter] = useState('all');

  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistError, setWaitlistError] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [markingRead, setMarkingRead] = useState(false);
  const [deletingNotifIds, setDeletingNotifIds] = useState({});
  const [deletingContactIds, setDeletingContactIds] = useState({});

  //  HOUSE ORDERING STATE 
  const [houseOrderMode, setHouseOrderMode] = useState(false); // toggle drag-to-reorder view
  const [orderedHouses, setOrderedHouses] = useState([]);
  const [savingOrder, setSavingOrder] = useState(false);
  const [draggedIdx, setDraggedIdx] = useState(null);

  const [housesToAdd, setHousesToAdd] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingHouse, setEditingHouse] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', description: '', location: '', address: '', bedrooms: 1, bathrooms: 1, annual_rent: '', images: [], amenities: '', status: 'pending', display_order: 0, cover_image_index: 0 });

  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  const [showNotifModal, setShowNotifModal] = useState(false);
  const [notifForm, setNotifForm] = useState({ userId: '', type: 'general', title: '', message: '' });
  const [tenantsList, setTenantsList] = useState([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [tenantSearch, setTenantSearch] = useState('');

  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIdx, setLightboxIdx] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);
  const [sensitiveConfirm, setSensitiveConfirm] = useState(null); // FIX #5: admin password-confirm modal state

  useEffect(() => {
    const storedToken = localStorage.getItem('adminAuthToken');
    if (!storedToken) { navigate('/admin/login'); return; }

    //  Step 1: Check JWT expiry locally 
    try {
      const payload = JSON.parse(atob(storedToken.split('.')[1]));
      if (payload.exp && Date.now() / 1000 > payload.exp) {
        localStorage.removeItem('adminAuthToken');
                navigate('/admin/login');
        return;
      }
    } catch {
      localStorage.removeItem('adminAuthToken');
            navigate('/admin/login');
      return;
    }

    //  Step 2: Validate against server and fetch fresh user data 
    // Never use localStorage for user data  always fetch from API
    axios.get(`${API_URL}/api/auth/me`, {
      headers: { Authorization: `Bearer ${storedToken}` }
    }).then(res => {
      const user = res.data.user;
      if (!['admin','super_admin'].includes(user?.role)) {
        localStorage.removeItem('adminAuthToken');
        navigate('/admin/login');
        return;
      }
      setCurrentUser(user);
    }).catch((err) => {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('adminAuthToken');
        navigate('/admin/login');
      }
      // Network error  remain on page, interceptor will catch next 401
    });
  }, [navigate]);

  const hasPermission = (perm) => {
    if (!currentUser) return false;
    if (currentUser.role === 'super_admin') return true;
    const perms = currentUser.permissions || [];
    return perms.includes(perm) || perms.includes('all');
  };

  const navItems = [
    { id: 'overview', label: 'Overview', alwaysShow: true },
    { id: 'users', label: 'Users', permission: 'manage_users' },
    { id: 'houses', label: 'Houses', permission: 'manage_houses' },
    { id: 'add-house', label: 'Add House', permission: 'manage_houses' },
    { id: 'tenant-submissions', label: 'Tenant Submissions', alwaysShow: true },
    { id: 'applications', label: 'Applications', permission: 'manage_applications' },
    // FIX #4: Active leases visible to admin with the manage_rentals permission.
    { id: 'rentals', label: 'Active Leases', permission: 'manage_rentals' },
    { id: 'payments', label: 'Payments', permission: 'manage_payments' },
    { id: 'waitlist', label: 'Interest Forms', permission: 'manage_waitlist' },
    { id: 'tickets', label: 'Support Tickets', permission: 'manage_tickets' },
    { id: 'notifications', label: `Notifications${unreadCount > 0 ? ` (${unreadCount})` : ''}`, permission: 'manage_notifications' },
    { id: 'contact-messages', label: 'Contact Messages', permission: 'manage_notifications' },
    { id: 'debit-audit',    label: 'Direct Debit Log', permission: 'manage_payments' },
    { id: 'debit-requests', label: 'Account Change Requests', permission: 'manage_payments' },
    { id: 'landlords', label: 'Landlords', permission: 'manage_houses' },
    { id: 'microfinance', label: 'Microfinance Banks', permission: 'manage_payments' },
    { id: 'tenant-logs', label: 'Tenant Activity Logs', permission: 'manage_tenants' },
    { id: 'audit-logs', label: 'Security Audit Logs', superAdminOnly: true },
    { id: 'manage-admins', label: 'Manage Admins', superAdminOnly: true },
    { id: 'admin-logs', label: 'Admin Activity Log', superAdminOnly: true },
  ].filter(item => {
    if (item.alwaysShow) return true;
    if (item.superAdminOnly) return isSuperAdmin;
    if (item.permission) return hasPermission(item.permission);
    return true;
  });

  useEffect(() => {
    if (!token || !currentUser) return;
    if (activeTab === 'overview') { loadStats(); loadSystemStatus(); }
    if (activeTab === 'users') loadUsers();
    if (activeTab === 'houses') loadHouses();
    if (activeTab === 'applications') loadApplications();
    if (activeTab === 'payments') loadPayments();
    if (activeTab === 'rentals') loadRentals(); // FIX #4
    if (activeTab === 'waitlist') loadWaitlist();
    if (activeTab === 'notifications') { loadNotifications(); loadSentNotifications(); }
    if (activeTab === 'contact-messages') loadContactMessages();
    if (activeTab === 'tickets') loadTickets();
    if (activeTab === 'manage-admins') loadUsers();
    if (activeTab === 'tenant-submissions') loadTenantSubmissions();
    if (activeTab === 'admin-logs') loadAdminLogs();
    if (activeTab === 'landlords') loadLandlords();
    if (activeTab === 'microfinance') loadMFBs();
  }, [activeTab, currentUser, token]);

  useEffect(() => {
    if (activeTab === 'tickets' && currentUser) loadTickets();
  }, [ticketFilter]);

  useEffect(() => {
    if (activeTab === 'notifications' && currentUser) loadNotifications();
  }, [notifFilter]);

  // FIX #4: Re-query rentals when status/tenant filter changes.
  useEffect(() => {
    if (activeTab === 'rentals' && currentUser) loadRentals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rentalStatusFilter, rentalTenantFilter]);

  useEffect(() => {
    if (activeTab === 'contact-messages' && currentUser) loadContactMessages();
  }, [contactStatusFilter, activeTab, currentUser]);

  //  Realtime polling for notifications and tickets 
  useEffect(() => {
    if (!token || !currentUser) return;

    const pollRealtime = async () => {
      try {
        const notifUrl = notifFilter !== 'all'
          ? `${API_URL}/api/admin/notifications?direction=received&type=${notifFilter}`
          : `${API_URL}/api/admin/notifications?direction=received`;

        const notifRes = await axios.get(notifUrl, { headers });
        const allNotifications = notifRes.data.notifications || [];
        setUnreadCount(allNotifications.filter(n => !n.is_read).length);

        if (activeTab === 'notifications' || activeTab === 'overview') {
          setNotifications(allNotifications);
        }

        if (activeTab === 'notifications') {
          const sentRes = await axios.get(`${API_URL}/api/admin/notifications?direction=sent`, { headers });
          setSentNotifications(sentRes.data.notifications || []);
        }

        if (activeTab === 'tickets' || activeTab === 'overview') {
          const ticketRes = await axios.get(`${API_URL}/api/admin/tickets?status=${ticketFilter}`, { headers });
          const nextTickets = ticketRes.data.tickets || [];
          setTickets(nextTickets);

          if (selectedTicket?.id) {
            const detailRes = await axios.get(`${API_URL}/api/admin/tickets/${selectedTicket.id}`, { headers });
            setSelectedTicket(detailRes.data.ticket || null);
            setTicketReplies(detailRes.data.replies || []);
          }
        }
      } catch (err) {
        console.error('Realtime polling error:', err);
      }
    };

    pollRealtime();
    const interval = setInterval(pollRealtime, 10000);
    return () => clearInterval(interval);
  }, [token, currentUser, activeTab, notifFilter, ticketFilter, selectedTicket?.id]);

  // Sync orderedHouses when houses load
  useEffect(() => {
    const sorted = [...houses].sort((a, b) => (a.display_order  -  999) - (b.display_order  -  999));
    setOrderedHouses(sorted);
  }, [houses]);

  const fetchTenantsForNotif = async () => {
    setTenantsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/notifications/recipients`, { headers });
      setTenantsList(res.data.recipients || []);
    } catch (err) { console.error('Failed to fetch notification recipients:', err); }
    finally { setTenantsLoading(false); }
  };

  const openNotifModal = () => {
    setNotifForm({ userId: '', type: 'general', title: '', message: '' });
    setTenantSearch('');
    setShowNotifModal(true);
    fetchTenantsForNotif();
  };

  //  LOAD FUNCTIONS 
  const loadTenantSubmissions = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/uploaded-houses`, { headers });
      setTenantSubmissions(res.data.houses || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const loadStats = async () => {
    setStatsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/dashboard/stats`, { headers });
      setStats(res.data.stats || {});
    } catch (err) { console.error(err); }
    finally { setStatsLoading(false); }
  };

  const loadSystemStatus = async () => {
    setSystemStatusLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/system-status`, { headers });
      setSystemStatus(res.data.checks || {});
    } catch (err) {
      console.error(err);
      setSystemStatus({
        api: { label: 'API Server', status: 'offline', detail: err.response?.data?.error || err.message || 'Status check failed' },
      });
    } finally {
      setSystemStatusLoading(false);
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/users?limit=100`, { headers });
      setUsers(res.data.users || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const loadHouses = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/houses?limit=100&status=all`, { headers });
      try {
        const adminRes = await axios.get(`${API_URL}/api/admin/all-houses?limit=100`, { headers });
        setHouses(adminRes.data.houses || res.data.houses || []);
      } catch { setHouses(res.data.houses || []); }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const loadApplications = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/applications?limit=100`, { headers });
      setApplications(res.data.applications || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const loadPayments = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/payments?limit=100`, { headers });
      setPayments(res.data.payments || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const loadLandlords = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/landlords`, { headers });
      setLandlords(res.data.landlords || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const loadLandlordPayments = async (landlordId) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/landlords/${landlordId}/payments`, { headers });
      setLandlordPayments(res.data.payments || []);
    } catch (err) { console.error(err); }
  };

  const loadMFBs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/mfbs`, { headers });
      setMFBs(res.data.mfbs || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const loadMFBDisbursements = async (mfbId) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/mfbs/${mfbId}/disbursements`, { headers });
      setMFBDisbursements(res.data.disbursements || []);
    } catch (err) { console.error(err); }
  };

  // FIX #4: Admin rentals loader  supports filter by status and by tenant.
  const loadRentals = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (rentalStatusFilter && rentalStatusFilter !== 'all') params.set('status', rentalStatusFilter);
      if (rentalTenantFilter) params.set('userId', rentalTenantFilter);
      const qs = params.toString();
      const res = await axios.get(`${API_URL}/api/admin/rentals${qs ? `?${qs}` : ''}`, { headers });
      setRentals(res.data.rentals || []);
    } catch (err) { console.error('Load rentals error:', err); }
    finally { setLoading(false); }
  };

  const loadWaitlist = async () => {
    setWaitlistLoading(true); setWaitlistError('');
    try {
      const res = await axios.get(`${API_URL}/api/admin/waitlist`, { headers });
      setWaitlist(res.data.waitlist || []);
    } catch (err) { setWaitlistError(err.response?.data?.error || err.message || 'Failed to load waitlist'); }
    finally { setWaitlistLoading(false); }
  };

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const url = notifFilter !== 'all'
        ? `${API_URL}/api/admin/notifications?direction=received&type=${notifFilter}`
        : `${API_URL}/api/admin/notifications?direction=received`;
      const res = await axios.get(url, { headers });
      setNotifications(res.data.notifications || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const loadSentNotifications = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/notifications?direction=sent`, { headers });
      setSentNotifications(res.data.notifications || []);
    } catch (err) { console.error(err); }
  };

  const loadAdminLogs = async () => {
    setAdminLogsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/admin-logs`, { headers });
      setAdminLogs(res.data.logs || []);
    } catch (err) {
      console.error('Admin logs load error:', err);
    } finally {
      setAdminLogsLoading(false);
    }
  };

  const loadContactMessages = async () => {    setLoading(true);
    try {
      const url = contactStatusFilter !== 'all'
        ? `${API_URL}/api/admin/contact-messages?status=${contactStatusFilter}`
        : `${API_URL}/api/admin/contact-messages`;
      const res = await axios.get(url, { headers });
      setContactMessages(res.data.messages || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const markContactMessageRead = async (contactId, status = 'read') => {
    try {
      await axios.put(`${API_URL}/api/admin/contact-messages/${contactId}`, { isRead: true, status }, { headers });
      setContactMessages(prev => prev.map(item => item.id === contactId ? { ...item, is_read: true, status } : item));
    } catch (err) {
      console.error('Mark contact message error:', err);
    }
  };

  const deleteAdminNotification = async (notifId, sent = false) => {
    const target = (sent ? sentNotifications : notifications).find(n => n.id === notifId);
    setDeletingNotifIds(prev => ({ ...prev, [notifId]: true }));
    try {
      await axios.delete(`${API_URL}/api/admin/notifications/${notifId}`, { headers });
      setNotifications(prev => prev.filter(n => n.id !== notifId));
      setSentNotifications(prev => prev.filter(n => n.id !== notifId));
      if (target && !target.is_read) setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Delete notification error:', err);
      alert(err.response?.data?.error || 'Failed to delete notification.');
    } finally {
      setDeletingNotifIds(prev => ({ ...prev, [notifId]: false }));
    }
  };

  const deleteContactMessage = async (contactId) => {
    setDeletingContactIds(prev => ({ ...prev, [contactId]: true }));
    try {
      await axios.delete(`${API_URL}/api/admin/contact-messages/${contactId}`, { headers });
      setContactMessages(prev => prev.filter(item => item.id !== contactId));
    } catch (err) {
      console.error('Delete contact message error:', err);
      alert(err.response?.data?.error || 'Failed to delete contact message.');
    } finally {
      setDeletingContactIds(prev => ({ ...prev, [contactId]: false }));
    }
  };

  const markNotificationRead = async (notifId) => {
    try {
      await axios.put(`${API_URL}/api/admin/notifications/${notifId}/read`, {}, { headers });
      setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) { console.error('Mark read error:', err); }
  };

  const markAllNotificationsRead = async () => {
    setMarkingRead(true);
    try {
      await axios.put(`${API_URL}/api/admin/notifications/read-all`, {}, { headers });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) { console.error('Mark all read error:', err); }
    finally { setMarkingRead(false); }
  };

  const loadTickets = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/tickets?status=${ticketFilter}`, { headers });
      setTickets(res.data.tickets || []);
      setSelectedTicket(null);
      setTicketReplies([]);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const loadTicketDetail = async (ticketId) => {
    setTicketDetailLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/tickets/${ticketId}`, { headers });
      setSelectedTicket(res.data.ticket);
      setTicketReplies(res.data.replies || []);
    } catch (err) { alert('Failed to load ticket details. Please try again.'); }
    finally { setTicketDetailLoading(false); }
  };

  //  HOUSE ORDER HANDLERS 

  // Drag-and-drop reorder
  const handleDragStart = (idx) => setDraggedIdx(idx);
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (draggedIdx === null || draggedIdx === idx) return;
    const updated = [...orderedHouses];
    const dragged = updated.splice(draggedIdx, 1)[0];
    updated.splice(idx, 0, dragged);
    setOrderedHouses(updated);
    setDraggedIdx(idx);
  };
  const handleDragEnd = () => setDraggedIdx(null);

  // Move up/down buttons (mobile-friendly)
  const moveHouse = (idx, direction) => {
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= orderedHouses.length) return;
    const updated = [...orderedHouses];
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    setOrderedHouses(updated);
  };

  // Save order to backend
  const saveHouseOrder = async () => {
    setSavingOrder(true);
    try {
      const orderPayload = orderedHouses.map((h, idx) => ({ id: h.id, display_order: idx + 1 }));
      await axios.put(`${API_URL}/api/admin/houses/reorder`, { order: orderPayload }, { headers });
      alert(' House display order saved!');
      setHouseOrderMode(false);
      await loadHouses();
    } catch (err) {
      // Fallback: update each house individually if bulk endpoint not available
      try {
        for (let i = 0; i < orderedHouses.length; i++) {
          await axios.put(`${API_URL}/api/admin/houses/${orderedHouses[i].id}`, { display_order: i + 1 }, { headers });
        }
        alert(' House display order saved!');
        setHouseOrderMode(false);
        await loadHouses();
      } catch (err2) {
        alert(err2.response?.data?.error || 'Failed to save order');
      }
    } finally { setSavingOrder(false); }
  };

  //  OTHER HANDLERS 
  const [expandedSubId, setExpandedSubId] = React.useState(null);
  const handleUpdateSubmission = async (id, status, adminNotes) => {
    try {
      await axios.put(`${API_URL}/api/admin/uploaded-houses/${id}`, { status, adminNotes }, { headers });
      loadTenantSubmissions();
    } catch (err) { alert('Failed to update submission'); }
  };

  const handleCancelPayment = async (paymentId) => {
    const reason = window.prompt('Reason for cancellation (shown to tenant):');
    if (reason === null) return; // user pressed Cancel on prompt
    try {
      await axios.put(`${API_URL}/api/admin/payments/${paymentId}/cancel`, { reason: reason || undefined }, { headers });
      alert(' Payment cancelled and tenant notified.');
      await loadPayments();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to cancel payment.');
    }
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim() || !selectedTicket) return;
    setSendingReply(true);
    try {
      await axios.post(`${API_URL}/api/admin/tickets/${selectedTicket.id}/reply`, { message: replyMessage }, { headers });
      setReplyMessage('');
      await loadTicketDetail(selectedTicket.id);
      await loadTickets();
      setSelectedTicket(prev => prev);
    } catch (err) { alert('Failed to send reply. Please try again.'); }
    finally { setSendingReply(false); }
  };

  const handleUpdateTicketStatus = async (ticketId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/admin/tickets/${ticketId}`, { status: newStatus }, { headers });
      setSelectedTicket(prev => prev ? { ...prev, status: newStatus } : null);
      setTickets(prev => prev.map(t => t.id === ticketId ? { ...t, status: newStatus } : t));
    } catch (err) { alert('Failed to update ticket status. Please try again.'); }
  };

  const handleSendNotification = async () => {
    if (!notifForm.userId || !notifForm.title || !notifForm.message) {
      alert('Please select a tenant and fill in all fields');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/admin/notifications/send`, notifForm, { headers });
      alert(' Notification sent successfully!');
      setNotifForm({ userId: '', type: 'general', title: '', message: '' });
      setTenantSearch('');
      setShowNotifModal(false);
      await loadSentNotifications();
    } catch (err) { alert(err.response?.data?.error || 'Failed to send notification'); }
  };

  const handleAdvanceApplication = (app, nextStage, label) => {
    const checks = [app.nin_verification_status, app.bvn_verification_status, app.bank_verification_status];
    const allVerified = checks.every(c => c === 'verified');
    if (nextStage === 'verification' && !allVerified) {
      alert('You must verify the tenant\'s NIN, BVN, and bank account before moving to Verification.');
      return;
    }
    if (nextStage === 'upfront_payment' && !allVerified) {
      alert('NIN, BVN, and bank verification must all be marked verified before requesting the 30% upfront payment.');
      return;
    }
    setSensitiveConfirm({
      title: `Move to ${label}`,
      message: `Confirm moving ${app.full_name || 'this applicant'}\'s application to "${label}". Enter your admin password to proceed.`,
      confirmButtonLabel: `Move to ${label}`,
      onConfirm: async (sensitiveActionToken) => {
        const note = window.prompt(`Optional note for ${label.toLowerCase()}:`, app.notes || '')  -  '';
        try {
          await axios.put(`${API_URL}/api/admin/applications/${app.id}`,
            { workflowStage: nextStage, notes: note || undefined },
            { headers: { ...headers, 'X-Sensitive-Token': sensitiveActionToken } }
          );
          alert(` Application moved to ${label}.`);
          setSensitiveConfirm(null);
          setExpandedAppId(null);
          await loadApplications();
          await loadPayments();
          await loadStats();
        } catch (err) {
          alert(err.response?.data?.error || `Failed to move application to ${label}`);
        }
      },
    });
  };

  const handleDeleteApplication = (appId, tenantName, houseTitle) => {
    setSensitiveConfirm({
      title: 'Delete Application',
      message: `Permanently delete ${tenantName || 'this tenant'}\'s application for ${houseTitle || 'this property'}? This cannot be undone. Enter your admin password to confirm.`,
      confirmButtonLabel: 'Delete Application',
      onConfirm: async (sensitiveActionToken) => {
        try {
          await axios.delete(`${API_URL}/api/admin/applications/${appId}`,
            { headers: { ...headers, 'X-Sensitive-Token': sensitiveActionToken } }
          );
          alert('Application deleted.');
          setSensitiveConfirm(null);
          setExpandedAppId(null);
          await loadApplications();
          await loadStats();
        } catch (err) {
          alert(err.response?.data?.error || 'Failed to delete application');
        }
      },
    });
  };

  const handleVerificationCheckUpdate = async (app, field, value) => {
    const payload = {
      ninStatus: field === 'nin' ? value : (app.nin_verification_status || 'pending'),
      bvnStatus: field === 'bvn' ? value : (app.bvn_verification_status || 'pending'),
      bankStatus: field === 'bank' ? value : (app.bank_verification_status || 'pending'),
    };
    try {
      await axios.put(`${API_URL}/api/admin/applications/${app.id}/verification-checks`, payload, { headers });
      setApplications((prev) => prev.map((item) => item.id === app.id ? {
        ...item,
        nin_verification_status: payload.ninStatus,
        bvn_verification_status: payload.bvnStatus,
        bank_verification_status: payload.bankStatus,
      } : item));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update verification check');
    }
  };

  //  Live NIN Verification via NIMC (DOJAH) 
  const [ninVerifyResult, setNinVerifyResult] = React.useState({});
  const [ninVerifyLoading, setNinVerifyLoading] = React.useState({});
  const [expandedAppId, setExpandedAppId] = React.useState(null);
  const [monoStatement, setMonoStatement] = React.useState({});
  const [monoLoading, setMonoLoading] = React.useState({});
  const handleLiveNinVerify = async (app) => {
    const nin = app.tenant_nin;
    if (!nin || String(nin).replace(/\D/g, '').length !== 11) {
      alert('This tenant does not have a valid 11-digit NIN on file. Ask them to update their profile first.');
      return;
    }
    setNinVerifyLoading((prev) => ({ ...prev, [app.id]: true }));
    setNinVerifyResult((prev) => ({ ...prev, [app.id]: null }));
    try {
      const res = await axios.post(`${API_URL}/api/admin/verify-nin`, { nin, applicationId: app.id }, { headers });
      setNinVerifyResult((prev) => ({ ...prev, [app.id]: { ok: true, data: res.data.data } }));
      setApplications((prev) => prev.map((item) => item.id === app.id ? { ...item, nin_verification_status: 'verified' } : item));
    } catch (err) {
      const msg = err.response?.data?.error || 'NIN verification failed. Please try again.';
      setNinVerifyResult((prev) => ({ ...prev, [app.id]: { ok: false, error: msg } }));
    } finally {
      setNinVerifyLoading((prev) => ({ ...prev, [app.id]: false }));
    }
  };

  //  Live BVN Verification via NIBSS (DOJAH) 
  const [bvnVerifyResult, setBvnVerifyResult] = React.useState({});
  const [bvnVerifyLoading, setBvnVerifyLoading] = React.useState({});
  const handleLiveBvnVerify = async (app) => {
    const bvn = app.tenant_bvn;
    if (!bvn || String(bvn).replace(/\D/g, '').length !== 11) {
      alert('This tenant does not have a valid 11-digit BVN on file. Ask them to update their profile first.');
      return;
    }
    setBvnVerifyLoading((prev) => ({ ...prev, [app.id]: true }));
    setBvnVerifyResult((prev) => ({ ...prev, [app.id]: null }));
    try {
      const res = await axios.post(`${API_URL}/api/admin/verify-bvn`, { bvn, applicationId: app.id }, { headers });
      setBvnVerifyResult((prev) => ({ ...prev, [app.id]: { ok: true, data: res.data.data } }));
      setApplications((prev) => prev.map((item) => item.id === app.id ? { ...item, bvn_verification_status: 'verified' } : item));
    } catch (err) {
      const msg = err.response?.data?.error || 'BVN verification failed. Please try again.';
      setBvnVerifyResult((prev) => ({ ...prev, [app.id]: { ok: false, error: msg } }));
    } finally {
      setBvnVerifyLoading((prev) => ({ ...prev, [app.id]: false }));
    }
  };

  //  Admin: View Tenant Documents 
  const [docsModal, setDocsModal] = React.useState({ open: false, loading: false, docs: null, appId: null, tenantId: null });
  const [docRequests, setDocRequests] = React.useState([]);
  const [docRequestForm, setDocRequestForm] = React.useState({ name: '', description: '' });
  const [docRequestLoading, setDocRequestLoading] = React.useState(false);

  const handleViewDocuments = async (app) => {
    setDocsModal({ open: true, loading: true, docs: null, appId: app.id, tenantId: app.user_id });
    setDocRequests([]);
    setDocRequestForm({ name: '', description: '' });
    try {
      const [docsRes, reqRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/applications/${app.id}/documents`, { headers }),
        axios.get(`${API_URL}/api/admin/applications/${app.id}/document-requests`, { headers }),
      ]);
      setDocsModal(prev => ({ ...prev, loading: false, docs: docsRes.data.documents }));
      setDocRequests(reqRes.data.requests || []);
    } catch (err) {
      setDocsModal({ open: false, loading: false, docs: null, appId: null, tenantId: null });
      alert(err.response?.data?.error || 'Could not load tenant documents.');
    }
  };

  const handleRequestDocument = async () => {
    if (!docRequestForm.name.trim()) { alert('Please enter the document name.'); return; }
    setDocRequestLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/applications/${docsModal.appId}/request-document`,
        { documentName: docRequestForm.name, description: docRequestForm.description },
        { headers }
      );
      setDocRequestForm({ name: '', description: '' });
      // Reload requests
      const r = await axios.get(`${API_URL}/api/admin/applications/${docsModal.appId}/document-requests`, { headers });
      setDocRequests(r.data.requests || []);
      alert(' Document request sent to tenant by email and in-app notification.');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to send document request.');
    } finally {
      setDocRequestLoading(false);
    }
  };

  const handleDeleteDocRequest = async (reqId) => {
    if (!window.confirm('Remove this document request?')) return;
    await axios.delete(`${API_URL}/api/admin/document-requests/${reqId}`, { headers }).catch(() => {});
    setDocRequests(prev => prev.filter(r => r.id !== reqId));
  };

  //  Toggle application open/closed 
  const toggleApp = (id) => setExpandedAppId(prev => prev === id ? null : id);

  //  Fetch Mono bank statement 
  const handleFetchBankStatement = async (app) => {
    setMonoLoading(prev => ({ ...prev, [app.id]: true }));
    setMonoStatement(prev => ({ ...prev, [app.id]: null }));
    try {
      const res = await axios.get(`${API_URL}/api/admin/applications/${app.id}/bank-statement`, { headers });
      setMonoStatement(prev => ({ ...prev, [app.id]: { ok: true, data: res.data } }));
    } catch (err) {
      const msg = err.response?.data?.error || 'Could not fetch bank statement.';
      setMonoStatement(prev => ({ ...prev, [app.id]: { ok: false, error: msg } }));
    } finally {
      setMonoLoading(prev => ({ ...prev, [app.id]: false }));
    }
  };

  const handleRejectApplication = (id) => {
    setSensitiveConfirm({
      title: 'Reject Application',
      message: 'This will reject the application. Enter your admin password to confirm.',
      confirmButtonLabel: 'Reject Application',
      onConfirm: async (sensitiveActionToken) => {
        const reason = window.prompt('Reason for rejection (optional):')  -  '';
        try {
          await axios.put(`${API_URL}/api/admin/applications/${id}`,
            { status: 'rejected', notes: reason || 'Rejected by admin' },
            { headers: { ...headers, 'X-Sensitive-Token': sensitiveActionToken } }
          );
          alert('Application rejected');
          setSensitiveConfirm(null);
          await loadApplications();
          await loadStats();
        } catch (err) { alert('Failed to reject application'); }
      },
    });
  };

  // FIX #5: All destructive admin deletes open the password-confirm modal.
  // The backend now enforces X-Sensitive-Token on these routes, so sending without it fails with 401.
  const handleDeleteHouse = (id) => {
    setSensitiveConfirm({
      title: 'Delete house',
      message: 'This permanently removes the house and its listing data. This cannot be undone.',
      confirmButtonLabel: 'Delete House',
      onConfirm: async (sensitiveActionToken) => {
        await axios.delete(`${API_URL}/api/admin/houses/${id}`, {
          headers: { ...headers, 'X-Sensitive-Token': sensitiveActionToken },
        });
        await loadHouses();
        await loadStats();
        setSensitiveConfirm(null);
      },
    });
  };

  const handleRemoveFromWaitlist = (id) => {
    setSensitiveConfirm({
      title: 'Remove interest form entry',
      message: 'This removes the entry permanently.',
      confirmButtonLabel: 'Remove',
      onConfirm: async (sensitiveActionToken) => {
        await axios.delete(`${API_URL}/api/admin/waitlist/${id}`, {
          headers: { ...headers, 'X-Sensitive-Token': sensitiveActionToken },
        });
        setWaitlist(prev => prev.filter(e => e.id !== id));
        setSensitiveConfirm(null);
      },
    });
  };

  const openPermissionModal = (admin) => {
    setEditingAdmin(admin);
    setSelectedPermissions(admin.permissions || []);
    setShowPermissionModal(true);
  };

  const togglePermission = (permKey) => {
    setSelectedPermissions(prev => prev.includes(permKey) ? prev.filter(p => p !== permKey) : [...prev, permKey]);
  };

  const savePermissions = async () => {
    if (!editingAdmin) return;
    try {
      await axios.put(`${API_URL}/api/admin/users/${editingAdmin.id}/permissions`, { permissions: selectedPermissions }, { headers });
      alert(' Permissions updated!');
      setShowPermissionModal(false);
      await loadUsers();
    } catch (err) { alert(err.response?.data?.error || 'Failed to update permissions'); }
  };

  const handleDeleteAdmin = (adminId) => {
    setSensitiveConfirm({
      title: 'Delete admin account',
      message: 'This permanently removes the admin user. They will lose all access immediately.',
      confirmButtonLabel: 'Delete Admin',
      onConfirm: async (sensitiveActionToken) => {
        await axios.delete(`${API_URL}/api/admin/users/${adminId}`, {
          headers: { ...headers, 'X-Sensitive-Token': sensitiveActionToken },
        });
        await loadUsers();
        setSensitiveConfirm(null);
      },
    });
  };

  // Hard-delete a lease. Releases the house back to available. Tenant's
  // payment history is preserved (payments.rental_id just goes to NULL).
  const handleDeleteRental = (rental) => {
    setSensitiveConfirm({
      title: 'Delete lease',
      message: `Permanently delete the lease for ${rental.tenant_name} at ${rental.house_title}? The property will be marked available again. Payment history is preserved.`,
      confirmButtonLabel: 'Delete Lease',
      onConfirm: async (sensitiveActionToken) => {
        await axios.delete(`${API_URL}/api/admin/rentals/${rental.id}`, {
          headers: { ...headers, 'X-Sensitive-Token': sensitiveActionToken },
        });
        await loadRentals();
        setSensitiveConfirm(null);
      },
    });
  };

  // Hard-delete a cancelled or failed payment.
  // Backend rejects this for completed/pending payments.
  const handleDeletePayment = (payment) => {
    setSensitiveConfirm({
      title: 'Delete payment record',
      message: `Permanently remove this ${payment.status} payment of ${Number(payment.amount).toLocaleString()}? This cannot be undone.`,
      confirmButtonLabel: 'Delete Payment',
      onConfirm: async (sensitiveActionToken) => {
        await axios.delete(`${API_URL}/api/admin/payments/${payment.id}`, {
          headers: { ...headers, 'X-Sensitive-Token': sensitiveActionToken },
        });
        await loadPayments();
        setSensitiveConfirm(null);
      },
    });
  };

  // Hard-delete a rejected tenant house-submission (frees storage).
  const handleDeleteTenantSubmission = (submission) => {
    if (submission.status !== 'rejected') {
      alert(`Only rejected submissions can be deleted. This one is "${submission.status}". Reject it first.`);
      return;
    }
    setSensitiveConfirm({
      title: 'Delete rejected submission',
      message: `Permanently delete the rejected submission at ${submission.address || submission.location || '(no address)'}? Any uploaded images will also be removed from storage.`,
      confirmButtonLabel: 'Delete Submission',
      onConfirm: async (sensitiveActionToken) => {
        await axios.delete(`${API_URL}/api/admin/uploaded-houses/${submission.id}`, {
          headers: { ...headers, 'X-Sensitive-Token': sensitiveActionToken },
        });
        await loadTenantSubmissions();
        setSensitiveConfirm(null);
      },
    });
  };

  const handleAddHouses = async (e) => {
    e.preventDefault();
    if (housesToAdd.length === 0) return;
    setLoading(true);
    try {
      for (const house of housesToAdd) {
        const formData = new FormData();
        Object.keys(house).forEach(k => { if (k !== 'id' && k !== 'images') formData.append(k, house[k]); }); // cover_image_index is included above
        if (house.images?.length > 0) house.images.forEach(f => formData.append('images', f));
        await axios.post(`${API_URL}/api/admin/houses`, formData, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      }
      alert(` Successfully added ${housesToAdd.length} house(s)!`);
      setHousesToAdd([]);
      await loadHouses();
      await loadStats();
    } catch (err) { alert(err.response?.data?.error || 'Failed to add houses'); }
    finally { setLoading(false); }
  };

  const addNewHouseForm = () => setHousesToAdd(prev => [...prev, {
    id: Date.now(), title: '', description: '', location: '', address: '',
    bedrooms: 1, bathrooms: 1, annual_rent: '', images: [], amenities: '',
    status: 'sample', display_order: '', cover_image_index: 0
  }]);
  const updateHouse = (index, field, value) => { const u = [...housesToAdd]; u[index][field] = value; setHousesToAdd(u); };
  const handleHouseImages = (index, files) => { const u = [...housesToAdd]; u[index].images = Array.from(files).slice(0, 10); u[index].cover_image_index = 0; setHousesToAdd(u); };
  const removeHouseForm = (index) => setHousesToAdd(prev => prev.filter((_, i) => i !== index));

  const openEditModal = (house) => {
    setEditingHouse(house);
    setEditForm({
      title: house.title || '', description: house.description || '',
      location: house.location || '', address: house.address || '',
      bedrooms: house.bedrooms || 1, bathrooms: house.bathrooms || 1,
      annual_rent: house.annual_rent || '', images: [],
      amenities: Array.isArray(house.amenities) ? house.amenities.join(', ') : (house.amenities || ''),
      status: house.status || 'pending',
      display_order: house.display_order  -  '',
      cover_image_index: house.cover_image_index  -  0
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingHouse) return;
    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/admin/houses/${editingHouse.id}`, {
        title: editForm.title, description: editForm.description,
        location: editForm.location, address: editForm.address,
        bedrooms: parseInt(editForm.bedrooms), bathrooms: parseInt(editForm.bathrooms),
        annual_rent: parseFloat(editForm.annual_rent), amenities: editForm.amenities,
        status: editForm.status,
        display_order: editForm.display_order !== '' ? parseInt(editForm.display_order) : null,
        cover_image_index: parseInt(editForm.cover_image_index) || 0
      }, { headers });
      alert(' House updated!');
      setShowEditModal(false);
      setEditingHouse(null);
      await loadHouses();
      await loadStats();
    } catch (err) { alert(err.response?.data?.error || 'Failed to update house'); }
    finally { setLoading(false); }
  };

  const downloadWaitlistCSV = () => {
    if (waitlist.length === 0) { alert('No interest form entries'); return; }
    const headers_csv = ['#', 'Name', 'Phone', 'Email', 'House/Property', 'House Address', 'Preferred Location', 'Move-in Date', 'Budget', 'Message', 'Date Joined', 'Priority Fee Paid', 'Fee Amount', 'Paystack Ref'];
    const rows = waitlist.map((e, i) => [
      i + 1,
      `"${e.full_name || ''}"`,
      `"${e.phone || ''}"`,
      `"${e.email || ''}"`,
      `"${e.house_title || 'Any'}"`,
      `"${e.house_address || ''}"`,
      `"${e.location || ''}"`,
      `"${e.move_in_date ? new Date(e.move_in_date).toLocaleDateString('en-NG') : ''}"`,
      `"${e.budget ? `N${parseFloat(e.budget).toLocaleString()}` : ''}"`,
      `"${(e.message || '').replace(/"/g, "'")}"`,
      `"${e.joined_at ? new Date(e.joined_at).toLocaleDateString('en-NG') : ''}"`,
      e.priority_fee_paid ? 'Yes' : 'No',
      e.fee_amount || '0',
      `"${e.paystack_reference || ''}"`
    ].join(','));
    const csv = [headers_csv.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `literent_waitlist_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  const downloadTicketsCSV = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/exports/tickets?status=${ticketFilter}`, { headers, responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `literent_tickets_${ticketFilter}_${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      return;
    } catch (err) {
      console.error('Server ticket export failed, falling back to in-browser export:', err);
    }

    if (tickets.length === 0) { alert('No tickets to download'); return; }
    const headers_csv = ['#', 'Ticket ID', 'Subject', 'Message', 'Tenant Name', 'Tenant Phone', 'Tenant Email', 'Category', 'Priority', 'Status', 'Assigned To', 'Date Created', 'Last Updated'];
    const rows = tickets.map((t, i) => [
      i + 1,
      t.id,
      `"${(t.subject || '').replace(/"/g, "'")}"`,
      `"${(t.message || '').replace(/"/g, "'")}"`,
      `"${t.tenant_name || ''}"`,
      `"${t.tenant_phone || ''}"`,
      `"${t.tenant_email || ''}"`,
      `"${t.category || ''}"`,
      `"${t.priority || ''}"`,
      `"${t.status || ''}"`,
      `"${t.assigned_name || ''}"`,
      `"${t.created_at ? new Date(t.created_at).toLocaleString('en-NG') : ''}"`,
      `"${t.updated_at ? new Date(t.updated_at).toLocaleString('en-NG') : ''}"`
    ].join(','));
    const csv = [headers_csv.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `literent_tickets_${ticketFilter}_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  const handleLogout = () => {
    localStorage.removeItem('adminAuthToken');
        localStorage.removeItem('authToken');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/admin/login', { replace: true });
  };

  const openLightbox = (images, idx) => {
    setLightboxImages(images);
    setLightboxIdx(idx);
    setShowLightbox(true);
  };

  const DocLink = ({ url, label }) => {
    if (!url) return <span className="text-sm text-gray-400 italic">Not uploaded</span>;

    // Read category from hash  set by tenant at upload time
    const hashCat = url.match(/#cat=(image|pdf|doc|sheet)/)?.[1] || null;
    const cleanUrl = url.split('#')[0].replace('/image/upload/fl_inline/', '/image/upload/');

    const ext = cleanUrl.split('?')[0].split('.').pop()?.toLowerCase() || '';
    const extCat = (() => {
      if (['jpg','jpeg','png','gif','webp','heic','heif','bmp','tiff','svg'].includes(ext)) return 'image';
      if (ext === 'pdf') return 'pdf';
      if (['doc','docx','odt','txt'].includes(ext)) return 'doc';
      if (['xls','xlsx','ods','csv','ppt','pptx'].includes(ext)) return 'sheet';
      return 'image';
    })();
    const cat = hashCat || extCat;

    const catMeta = {
      image: { icon: '', label: 'Image',  badge: 'bg-blue-100 text-blue-700' },
      pdf:   { icon: '', label: 'PDF',    badge: 'bg-red-100 text-red-700' },
      doc:   { icon: '', label: 'Word',   badge: 'bg-indigo-100 text-indigo-700' },
      sheet: { icon: '', label: 'Sheet',  badge: 'bg-green-100 text-green-700' },
    }[cat] || { icon: '', label: 'File', badge: 'bg-gray-100 text-gray-600' };

    return (
      <div className="space-y-2">
        {/* Image thumbnail */}
        {cat === 'image' && (
          <img src={cleanUrl} alt={label} onClick={() => openDoc(url)}
            className="w-full h-24 object-cover rounded-lg border border-gray-200 hover:opacity-90 transition cursor-pointer"
            onError={e => e.target.style.display='none'} />
        )}
        {/* Category badge + open button */}
        <div className="flex items-center gap-2">
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${catMeta.badge}`}>
            {catMeta.icon} {catMeta.label}
          </span>
          <button onClick={() => openDoc(url)}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#294c79] text-white text-xs font-medium hover:bg-[#1f3a5c] transition">
            Open {label}
          </button>
        </div>
      </div>
    );
  };

  const filteredTenants = tenantsList.filter(t => {
    const q = tenantSearch.toLowerCase();
    return t.full_name?.toLowerCase().includes(q) || t.phone?.toLowerCase().includes(q) || t.email?.toLowerCase().includes(q);
  });

  const selectedTenant = tenantsList.find(t => String(t.id) === String(notifForm.userId));

  //  SHARED COMPONENTS 
  const Spinner = () => (
    <div className="flex justify-center py-20">
      <div className="animate-spin h-12 w-12 border-4 border-[#a5c250] border-t-transparent rounded-full" />
    </div>
  );

  const StatusBadge = ({ status }) => {
    const map = {
      pending: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-green-100 text-green-700',
      completed: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      failed: 'bg-red-100 text-red-700',
      open: 'bg-blue-100 text-blue-700',
      in_progress: 'bg-orange-100 text-orange-700',
      closed: 'bg-gray-100 text-gray-600',
      resolved: 'bg-green-100 text-green-700',
    };
    return <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${map[status] || 'bg-gray-100 text-gray-600'}`}>{status?.replace('_', ' ')}</span>;
  };

  const WorkflowBadge = ({ stage }) => {
    const meta = APPLICATION_STAGE_META[stage] || APPLICATION_STAGE_META.application;
    return <span className={`px-3 py-1 rounded-full text-xs font-semibold ${meta.tone}`}>{meta.label}</span>;
  };

  const HouseStatusBadge = ({ status }) => {
    const map = { verified: 'bg-emerald-100 text-emerald-700', pending: 'bg-amber-100 text-amber-700', sample: 'bg-violet-100 text-violet-700' };
    return <span className={`px-3 py-1 rounded-full text-xs font-semibold ${map[status] || 'bg-gray-100 text-gray-600'}`}>{status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Pending'}</span>;
  };

  const ticketStatusColors = {
    open: 'bg-blue-100 text-blue-700 border-blue-200',
    in_progress: 'bg-orange-100 text-orange-700 border-orange-200',
    resolved: 'bg-green-100 text-green-700 border-green-200',
    closed: 'bg-gray-100 text-gray-600 border-gray-200',
  };

  return (
    <div className="min-h-screen bg-[#f5f7fa] flex flex-col lg:flex-row">

      {/* Desktop Sidebar */}
      <aside className="w-64 xl:w-72 bg-[#294c79] text-white hidden lg:flex flex-col fixed h-full z-20">
        <div className="p-6 xl:p-8 flex flex-col h-full overflow-y-auto">
          <Link to="/" className="flex items-center gap-3 mb-10">
            <img src="/Logo.jpg" alt="LiteRent" className="h-12 w-auto object-contain bg-white rounded-lg p-1" />
          </Link>
          <div className="mb-4">
            <h2 className="text-xl xl:text-2xl font-bold">Admin Panel</h2>
            <p className="text-xs text-white/60 mt-1">{isSuperAdmin ? 'Super Admin' : 'Admin'}</p>
          </div>
          <nav className="flex-1 space-y-1">
            {navItems.map(item => (
              <button key={item.id} onClick={() => setActiveTab(item.id)}
                className={`w-full text-left px-4 xl:px-5 py-3 rounded-xl transition font-medium flex items-center gap-3 text-sm xl:text-base ${activeTab === item.id ? 'bg-white text-[#294c79]' : 'text-white/80 hover:bg-white/10'}`}>
                {item.icon && <span className="text-lg">{item.icon}</span>}{item.label}
              </button>
            ))}
          </nav>
          <button onClick={handleLogout} className="mt-8 w-full px-5 py-3 bg-red-600 hover:bg-red-700 rounded-xl font-medium transition text-sm">Logout</button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 bg-[#294c79] text-white z-30 px-4 py-3 flex items-center justify-between shadow-md">
        <div className="flex items-center gap-2">
          <img src="/Logo.jpg" alt="LiteRent" className="h-8 w-auto object-contain bg-white rounded p-0.5" />
          <h1 className="text-lg font-bold">Admin Panel</h1>
        </div>
        <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-2 rounded-lg hover:bg-white/10">
          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {isMobileMenuOpen
              ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />}
          </svg>
        </button>
      </header>

      {/* Mobile Drawer */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/60" onClick={() => setIsMobileMenuOpen(false)}>
          <div className="absolute inset-y-0 left-0 w-72 bg-[#294c79] text-white flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="p-5 flex items-center justify-between border-b border-white/20">
              <span className="font-bold text-lg">Menu</span>
              <button onClick={() => setIsMobileMenuOpen(false)} className="text-white/70 hover:text-white text-2xl" aria-label="Close menu">x</button>
            </div>
            <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
              {navItems.map(item => (
                <button key={item.id} onClick={() => { setActiveTab(item.id); setIsMobileMenuOpen(false); }}
                  className={`w-full text-left px-4 py-3 rounded-xl transition font-medium flex items-center gap-3 ${activeTab === item.id ? 'bg-white text-[#294c79]' : 'text-white/80 hover:bg-white/10'}`}>
                  {item.icon && <span className="text-lg">{item.icon}</span>}{item.label}
                </button>
              ))}
            </nav>
            <div className="p-4 border-t border-white/20">
              <button onClick={handleLogout} className="w-full px-5 py-3 bg-red-600 hover:bg-red-700 rounded-xl font-medium">Logout</button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 xl:ml-72 min-w-0 p-4 sm:p-6 lg:p-8 xl:p-10">

        {/*  OVERVIEW  */}
        {activeTab === 'overview' && (
          <div>
            <h1 className="text-2xl sm:text-3xl xl:text-4xl font-bold text-[#294c79] mb-6 sm:mb-10">Dashboard Overview</h1>
            {statsLoading ? <Spinner /> : (
              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4 mb-6 sm:mb-10">
                {[
                  { label: 'Total Tenants', value: stats.users, border: 'border-blue-500', text: 'text-blue-600' },
                  { label: 'Houses on Browse', value: `${stats.houses  -  0} / ${stats.housesTotal  -  0}`, border: 'border-green-500', text: 'text-green-600' },
                  { label: 'Applications', value: stats.applications  -  0, border: 'border-yellow-500', text: 'text-yellow-600' },
                  ...(isSuperAdmin ? [{ label: 'Revenue', value: `${(stats.revenue || 0).toLocaleString()}`, border: 'border-purple-500', text: 'text-purple-600' }] : []),
                  { label: 'Interest Forms', value: stats.waitlist || 0, border: 'border-orange-500', text: 'text-orange-600' },
                  { label: 'Open Tickets', value: stats.openTickets || 0, border: 'border-red-500', text: 'text-red-600' },
                ].map(s => (
                  <div key={s.label} className={`bg-white p-4 sm:p-6 rounded-2xl shadow-sm border-l-4 ${s.border}`}>
                    <p className="text-gray-500 text-xs mb-1">{s.label}</p>
                    <p className={`text-xl sm:text-3xl font-bold ${s.text} break-all`}>{s.value}</p>
                  </div>
                ))}
              </div>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-sm">
                <h3 className="text-lg sm:text-xl font-semibold text-[#294c79] mb-4">Quick Actions</h3>
                <div className="space-y-3">
                  {hasPermission('manage_houses') && <button onClick={() => setActiveTab('add-house')} className="w-full px-4 py-3 bg-[#a5c250] hover:bg-[#8bb83a] text-white rounded-xl font-medium text-left text-sm">Add New House</button>}
                  {hasPermission('manage_applications') && <button onClick={() => setActiveTab('applications')} className="w-full px-4 py-3 bg-[#294c79] hover:bg-[#1f3a5c] text-white rounded-xl font-medium text-left text-sm">Review Applications</button>}
                  {hasPermission('manage_tickets') && <button onClick={() => setActiveTab('tickets')} className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium text-left text-sm">Support Tickets {stats.openTickets > 0 && <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{stats.openTickets}</span>}</button>}
                  {hasPermission('manage_waitlist') && <button onClick={() => setActiveTab('waitlist')} className="w-full px-4 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-medium text-left text-sm">View Interest Forms</button>}
                  {hasPermission('manage_notifications') && <button onClick={() => setActiveTab('contact-messages')} className="w-full px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium text-left text-sm">Contact Messages</button>}
                  <button onClick={() => setActiveTab('tenant-submissions')} className="w-full px-4 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-medium text-left text-sm">Tenant Submissions</button>
                </div>
              </div>
              <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-sm">
                <h3 className="text-lg sm:text-xl font-semibold text-[#294c79] mb-4">System Status</h3>
                <div className="space-y-4">
                  {(systemStatusLoading ? [] : Object.values(systemStatus)).map(s => (
                    <div key={s.label} className="flex items-center justify-between gap-3">
                      <span className="text-gray-600 text-sm">{s.label}</span>
                      <span title={s.detail || ''} className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${
                        s.status === 'online' ? 'bg-green-100 text-green-700' :
                        s.status === 'degraded' ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                      }`}>{s.status}</span>
                    </div>
                  ))}
                  {systemStatusLoading && <p className="text-sm text-gray-500">Checking services...</p>}
                  {!systemStatusLoading && Object.values(systemStatus).length === 0 && <p className="text-sm text-gray-500">Status unavailable.</p>}
                </div>
                {isSuperAdmin && (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-700">
                    You have full Super Admin access to all features and permissions.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/*  USERS  */}
        {activeTab === 'users' && (
          <div>
            <h1 className="text-2xl sm:text-4xl font-bold text-[#294c79] mb-6 sm:mb-10">All Users</h1>
            {loading ? <Spinner /> : (isSuperAdmin ? users : users.filter(u => u.role === 'tenant')).length === 0 ? (
              <div className="bg-white p-10 rounded-2xl shadow-sm text-center text-xl text-gray-500">No users yet</div>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-max w-full text-sm">
                    <thead className="bg-[#e8f2d1]">
                      <tr>{['#', 'User ID', 'Name', 'Phone', 'Email', 'Role', 'Verified', 'Joined'].map(h => <th key={h} className="px-4 sm:px-6 py-4 text-left font-semibold text-[#294c79] whitespace-nowrap">{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {(isSuperAdmin ? users : users.filter(u => u.role === 'tenant')).map((user, rowIndex) => (
                        <tr key={user.id} className="border-t border-gray-100 hover:bg-gray-50">
                          <td className="px-4 sm:px-6 py-3 text-gray-500 font-semibold">{rowIndex + 1}</td>
                          <td className="px-4 sm:px-6 py-3 text-gray-400">{user.id}</td>
                          <td className="px-4 sm:px-6 py-3 font-medium whitespace-nowrap">{user.full_name}</td>
                          <td className="px-4 sm:px-6 py-3 whitespace-nowrap">{user.phone}</td>
                          <td className="px-4 sm:px-6 py-3">{user.email || ''}</td>
                          <td className="px-4 sm:px-6 py-3 whitespace-nowrap capitalize">{user.role || 'tenant'}</td>
                          <td className="px-4 sm:px-6 py-3">{user.is_verified ? <span className="text-green-600"></span> : <span className="text-gray-400"></span>}</td>
                          <td className="px-4 sm:px-6 py-3 text-gray-500 whitespace-nowrap">{new Date(user.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/*  HOUSES  */}
        {activeTab === 'houses' && (
          <div>
            <div className="flex flex-wrap gap-3 justify-between items-center mb-6 sm:mb-8">
              <h1 className="text-2xl sm:text-4xl font-bold text-[#294c79]">All Houses</h1>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => setActiveTab('add-house')} className="px-4 sm:px-5 py-2 sm:py-3 bg-[#a5c250] text-white rounded-xl hover:bg-[#8bb83a] font-medium text-sm"> Add House</button>
                <button
                  onClick={() => { setHouseOrderMode(!houseOrderMode); if (!houseOrderMode) setOrderedHouses([...houses].sort((a,b)=>(a.display_order - 999)-(b.display_order - 999))); }}
                  className={`px-4 sm:px-5 py-2 sm:py-3 rounded-xl font-medium text-sm transition ${houseOrderMode ? 'bg-orange-500 text-white hover:bg-orange-600' : 'bg-[#294c79] text-white hover:bg-[#1f3a5c]'}`}>
                  {houseOrderMode ? ' Cancel Reorder' : ' Set Display Order'}
                </button>
              </div>
            </div>

            {/*  REORDER MODE  */}
            {houseOrderMode && (
              <div className="mb-6">
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-4 text-sm text-amber-800">
                  <strong> Reorder Mode</strong>  Drag and drop houses to set the order they appear on the Browse page. <strong>Position 1</strong> shows first.
                  <span className="block mt-1 text-xs text-amber-600">On mobile, use the   buttons to move houses up or down.</span>
                </div>
                <div className="space-y-3">
                  {orderedHouses.map((house, idx) => (
                    <div
                      key={house.id}
                      draggable
                      onDragStart={() => handleDragStart(idx)}
                      onDragOver={(e) => handleDragOver(e, idx)}
                      onDragEnd={handleDragEnd}
                      className={`bg-white rounded-2xl border-2 p-4 flex items-center gap-4 cursor-grab active:cursor-grabbing transition select-none
                        ${draggedIdx === idx ? 'border-[#a5c250] shadow-lg opacity-80 scale-[1.01]' : 'border-gray-200 hover:border-[#a5c250]/50'}`}
                    >
                      {/* Position badge */}
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#294c79] text-white flex items-center justify-center font-bold text-lg">
                        {idx + 1}
                      </div>

                      {/* Drag handle */}
                      <div className="flex-shrink-0 text-gray-300 hover:text-gray-500 hidden sm:block text-xl cursor-grab"></div>

                      {/* Thumbnail */}
                      <img
                        src={house.images?.[house.cover_image_index  -  0] || house.images?.[0] || 'https://via.placeholder.com/80x60?text=No+Img'}
                        alt={house.title}
                        className="w-16 h-12 rounded-xl object-cover flex-shrink-0"
                      />

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-[#294c79] truncate">{house.title}</p>
                        <p className="text-xs text-gray-500 truncate">{house.location}  {parseFloat(house.annual_rent || 0).toLocaleString()}/yr</p>
                      </div>

                      <HouseStatusBadge status={house.status} />

                      {/* Mobile up/down buttons */}
                      <div className="flex flex-col gap-1 sm:hidden flex-shrink-0">
                        <button onClick={() => moveHouse(idx, -1)} disabled={idx === 0} className="px-2 py-1 bg-gray-100 rounded-lg text-xs disabled:opacity-30 hover:bg-gray-200" aria-label="Move house up">Up</button>
                        <button onClick={() => moveHouse(idx, 1)} disabled={idx === orderedHouses.length - 1} className="px-2 py-1 bg-gray-100 rounded-lg text-xs disabled:opacity-30 hover:bg-gray-200" aria-label="Move house down">Down</button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex gap-3 mt-6">
                  <button onClick={() => setHouseOrderMode(false)} className="flex-1 py-3 border-2 border-gray-300 rounded-xl font-medium text-gray-600 hover:bg-gray-50">Cancel</button>
                  <button onClick={saveHouseOrder} disabled={savingOrder}
                    className="flex-1 py-3 bg-[#a5c250] text-white rounded-xl font-semibold hover:bg-[#8bb83a] disabled:bg-gray-400 transition flex items-center justify-center gap-2">
                    {savingOrder ? <><div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />Saving</> : ' Save Display Order'}
                  </button>
                </div>
              </div>
            )}

            {/*  NORMAL HOUSE GRID  */}
            {!houseOrderMode && (
              loading ? <Spinner /> : houses.length === 0 ? (
                <div className="bg-white p-10 rounded-2xl shadow-sm text-center">
                  <p className="text-xl text-gray-500 mb-6">No houses yet</p>
                  <button onClick={() => setActiveTab('add-house')} className="px-8 py-4 bg-[#a5c250] text-white rounded-xl font-medium">Add Your First House</button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
                  {[...houses].sort((a,b)=>(a.display_order - 999)-(b.display_order - 999)).map((house, idx) => (
                    <div key={house.id} className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-200">
                      <div className="relative">
                        <img src={house.images?.[house.cover_image_index  -  0] || house.images?.[0] || 'https://via.placeholder.com/400x300?text=No+Image'} alt={house.title} className="w-full h-44 object-cover" />
                        <div className="absolute top-3 right-3"><HouseStatusBadge status={house.status} /></div>
                        {/* Display order badge */}
                        <div className="absolute top-3 left-3 w-8 h-8 bg-[#294c79] text-white rounded-full flex items-center justify-center text-xs font-bold shadow">
                          {house.display_order  -  idx + 1}
                        </div>
                      </div>
                      <div className="p-4 sm:p-6">
                        <h3 className="text-lg font-semibold text-[#294c79] mb-1">{house.title}</h3>
                        <p className="text-gray-500 text-sm mb-2">{house.location}</p>
                        <p className="text-xl font-bold text-[#a5c250] mb-3">{parseFloat(house.annual_rent || 0).toLocaleString()} / yr</p>
                        <div className="flex gap-3 text-sm text-gray-500 mb-4"><span> {house.bedrooms} bed</span><span> {house.bathrooms} bath</span></div>
                        <div className="flex gap-2">
                          <Link to={`/house/${house.id}`} className="flex-1 px-3 py-2 bg-[#294c79] text-white rounded-xl font-medium text-center text-xs">View</Link>
                          <button onClick={() => openEditModal(house)} className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-xl font-medium text-xs"> Edit</button>
                          <button onClick={() => handleDeleteHouse(house.id)} className="flex-1 px-3 py-2 bg-red-600 text-white rounded-xl font-medium text-xs">Delete</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </div>
        )}

        {/*  ADD HOUSE  */}
        {activeTab === 'add-house' && (
          <div>
            <div className="flex flex-wrap gap-3 justify-between items-center mb-6 sm:mb-8">
              <h1 className="text-2xl sm:text-4xl font-bold text-[#294c79]">Add Houses</h1>
              <button onClick={addNewHouseForm} className="px-4 sm:px-6 py-2 sm:py-3 bg-[#a5c250] text-white rounded-xl hover:bg-[#8bb83a] font-medium text-sm"> Add Another</button>
            </div>
            {housesToAdd.length === 0 ? (
              <div className="bg-white p-10 rounded-2xl shadow-sm text-center">
                <p className="text-xl text-gray-500 mb-6">No houses to add yet</p>
                <button onClick={addNewHouseForm} className="px-8 py-4 bg-[#a5c250] text-white rounded-xl font-medium text-lg">Start Adding Houses</button>
              </div>
            ) : (
              <form onSubmit={handleAddHouses}>
                <div className="space-y-8">
                  {housesToAdd.map((house, index) => (
                    <div key={house.id} className="bg-white p-5 sm:p-8 rounded-2xl shadow-sm border border-[#e8f2d1]">
                      <div className="flex justify-between items-center mb-5">
                        <h3 className="text-xl font-semibold text-[#294c79]">House {index + 1}</h3>
                        <button type="button" onClick={() => removeHouseForm(index)} className="text-red-600 hover:text-red-700 font-medium text-sm">Remove</button>
                      </div>
                      <div className="space-y-5">
                        {[['Title', 'title', 'text', true], ['Description', 'description', 'text', false]].map(([label, field, type, req]) => (
                          <div key={field}>
                            <label className="block text-sm font-medium text-[#294c79] mb-2">{label} {req && <span className="text-red-500">*</span>}</label>
                            {field === 'description' ? (
                              <textarea value={house[field]} onChange={e => updateHouse(index, field, e.target.value)} className="w-full px-4 py-3 border-2 border-[#afc09e] rounded-xl h-24 focus:outline-none focus:border-[#a5c250]" />
                            ) : (
                              <input type={type} value={house[field]} onChange={e => updateHouse(index, field, e.target.value)} className="w-full px-4 py-3 border-2 border-[#afc09e] rounded-xl focus:outline-none focus:border-[#a5c250]" required={req} />
                            )}
                          </div>
                        ))}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {[['Location', 'location', true], ['Address', 'address', false]].map(([label, field, req]) => (
                            <div key={field}>
                              <label className="block text-sm font-medium text-[#294c79] mb-2">{label} {req && <span className="text-red-500">*</span>}</label>
                              <input type="text" value={house[field]} onChange={e => updateHouse(index, field, e.target.value)} className="w-full px-4 py-3 border-2 border-[#afc09e] rounded-xl focus:outline-none focus:border-[#a5c250]" required={req} />
                            </div>
                          ))}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          {[['Bedrooms', 'bedrooms'], ['Bathrooms', 'bathrooms']].map(([label, field]) => (
                            <div key={field}>
                              <label className="block text-sm font-medium text-[#294c79] mb-2">{label}</label>
                              <input type="number" value={house[field]} min="1" onChange={e => updateHouse(index, field, e.target.value)} className="w-full px-4 py-3 border-2 border-[#afc09e] rounded-xl focus:outline-none focus:border-[#a5c250]" />
                            </div>
                          ))}
                          <div>
                            <label className="block text-sm font-medium text-[#294c79] mb-2">Annual Rent (NGN) <span className="text-red-500">*</span></label>
                            <input type="number" value={house.annual_rent} onChange={e => updateHouse(index, 'annual_rent', e.target.value)} className="w-full px-4 py-3 border-2 border-[#afc09e] rounded-xl focus:outline-none focus:border-[#a5c250]" required />
                          </div>
                        </div>

                        {/*  DISPLAY ORDER FIELD  */}
                        <div>
                          <label className="block text-sm font-medium text-[#294c79] mb-2">
                            Display Order
                            <span className="ml-2 text-xs font-normal text-gray-500">(determines position on Browse page)</span>
                          </label>
                          <div className="flex items-center gap-3">
                            <input
                              type="number"
                              min="1"
                              value={house.display_order}
                              onChange={e => updateHouse(index, 'display_order', e.target.value)}
                              placeholder="e.g. 1 = appears first"
                              className="w-full px-4 py-3 border-2 border-[#afc09e] rounded-xl focus:outline-none focus:border-[#a5c250]"
                            />
                          </div>
                          <p className="text-xs text-gray-400 mt-1.5">
                             Leave blank to add at the end. Enter <strong>1</strong> to show this house first, <strong>2</strong> for second, etc.
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-[#294c79] mb-2">House Status <span className="text-red-500">*</span></label>
                          <select value={house.status} onChange={e => updateHouse(index, 'status', e.target.value)} className="w-full px-4 py-3 border-2 border-[#afc09e] rounded-xl focus:outline-none focus:border-[#a5c250]">
                            <option value="pending">Pending (Not visible on browse page)</option>
                            <option value="verified">Verified (Visible on browse page)</option>
                            <option value="sample">Sample House</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-[#294c79] mb-2">
                            Images (Max 10)
                            <span className="ml-2 text-xs font-normal text-gray-400"> click a preview to set it as the cover image</span>
                          </label>
                          <input type="file" multiple accept="image/*" onChange={e => handleHouseImages(index, e.target.files)} className="w-full px-4 py-3 border-2 border-[#afc09e] rounded-xl" />
                          {house.images.length > 0 && (
                            <div className="mt-3">
                              <p className="text-xs font-semibold text-gray-500 mb-2">
                                {house.images.length} image(s) selected  
                                <span className="text-[#294c79]"> Cover: Photo {(house.cover_image_index || 0) + 1}</span>
                              </p>
                              <div className="flex gap-2 flex-wrap">
                                {house.images.map((file, imgIdx) => (
                                  <button
                                    key={imgIdx}
                                    type="button"
                                    onClick={() => { const u = [...housesToAdd]; u[index].cover_image_index = imgIdx; setHousesToAdd(u); }}
                                    className={`relative w-20 h-20 rounded-xl overflow-hidden border-3 transition flex-shrink-0 ${(house.cover_image_index || 0) === imgIdx ? 'border-[#a5c250] ring-2 ring-[#a5c250]' : 'border-gray-200 hover:border-[#a5c250]/50'}`}
                                    title={`Set photo ${imgIdx + 1} as cover`}
                                  >
                                    <img src={URL.createObjectURL(file)} alt={`Preview ${imgIdx + 1}`} className="w-full h-full object-cover" />
                                    {(house.cover_image_index || 0) === imgIdx ? (
                                      <div className="absolute inset-0 bg-[#a5c250]/20 flex items-end justify-center pb-1">
                                        <span className="bg-[#a5c250] text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">COVER</span>
                                      </div>
                                    ) : (
                                      <div className="absolute inset-0 bg-black/0 hover:bg-black/20 flex items-center justify-center opacity-0 hover:opacity-100 transition">
                                        <span className="text-white text-[9px] font-bold bg-black/50 px-1.5 py-0.5 rounded-full">Set Cover</span>
                                      </div>
                                    )}
                                  </button>
                                ))}
                              </div>
                              <p className="text-xs text-gray-400 mt-2"> The cover image appears as the main photo on the Browse page.</p>
                            </div>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-[#294c79] mb-2">Amenities (comma-separated)</label>
                          <input type="text" value={house.amenities} onChange={e => updateHouse(index, 'amenities', e.target.value)} placeholder="Parking, Security, Backup Power" className="w-full px-4 py-3 border-2 border-[#afc09e] rounded-xl focus:outline-none focus:border-[#a5c250]" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <button type="submit" disabled={loading || housesToAdd.length === 0}
                  className={`w-full py-4 rounded-xl font-semibold text-lg mt-6 ${loading || housesToAdd.length === 0 ? 'bg-gray-400 text-white cursor-not-allowed' : 'bg-[#a5c250] text-white hover:bg-[#8bb83a] shadow-md'}`}>
                  {loading ? 'Adding Houses' : `Submit All ${housesToAdd.length} House(s)`}
                </button>
              </form>
            )}
          </div>
        )}

        {/*  APPLICATIONS  */}
        {activeTab === 'applications' && (
          <div>
            <h1 className="text-2xl sm:text-4xl font-bold text-[#294c79] mb-6 sm:mb-10">Applications</h1>
            {loading ? <Spinner /> : applications.length === 0 ? (
              <div className="bg-white p-10 rounded-2xl shadow-sm text-center text-xl text-gray-500">No applications yet</div>
            ) : (
              <div className="space-y-3">
                {applications.map(app => {
                  const stage = app.workflow_stage || 'application';
                  const meta = APPLICATION_STAGE_META[stage] || APPLICATION_STAGE_META.application;
                  const stageSequence = ['application', 'verification', 'upfront_payment', 'direct_debit', 'active'];
                  const stageIndex = stageSequence.indexOf(stage);
                  const isOpen = expandedAppId === app.id;

                  return (
                    <div key={app.id} className={`rounded-2xl border transition-all duration-200 overflow-hidden ${isOpen ? 'border-[#294c79] shadow-lg' : 'border-gray-200 bg-white shadow-sm hover:border-[#294c79]/40 hover:shadow-md'}`}>

                      {/*  Clickable Summary Row  */}
                      <button
                        onClick={() => toggleApp(app.id)}
                        className="w-full text-left px-5 sm:px-6 py-4 bg-white flex items-center justify-between gap-4 group"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <span className="font-bold text-[#294c79] text-base truncate">
                              Application from {app.full_name}
                            </span>
                            <span className="text-gray-400 text-sm hidden sm:inline"></span>
                            <span className="text-gray-500 text-sm truncate hidden sm:inline">{app.house_title}</span>
                          </div>
                          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-400">
                            <span>{app.phone}</span>
                            <span></span>
                            <span>{app.location}</span>
                            <span></span>
                            <span>{new Date(app.created_at).toLocaleDateString('en-NG')}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <StatusBadge status={app.status} />
                          <WorkflowBadge stage={stage} />
                          <svg className={`w-5 h-5 text-[#294c79] transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </button>

                      {/*  Expanded Detail Panel  */}
                      {isOpen && (
                        <div className="border-t border-gray-100 bg-[#fbfcfe] px-5 sm:px-6 py-6 space-y-6">

                          {/* Applicant Details */}
                          <div>
                            <div className="flex items-center gap-3 mb-3">
                              <p className="text-xs font-bold text-[#294c79] uppercase tracking-wider">Applicant Details</p>
                              <span className={`px-3 py-1 rounded-full text-xs font-bold ${app.applicant_type === 'student' ? 'bg-purple-100 text-purple-700' : app.applicant_type === 'nysc' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                                {app.applicant_type === 'student' ? ' Student' : app.applicant_type === 'nysc' ? ' NYSC' : ' Employed'}
                              </span>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm mb-3">
                              {[
                                ['Full Name', app.full_name],
                                ['Phone', app.phone],
                                ['Email', app.email || ''],
                                ['Applied', new Date(app.created_at).toLocaleDateString('en-NG')],
                                ['Property', app.house_title],
                                ['Location', app.location],
                                ['Annual Rent', app.annual_rent ? `${parseFloat(app.annual_rent).toLocaleString()}` : ''],
                                ['Monthly Income', app.monthly_income ? `${parseFloat(app.monthly_income).toLocaleString()}` : ''],
                              ].map(([label, val]) => (
                                <div key={label} className="rounded-xl bg-white border border-[#edf2f7] p-3">
                                  <p className="text-xs text-gray-400 mb-0.5">{label}</p>
                                  <p className="font-semibold text-[#1a1a1a] text-sm">{val}</p>
                                </div>
                              ))}
                            </div>

                            {/* Student-specific summary */}
                            {app.applicant_type === 'student' && (
                              <div className="rounded-xl border border-purple-200 bg-purple-50 p-4 mb-3">
                                <p className="text-xs font-bold text-purple-700 uppercase tracking-wide mb-3">Student Details</p>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                                  {[
                                    ['Institution', app.institution_name],
                                    ['Level', app.student_level],
                                    ['Matric No.', app.matric_number],
                                    ['Parent/Guardian', app.parent_full_name],
                                    ['Parent Phone', app.parent_phone],
                                    ['Parent Bank', app.parent_bank_name],
                                    ['Parent Account', app.parent_account_number ? `${String(app.parent_account_number).slice(-4)}` : ''],
                                    ['Parent Acct Name', app.parent_bank_account_name],
                                  ].map(([k, v]) => (
                                    <div key={k} className="rounded-lg bg-white border border-purple-100 p-2">
                                      <p className="text-gray-400">{k}</p>
                                      <p className="font-semibold text-purple-800">{v || ''}</p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* NYSC-specific summary */}
                            {app.applicant_type === 'nysc' && (
                              <div className="rounded-xl border border-green-200 bg-green-50 p-4 mb-3">
                                <p className="text-xs font-bold text-green-700 uppercase tracking-wide mb-3">NYSC Service Details</p>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                                  {[
                                    ['State Code', app.nysc_state_code],
                                    ['Call-Up No.', app.nysc_call_up_number],
                                    ['Corps No.', app.nysc_corps_number],
                                    ['Service State', app.nysc_service_state],
                                    ['Service LGA', app.nysc_service_lga],
                                    ['PPA', app.nysc_ppa_name],
                                    ['PPA Type', app.nysc_ppa_type],
                                    ['Allawee Bank', app.nysc_allawee_bank_name],
                                    ['Allawee Account', app.nysc_allawee_account_number ? `${String(app.nysc_allawee_account_number).slice(-4)}` : ''],
                                    ['Sponsor', app.nysc_sponsor_full_name],
                                    ['Sponsor Phone', app.nysc_sponsor_phone],
                                    ['Sponsor Bank', app.nysc_sponsor_bank_name],
                                    ['Sponsor Account', app.nysc_sponsor_account_number ? `${String(app.nysc_sponsor_account_number).slice(-4)}` : ''],
                                    ['Sponsor Acct Name', app.nysc_sponsor_account_name],
                                  ].map(([k, v]) => (
                                    <div key={k} className="rounded-lg bg-white border border-green-100 p-2">
                                      <p className="text-gray-400">{k}</p>
                                      <p className="font-semibold text-green-800">{v || ''}</p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Stage Progress */}
                          <div>
                            <p className="text-xs font-bold text-[#294c79] uppercase tracking-wider mb-3">Application Progress</p>
                            <div className="grid grid-cols-5 gap-2">
                              {stageSequence.map((step, index) => {
                                const stepMeta = APPLICATION_STAGE_META[step];
                                const isCurrent = stage === step;
                                const isComplete = stageIndex >= 0 && index < stageIndex;
                                return (
                                  <div key={step} className={`rounded-xl border p-2.5 text-center ${isCurrent ? 'border-[#294c79] bg-[#294c79] text-white' : isComplete ? 'border-[#a5c250] bg-[#f5f9ee] text-[#294c79]' : 'border-gray-200 bg-white text-gray-400'}`}>
                                    <div className="text-[10px] uppercase tracking-wide font-bold opacity-70">Step {index + 1}</div>
                                    <div className="font-semibold text-xs mt-1 leading-tight">{stepMeta.label}</div>
                                    {isComplete && <div className="text-[10px] mt-1 opacity-80"> Done</div>}
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          {/*  Identity & Bank Numbers  */}
                          <div>
                            <p className="text-xs font-bold text-[#294c79] uppercase tracking-wider mb-3">Identity & Banking</p>
                            <div className="grid md:grid-cols-4 gap-3 mb-4 text-sm">
                              <div className="rounded-xl border border-[#e8f2d1] bg-[#f8fbf2] p-3"><p className="text-xs text-gray-500 mb-1">Tenant NIN</p><p className="font-semibold font-mono text-sm">{app.tenant_nin || ''}</p></div>
                              <div className="rounded-xl border border-[#e8f2d1] bg-[#f8fbf2] p-3"><p className="text-xs text-gray-500 mb-1">Tenant BVN</p><p className="font-semibold font-mono text-sm">{app.tenant_bvn || ''}</p></div>
                              <div className="rounded-xl border border-[#e8f2d1] bg-[#f8fbf2] p-3"><p className="text-xs text-gray-500 mb-1">Bank</p><p className="font-semibold text-sm">{app.tenant_bank_name || ''}</p></div>
                              <div className="rounded-xl border border-[#e8f2d1] bg-[#f8fbf2] p-3"><p className="text-xs text-gray-500 mb-1">Account</p><p className="font-semibold text-sm">{app.tenant_bank_account_name || ''} {app.tenant_account_number ? `${String(app.tenant_account_number).slice(-4)}` : ''}</p></div>
                            </div>

                            {/*  NIN: Portal link + manual verify  */}
                            <div className="rounded-xl border border-[#d9e5ef] bg-white p-4 mb-3">
                              <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                                <div>
                                  <p className="text-sm font-semibold text-[#294c79]">NIN Verification</p>
                                  <p className="text-xs text-gray-400 mt-0.5">Open the NIMC portal, search this NIN, confirm name matches, then mark verified.</p>
                                  <span className={`inline-block mt-2 px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize ${verificationTone(app.nin_verification_status || 'pending')}`}>{String(app.nin_verification_status || 'pending').replace('_', ' ')}</span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  <button
                                    onClick={() => { navigator.clipboard?.writeText(app.tenant_nin || ''); }}
                                    className="px-3 py-2 rounded-xl border border-[#d9e5ef] text-[#294c79] text-xs font-semibold hover:bg-[#f3f7fb] transition"
                                    title="Copy NIN to clipboard"
                                  >
                                     Copy NIN
                                  </button>
                                  <a
                                    href="https://nimc.gov.ng/nin-verification/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-3 py-2 rounded-xl bg-[#294c79] text-white text-xs font-semibold hover:bg-[#1f3a5c] transition"
                                  >
                                     Open NIMC Portal
                                  </a>
                                </div>
                              </div>
                              {app.tenant_nin && (
                                <div className="mb-3 p-2.5 rounded-lg bg-[#f8fbf2] border border-[#e8f2d1] text-sm">
                                  <span className="text-gray-400 text-xs">NIN on file: </span>
                                  <span className="font-mono font-bold text-[#294c79] tracking-wider">{app.tenant_nin}</span>
                                </div>
                              )}
                              <div className="flex items-center gap-3">
                                <span className="text-xs text-gray-500">Mark as:</span>
                                {['verified', 'flagged', 'pending'].map(val => (
                                  <button
                                    key={val}
                                    onClick={() => handleVerificationCheckUpdate(app, 'nin', val)}
                                    className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition border ${
                                      (app.nin_verification_status || 'pending') === val
                                        ? val === 'verified' ? 'bg-emerald-600 text-white border-emerald-600'
                                        : val === 'flagged' ? 'bg-red-500 text-white border-red-500'
                                        : 'bg-gray-400 text-white border-gray-400'
                                        : 'bg-white text-gray-600 border-gray-200 hover:border-[#294c79]'
                                    }`}
                                  >
                                    {val === 'verified' ? ' Verified' : val === 'flagged' ? ' Flag' : ' Pending'}
                                  </button>
                                ))}
                              </div>
                            </div>

                            {/*  BVN: Portal link + manual verify  */}
                            <div className="rounded-xl border border-[#d9e5ef] bg-white p-4 mb-3">
                              <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                                <div>
                                  <p className="text-sm font-semibold text-[#294c79]">BVN Verification</p>
                                  <p className="text-xs text-gray-400 mt-0.5">Open the CBN BVN portal, enter the BVN, confirm name and bank match, then mark verified.</p>
                                  <span className={`inline-block mt-2 px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize ${verificationTone(app.bvn_verification_status || 'pending')}`}>{String(app.bvn_verification_status || 'pending').replace('_', ' ')}</span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  <button
                                    onClick={() => { navigator.clipboard?.writeText(app.tenant_bvn || ''); }}
                                    className="px-3 py-2 rounded-xl border border-[#d9e5ef] text-[#294c79] text-xs font-semibold hover:bg-[#f3f7fb] transition"
                                    title="Copy BVN to clipboard"
                                  >
                                     Copy BVN
                                  </button>
                                  <a
                                    href="https://www.bvnvalidation.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-3 py-2 rounded-xl bg-[#294c79] text-white text-xs font-semibold hover:bg-[#1f3a5c] transition"
                                  >
                                     Open BVN Portal
                                  </a>
                                </div>
                              </div>
                              {app.tenant_bvn && (
                                <div className="mb-3 p-2.5 rounded-lg bg-[#f8fbf2] border border-[#e8f2d1] text-sm">
                                  <span className="text-gray-400 text-xs">BVN on file: </span>
                                  <span className="font-mono font-bold text-[#294c79] tracking-wider">{app.tenant_bvn}</span>
                                </div>
                              )}
                              <div className="flex items-center gap-3">
                                <span className="text-xs text-gray-500">Mark as:</span>
                                {['verified', 'flagged', 'pending'].map(val => (
                                  <button
                                    key={val}
                                    onClick={() => handleVerificationCheckUpdate(app, 'bvn', val)}
                                    className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition border ${
                                      (app.bvn_verification_status || 'pending') === val
                                        ? val === 'verified' ? 'bg-emerald-600 text-white border-emerald-600'
                                        : val === 'flagged' ? 'bg-red-500 text-white border-red-500'
                                        : 'bg-gray-400 text-white border-gray-400'
                                        : 'bg-white text-gray-600 border-gray-200 hover:border-[#294c79]'
                                    }`}
                                  >
                                    {val === 'verified' ? ' Verified' : val === 'flagged' ? ' Flag' : ' Pending'}
                                  </button>
                                ))}
                              </div>
                            </div>

                            {/*  Bank Statement: view uploaded PDF + manual verify  */}
                            <div className="rounded-xl border border-[#d9e5ef] bg-white p-4 mb-3">
                              <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                                <div>
                                  <p className="text-sm font-semibold text-[#294c79]">Bank Account & Statement</p>
                                  <p className="text-xs text-gray-400 mt-0.5">Review the bank statement the tenant uploaded. Check that the account name matches their profile, credits are consistent with stated income, and the account is active.</p>
                                  <span className={`inline-block mt-2 px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize ${verificationTone(app.bank_verification_status || 'pending')}`}>{String(app.bank_verification_status || 'pending').replace('_', ' ')}</span>
                                </div>
                                <button
                                  onClick={() => handleViewDocuments(app)}
                                  className="px-3 py-2 rounded-xl bg-[#294c79] text-white text-xs font-semibold hover:bg-[#1f3a5c] transition"
                                >
                                   View Bank Statement
                                </button>
                              </div>
                              <div className="mb-3 p-2.5 rounded-lg bg-[#f8fbf2] border border-[#e8f2d1] text-sm grid grid-cols-2 gap-x-4 gap-y-1">
                                <div><span className="text-gray-400 text-xs">Bank: </span><span className="font-semibold text-[#294c79]">{app.tenant_bank_name || ''}</span></div>
                                <div><span className="text-gray-400 text-xs">Account Name: </span><span className="font-semibold text-[#294c79]">{app.tenant_bank_account_name || ''}</span></div>
                                <div><span className="text-gray-400 text-xs">Account No: </span><span className="font-mono font-semibold text-[#294c79]">{app.tenant_account_number ? `${String(app.tenant_account_number).slice(-4)}` : ''}</span></div>
                                <div><span className="text-gray-400 text-xs">Stated Income: </span><span className="font-semibold text-[#294c79]">{app.monthly_income ? `${parseFloat(app.monthly_income).toLocaleString()}/mo` : ''}</span></div>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="text-xs text-gray-500">Mark as:</span>
                                {['verified', 'flagged', 'pending'].map(val => (
                                  <button
                                    key={val}
                                    onClick={() => handleVerificationCheckUpdate(app, 'bank', val)}
                                    className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition border ${
                                      (app.bank_verification_status || 'pending') === val
                                        ? val === 'verified' ? 'bg-emerald-600 text-white border-emerald-600'
                                        : val === 'flagged' ? 'bg-red-500 text-white border-red-500'
                                        : 'bg-gray-400 text-white border-gray-400'
                                        : 'bg-white text-gray-600 border-gray-200 hover:border-[#294c79]'
                                    }`}
                                  >
                                    {val === 'verified' ? ' Verified' : val === 'flagged' ? ' Flag' : ' Pending'}
                                  </button>
                                ))}
                              </div>
                            </div>

                            {/* All Documents */}
                            <button onClick={() => handleViewDocuments(app)} className="px-5 py-2.5 rounded-xl border border-[#294c79] text-[#294c79] text-sm font-semibold hover:bg-[#f3f7fb] transition">
                               View All Submitted Documents
                            </button>
                          </div>

                          {/* Payment Summary */}
                          <div>
                            <p className="text-xs font-bold text-[#294c79] uppercase tracking-wider mb-3">Payment Plan</p>
                            <div className="grid md:grid-cols-3 gap-3 text-sm">
                              <div className="rounded-xl border border-gray-200 bg-white p-3"><p className="text-xs text-gray-500 mb-1">30% Upfront</p><p className="font-semibold">{parseFloat(app.upfront_amount || 0).toLocaleString()}</p></div>
                              <div className="rounded-xl border border-gray-200 bg-white p-3"><p className="text-xs text-gray-500 mb-1">Monthly  {app.monthly_instalments_total || 11}</p><p className="font-semibold">{parseFloat(app.monthly_instalment_amount || 0).toLocaleString()}</p></div>
                              <div className="rounded-xl border border-gray-200 bg-white p-3"><p className="text-xs text-gray-500 mb-1">Direct Debit Status</p><p className="font-semibold capitalize">{String(app.direct_debit_status || 'pending').replace('_', ' ')}</p></div>
                            </div>
                          </div>

                          {/* Stage Gating Info */}
                          {stage === 'application' && (() => {
                            const ninOk = app.nin_verification_status === 'verified';
                            const bvnOk = app.bvn_verification_status === 'verified';
                            const bankOk = app.bank_verification_status === 'verified';
                            const allOk = ninOk && bvnOk && bankOk;
                            return !allOk ? (
                              <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-800">
                                <p className="font-semibold mb-1"> Complete all checks before moving to Verification:</p>
                                <div className="flex flex-wrap gap-3 mt-1">
                                  {[['NIN', ninOk], ['BVN', bvnOk], ['Bank', bankOk]].map(([label, ok]) => (
                                    <span key={label} className={`px-2 py-1 rounded-full font-semibold ${ok ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{ok ? '' : ''} {label}</span>
                                  ))}
                                </div>
                              </div>
                            ) : null;
                          })()}

                          {app.notes && <div className="p-3 bg-white rounded-xl text-sm text-gray-600 border border-gray-200"><strong>Admin Notes:</strong> {app.notes}</div>}

                          {/* Workflow Actions */}
                          <div>
                            <p className="text-xs font-bold text-[#294c79] uppercase tracking-wider mb-3">Actions</p>
                            <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
                              {meta.next && stage !== 'rejected' && (() => {
                                // Guard: can only move to direct_debit if upfront is confirmed paid
                                if (meta.next === 'direct_debit') {
                                  const upfrontPaid = app.upfront_payment_status === 'completed' || app.workflow_stage === 'direct_debit';
                                  if (!upfrontPaid) {
                                    return (
                                      <div className="flex-1 px-5 py-3 bg-gray-100 text-gray-500 rounded-xl text-sm text-center">
                                         Waiting for tenant to pay 30% upfront before advancing to Direct Debit
                                      </div>
                                    );
                                  }
                                }
                                return (
                                  <button onClick={() => handleAdvanceApplication(app, meta.next, APPLICATION_STAGE_META[meta.next].label)} className="flex-1 px-5 py-3 bg-[#294c79] text-white rounded-xl hover:bg-[#1f3a5c] font-semibold text-sm transition">
                                     {meta.nextLabel}
                                  </button>
                                );
                              })()}
                              {stage !== 'rejected' && stage !== 'active' && (
                                <button onClick={() => handleRejectApplication(app.id)} className="px-5 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 font-semibold text-sm transition">Reject</button>
                              )}
                              {stage !== 'active' && (
                                <button onClick={() => handleDeleteApplication(app.id, app.full_name, app.house_title || app.house_name)} className="px-5 py-3 bg-gray-700 text-white rounded-xl hover:bg-gray-900 font-semibold text-sm transition">Delete</button>
                              )}
                              <a
                                href={`${API_URL}/api/admin/applications/${app.id}/download`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-5 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 font-semibold text-sm transition flex items-center gap-2"
                                onClick={e => {
                                  e.preventDefault();
                                  const token = localStorage.getItem('adminAuthToken');
                                  if (!token) return;
                                  window.open(`${API_URL}/api/admin/applications/${app.id}/download?token=${encodeURIComponent(token)}`, '_blank', 'noopener,noreferrer');
                                }}
                              >
                                 Download Application
                              </a>
                            </div>
                            {stage === 'direct_debit' && (
                              <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-xl text-xs text-purple-700">
                                 <strong>Waiting for tenant to authorise direct debit.</strong> The tenant must click "Authorise Direct Debit on Paystack" in their dashboard. Once they complete it on Paystack, the rental activates automatically. Current status: <strong>{app.direct_debit_status || 'pending'}</strong>
                              </div>
                            )}
                            {stage === 'active' && (
                              <div className="mt-3 p-3 bg-green-50 rounded-xl text-xs text-green-700"> This rental is now active. Monthly debits and receipts are visible to the tenant.</div>
                            )}
                          </div>

                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* FIX #4:  ACTIVE LEASES / RENTALS  */}
        {activeTab === 'rentals' && (
          <div>
            <div className="flex flex-wrap justify-between items-center mb-6 sm:mb-8 gap-4">
              <h1 className="text-2xl sm:text-4xl font-bold text-[#294c79]">Active Leases</h1>
              {isSuperAdmin && (
                <button
                  onClick={async () => {
                    if (!window.confirm('Run automatic monthly direct-debit charges for all active tenants who have authorised? This will charge their accounts now.')) return;
                    try {
                      const res = await axios.post(`${API_URL}/api/admin/direct-debit/run-monthly-charges`, {}, { headers });
                      alert(res.data?.message || `Done. ${res.data?.processed || 0} rental(s) processed.`);
                      await loadRentals();
                    } catch (err) {
                      alert(err.response?.data?.error || 'Failed to run charges. Check server logs.');
                    }
                  }}
                  className="px-5 py-3 bg-purple-600 text-white rounded-xl font-semibold text-sm hover:bg-purple-700 transition"
                >
                   Run Monthly Direct Debits
                </button>
              )}
            </div>

            <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6 mb-6 flex flex-col sm:flex-row sm:items-end gap-3 flex-wrap">
              <div className="flex-1 min-w-[160px]">
                <label className="block text-xs font-semibold text-[#294c79] mb-1">Status</label>
                <select
                  value={rentalStatusFilter}
                  onChange={(e) => setRentalStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-[#d9e5ef] focus:outline-none focus:ring-2 focus:ring-[#294c79]/30"
                >
                  <option value="all">All</option>
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="terminated">Terminated</option>
                </select>
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs font-semibold text-[#294c79] mb-1">Filter by Tenant ID (optional)</label>
                <input
                  type="number"
                  placeholder="e.g. 42"
                  value={rentalTenantFilter}
                  onChange={(e) => setRentalTenantFilter(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-[#d9e5ef] focus:outline-none focus:ring-2 focus:ring-[#294c79]/30"
                />
              </div>
              <button
                onClick={() => { setRentalTenantFilter(''); setRentalStatusFilter('active'); }}
                className="px-4 py-2 rounded-xl border border-[#d9e5ef] text-[#294c79] font-medium hover:bg-gray-50 text-sm"
              >
                Reset
              </button>
              <button
                onClick={loadRentals}
                className="px-4 py-2 rounded-xl bg-[#294c79] text-white font-medium hover:bg-[#1f3a5c] text-sm"
              >
                Refresh
              </button>
            </div>

            {loading ? <Spinner /> : rentals.length === 0 ? (
              <div className="bg-white p-10 rounded-2xl shadow-sm text-center text-gray-500">
                No leases match these filters.
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-max w-full text-sm">
                    <thead className="bg-[#e8f2d1]">
                      <tr>
                        {['Tenant', 'Property', 'Status', 'Rent/yr', 'Monthly', 'Paid/Total', 'Direct Debit', 'Lease Start', 'Lease End', 'Action'].map(h => (
                          <th key={h} className="px-4 sm:px-6 py-4 text-left font-semibold text-[#294c79] whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rentals.map(r => (
                        <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50">
                          <td className="px-4 sm:px-6 py-3">
                            <div className="font-semibold text-[#294c79]">{r.tenant_name}</div>
                            <div className="text-xs text-gray-500">{r.tenant_phone}</div>
                            {r.tenant_email && <div className="text-xs text-gray-500">{r.tenant_email}</div>}
                          </td>
                          <td className="px-4 sm:px-6 py-3">
                            <div className="font-semibold text-[#1a1a1a]">{r.house_title}</div>
                            <div className="text-xs text-gray-500">{r.house_location}</div>
                          </td>
                          <td className="px-4 sm:px-6 py-3 whitespace-nowrap">
                            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${
                              r.status === 'active' ? 'bg-green-100 text-green-700'
                              : r.status === 'completed' ? 'bg-blue-100 text-blue-700'
                              : 'bg-red-100 text-red-700'
                            }`}>{r.status}</span>
                          </td>
                          <td className="px-4 sm:px-6 py-3 whitespace-nowrap text-gray-700">
                            {Number(r.annual_rent || 0).toLocaleString()}
                          </td>
                          <td className="px-4 sm:px-6 py-3 whitespace-nowrap text-gray-700">
                            {Number(r.monthly_payment || 0).toLocaleString()}
                          </td>
                          <td className="px-4 sm:px-6 py-3 whitespace-nowrap text-gray-700">
                            {r.instalments_paid || 0} / {r.total_instalments || 11}
                          </td>
                          <td className="px-4 sm:px-6 py-3 whitespace-nowrap capitalize text-gray-700">
                            {(r.direct_debit_status || 'pending').replace(/_/g, ' ')}
                          </td>
                          <td className="px-4 sm:px-6 py-3 whitespace-nowrap text-gray-500">
                            {r.lease_start_date ? new Date(r.lease_start_date).toLocaleDateString() : ''}
                          </td>
                          <td className="px-4 sm:px-6 py-3 whitespace-nowrap text-gray-500">
                            {r.lease_end_date ? new Date(r.lease_end_date).toLocaleDateString() : ''}
                          </td>
                          <td className="px-4 sm:px-6 py-3 whitespace-nowrap">
                            <button
                              onClick={() => handleDeleteRental(r)}
                              className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-red-200 text-red-700 hover:bg-red-50 transition"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="px-4 sm:px-6 py-3 text-xs text-gray-500 border-t border-gray-100">
                  Showing {rentals.length} lease{rentals.length === 1 ? '' : 's'}.
                </div>
              </div>
            )}
          </div>
        )}

        {/*  PAYMENTS  */}
        {activeTab === 'payments' && (
          <PaymentsByRental API_URL={API_URL} headers={headers} isSuperAdmin={isSuperAdmin} setSensitiveConfirm={setSensitiveConfirm} />
        )}

        {/*  WAITLIST  */}
        {activeTab === 'waitlist' && (
          <div>
            <div className="flex flex-wrap gap-3 justify-between items-center mb-6 sm:mb-8">
              <h1 className="text-2xl sm:text-4xl font-bold text-[#294c79]">Interest Forms</h1>
              <div className="flex gap-3">
                <button onClick={loadWaitlist} className="px-4 py-2 bg-[#294c79] text-white rounded-xl hover:bg-[#1f3a5c] font-medium text-sm"> Refresh</button>
                <button onClick={downloadWaitlistCSV} className="px-4 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 font-medium text-sm"> Download CSV</button>
              </div>
            </div>
            {waitlistLoading ? <Spinner /> : waitlistError ? (
              <div className="bg-red-50 border border-red-200 p-8 rounded-2xl text-center">
                <p className="text-red-600 text-lg font-medium mb-4"> {waitlistError}</p>
                <button onClick={loadWaitlist} className="px-6 py-3 bg-red-600 text-white rounded-xl font-medium">Try Again</button>
              </div>
            ) : waitlist.length === 0 ? (
              <div className="bg-white p-10 rounded-2xl shadow-sm text-center"><p className="text-5xl mb-4"></p><p className="text-xl text-gray-500">No interest form entries yet</p></div>
            ) : (
              <>
                <div className="flex flex-wrap gap-4 mb-5">
                  <span className="inline-flex items-center gap-2 bg-white px-5 py-3 rounded-xl shadow-sm border border-[#e8f2d1] text-[#294c79] font-semibold"> Total: <span className="text-2xl font-bold">{waitlist.length}</span></span>
                  <span className="inline-flex items-center gap-2 bg-white px-5 py-3 rounded-xl shadow-sm border border-green-200 text-green-700 font-semibold"> Paid: <span className="text-2xl font-bold">{waitlist.filter(e => e.priority_fee_paid).length}</span></span>
                </div>
                <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-max w-full text-sm">
                      <thead className="bg-[#e8f2d1]">
                        <tr>{['#', 'Name', 'Phone', 'Email', 'House/Property', 'House Address', 'Preferred Location', 'Move-in Date', 'Budget', 'Message', 'Date Joined', 'Priority Fee Paid', 'Fee Amount', 'Paystack Ref', 'Action'].map(h => <th key={h} className="px-4 py-4 text-left font-semibold text-[#294c79] whitespace-nowrap">{h}</th>)}</tr>
                      </thead>
                      <tbody>
                        {waitlist.map((entry, i) => (
                          <tr key={entry.id} className="border-t border-gray-100 hover:bg-gray-50 align-top">
                            <td className="px-4 py-3 text-gray-500 font-semibold">{i + 1}</td>
                            <td className="px-4 py-3 font-semibold text-[#294c79] whitespace-nowrap">{entry.full_name || ''}</td>
                            <td className="px-4 py-3 whitespace-nowrap">{entry.phone ? <a href={`tel:${entry.phone}`} className="text-blue-600 hover:underline">{entry.phone}</a> : ''}</td>
                            <td className="px-4 py-3">{entry.email ? <a href={`mailto:${entry.email}`} className="text-blue-600 hover:underline">{entry.email}</a> : ''}</td>
                            <td className="px-4 py-3 font-medium text-[#294c79]">{entry.house_title || <span className="text-gray-400 italic text-xs">Any</span>}</td>
                            <td className="px-4 py-3 text-gray-600 min-w-[220px]">{entry.house_address || ''}</td>
                            <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{entry.location || ''}</td>
                            <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{entry.move_in_date ? new Date(entry.move_in_date).toLocaleDateString('en-NG', { year: 'numeric', month: 'short', day: 'numeric' }) : ''}</td>
                            <td className="px-4 py-3 font-medium text-[#294c79] whitespace-nowrap">{entry.budget ? `${parseFloat(entry.budget).toLocaleString()}` : ''}</td>
                            <td className="px-4 py-3 text-gray-600 min-w-[260px]">{entry.message || ''}</td>
                            <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{entry.joined_at ? new Date(entry.joined_at).toLocaleDateString('en-NG', { year: 'numeric', month: 'short', day: 'numeric' }) : ''}</td>
                            <td className="px-4 py-3"><span className={`px-3 py-1 rounded-full text-xs font-semibold ${entry.priority_fee_paid ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{entry.priority_fee_paid ? ' Yes' : 'No'}</span></td>
                            <td className="px-4 py-3 font-semibold text-[#294c79] whitespace-nowrap">{entry.priority_fee_paid ? `${parseFloat(entry.fee_amount || 0).toLocaleString()}` : ''}</td>
                            <td className="px-4 py-3 text-xs text-gray-500 min-w-[150px]">{entry.paystack_reference || ''}</td>
                            <td className="px-4 py-3"><button onClick={() => handleRemoveFromWaitlist(entry.id)} className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-xs font-medium whitespace-nowrap"> Remove</button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/*  SUPPORT TICKETS  */}
        {activeTab === 'tickets' && (
          <div>
            <div className="flex flex-wrap gap-3 justify-between items-center mb-6 sm:mb-8">
              <h1 className="text-2xl sm:text-4xl font-bold text-[#294c79]">Support Tickets</h1>
              <div className="flex gap-2">
                <button onClick={loadTickets} className="px-4 py-2 bg-[#294c79] text-white rounded-xl hover:bg-[#1f3a5c] font-medium text-sm"> Refresh</button>
                <button onClick={downloadTicketsCSV} className="px-4 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 font-medium text-sm"> Download CSV</button>
              </div>
            </div>
            <div className="flex gap-2 mb-6 flex-wrap">
              {[{ value: 'open', label: ' Open' }, { value: 'in_progress', label: ' In Progress' }, { value: 'resolved', label: ' Resolved' }, { value: 'closed', label: ' Closed' }].map(s => (
                <button key={s.value} onClick={() => setTicketFilter(s.value)}
                  className={`px-4 py-2 rounded-xl font-medium text-sm transition ${ticketFilter === s.value ? 'bg-[#294c79] text-white' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'}`}>
                  {s.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              <div className="lg:col-span-2 space-y-3">
                {loading ? <Spinner /> : tickets.length === 0 ? (
                  <div className="bg-white p-10 rounded-2xl shadow-sm text-center"><p className="text-4xl mb-3"></p><p className="text-gray-500">No {ticketFilter.replace('_', ' ')} tickets</p></div>
                ) : (
                  tickets.map(ticket => (
                    <div key={ticket.id} onClick={() => loadTicketDetail(ticket.id)}
                      className={`bg-white p-4 rounded-2xl shadow-sm border-2 cursor-pointer transition hover:shadow-md ${selectedTicket?.id === ticket.id ? 'border-[#294c79]' : 'border-transparent hover:border-gray-200'}`}>
                      <div className="flex justify-between items-start mb-2 gap-2">
                        <h3 className="font-semibold text-[#294c79] text-sm leading-tight">{ticket.subject}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold whitespace-nowrap border ${ticketStatusColors[ticket.status] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>{ticket.status?.replace('_', ' ')}</span>
                      </div>
                      <p className="text-xs text-gray-600 mb-1"> <span className="font-medium">{ticket.tenant_name}</span></p>
                      <p className="text-xs text-gray-500 mb-2">{ticket.tenant_phone}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex gap-1">
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded capitalize">{ticket.category}</span>
                          <span className={`text-xs px-2 py-0.5 rounded capitalize ${ticket.priority === 'urgent' ? 'bg-red-100 text-red-600' : ticket.priority === 'high' ? 'bg-orange-100 text-orange-600' : 'bg-gray-100 text-gray-500'}`}>{ticket.priority}</span>
                        </div>
                        <span className="text-xs text-gray-400">{new Date(ticket.updated_at).toLocaleDateString('en-NG')}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div className="lg:col-span-3">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 flex flex-col sticky top-4" style={{ minHeight: '500px', maxHeight: '80vh' }}>
                  {!selectedTicket ? (
                    <div className="flex-1 flex items-center justify-center text-gray-400 text-center p-10">
                      <div><p className="text-5xl mb-4"></p><p className="font-medium text-gray-500">Select a ticket to view details</p><p className="text-sm text-gray-400 mt-1">Click any ticket on the left to open it</p></div>
                    </div>
                  ) : ticketDetailLoading ? (
                    <div className="flex-1 flex items-center justify-center"><Spinner /></div>
                  ) : (
                    <>
                      <div className="p-5 border-b border-gray-100 flex-shrink-0">
                        <div className="flex justify-between items-start gap-3 mb-3">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-bold text-[#294c79] text-lg leading-tight mb-1">{selectedTicket.subject}</h3>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                              <span> {selectedTicket.tenant_name}</span>
                              <span> {selectedTicket.tenant_phone}</span>
                              {selectedTicket.tenant_email && <span> {selectedTicket.tenant_email}</span>}
                              <span> {new Date(selectedTicket.created_at).toLocaleString('en-NG')}</span>
                            </div>
                          </div>
                          <span className={`text-xs px-3 py-1 rounded-full font-semibold border flex-shrink-0 ${ticketStatusColors[selectedTicket.status] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>{selectedTicket.status?.replace('_', ' ')}</span>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-2 font-medium">Update Status:</p>
                          <div className="flex gap-2 flex-wrap">
                            {[{ value: 'open', label: 'Open', color: 'bg-blue-600' }, { value: 'in_progress', label: 'In Progress', color: 'bg-orange-500' }, { value: 'resolved', label: 'Resolved', color: 'bg-green-600' }, { value: 'closed', label: 'Closed', color: 'bg-gray-500' }].map(s => (
                              <button key={s.value} onClick={() => handleUpdateTicketStatus(selectedTicket.id, s.value)}
                                className={`text-xs px-3 py-1.5 rounded-lg font-medium transition ${selectedTicket.status === s.value ? `${s.color} text-white` : 'border border-gray-200 text-gray-600 hover:border-gray-400'}`}>
                                {s.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="flex-1 overflow-y-auto p-4 space-y-4">
                        <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="w-7 h-7 bg-[#294c79] rounded-full flex items-center justify-center text-white text-xs font-bold">{selectedTicket.tenant_name?.[0]?.toUpperCase()}</span>
                            <div>
                              <p className="text-xs font-semibold text-[#294c79]">{selectedTicket.tenant_name} <span className="text-gray-400 font-normal">(Tenant)</span></p>
                              <p className="text-xs text-gray-400">{new Date(selectedTicket.created_at).toLocaleString('en-NG')}</p>
                            </div>
                          </div>
                          <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">{selectedTicket.message}</p>
                        </div>
                        {ticketReplies.map(reply => (
                          <div key={reply.id} className={`rounded-2xl p-4 border ${reply.is_admin_reply ? 'bg-[#f0f7e4] border-[#d4e8a0] ml-4' : 'bg-blue-50 border-blue-100 mr-4'}`}>
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold ${reply.is_admin_reply ? 'bg-[#a5c250]' : 'bg-blue-500'}`}>{reply.is_admin_reply ? '' : reply.replier_name?.[0]?.toUpperCase()}</span>
                              <div>
                                <p className={`text-xs font-semibold ${reply.is_admin_reply ? 'text-[#294c79]' : 'text-blue-700'}`}>{reply.replier_name} <span className="font-normal text-gray-400">({reply.is_admin_reply ? 'Admin' : 'Tenant'})</span></p>
                                <p className="text-xs text-gray-400">{new Date(reply.created_at).toLocaleString('en-NG')}</p>
                              </div>
                            </div>
                            <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">{reply.message}</p>
                          </div>
                        ))}
                        {ticketReplies.length === 0 && <p className="text-center text-xs text-gray-400 py-4">No replies yet  be the first to respond</p>}
                      </div>
                      <div className="p-4 border-t border-gray-100 flex-shrink-0">
                        <p className="text-xs font-medium text-gray-500 mb-2">Reply to tenant:</p>
                        <textarea value={replyMessage} onChange={e => setReplyMessage(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) handleSendReply(); }} placeholder="Type your reply... (Ctrl+Enter to send)" className="w-full px-4 py-3 border-2 border-[#afc09e] rounded-xl text-sm focus:outline-none focus:border-[#294c79] h-24 resize-none transition" />
                        <div className="flex items-center justify-between mt-2">
                          <p className="text-xs text-gray-400">Ctrl+Enter to send</p>
                          <button onClick={handleSendReply} disabled={!replyMessage.trim() || sendingReply} className="px-6 py-2.5 bg-[#294c79] text-white rounded-xl font-medium text-sm hover:bg-[#1f3a5c] disabled:bg-gray-300 disabled:cursor-not-allowed transition flex items-center gap-2">
                            {sendingReply ? <><div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> Sending...</> : ' Send Reply'}
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/*  NOTIFICATIONS  */}
        {activeTab === 'notifications' && (
          <div>
            <div className="flex flex-wrap gap-3 justify-between items-center mb-6 sm:mb-8">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl sm:text-4xl font-bold text-[#294c79]">Notifications</h1>
                {unreadCount > 0 && (
                  <span className="bg-red-500 text-white text-sm font-bold px-2.5 py-1 rounded-full">{unreadCount} new</span>
                )}
              </div>
              <div className="flex gap-2 flex-wrap">
                {unreadCount > 0 && (
                  <button onClick={markAllNotificationsRead} disabled={markingRead}
                    className="px-4 py-2 bg-gray-600 text-white rounded-xl hover:bg-gray-700 font-medium text-sm disabled:opacity-50">
                    {markingRead ? 'Marking...' : ' Mark All Read'}
                  </button>
                )}
                <button onClick={openNotifModal} className="px-4 py-2 bg-[#a5c250] text-white rounded-xl hover:bg-[#8bb83a] font-medium text-sm"> Send Notification</button>
              </div>
            </div>
            <div className="flex gap-2 mb-6">
              <button onClick={() => setNotifSubTab('received')} className={`px-5 py-2.5 rounded-xl font-medium text-sm transition ${notifSubTab === 'received' ? 'bg-[#294c79] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>Received Notifications ({notifications.length})</button>
              <button onClick={() => { setNotifSubTab('sent'); loadSentNotifications(); }} className={`px-5 py-2.5 rounded-xl font-medium text-sm transition ${notifSubTab === 'sent' ? 'bg-[#294c79] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>Send Notification Records ({sentNotifications.length})</button>
            </div>
            {notifSubTab === 'received' && (
              <>
                <div className="flex gap-2 mb-5 flex-wrap">
                  {['all', 'payment', 'application', 'ticket', 'ticket_reply', 'general'].map(t => (
                    <button key={t} onClick={() => setNotifFilter(t)} className={`px-4 py-2 rounded-xl font-medium text-xs capitalize transition ${notifFilter === t ? 'bg-[#294c79] text-white' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'}`}>{t.replace('_', ' ')}</button>
                  ))}
                </div>
                {loading ? <Spinner /> : notifications.length === 0 ? (
                  <div className="bg-white p-10 rounded-2xl shadow-sm text-center text-xl text-gray-500">No notifications</div>
                ) : (
                  <div className="space-y-3">
                    {notifications.map(notif => (
                      <div key={notif.id}
                        onClick={() => { if (!notif.is_read) markNotificationRead(notif.id); }}
                        className={`bg-white p-5 rounded-2xl shadow-sm border-l-4 transition-all ${notif.is_read ? 'border-gray-200 opacity-80' : 'border-[#a5c250] cursor-pointer hover:shadow-md'}`}>
                        <div className="flex flex-wrap justify-between items-start gap-3 mb-2">
                          <div className="flex items-center gap-2">
                            <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${notif.type === 'payment' ? 'bg-purple-500' : notif.type === 'application' ? 'bg-green-500' : notif.type.includes('ticket') ? 'bg-blue-500' : 'bg-gray-400'}`} />
                            <span className="font-semibold text-[#294c79] text-sm capitalize">{notif.type.replace('_', ' ')}</span>
                            {!notif.is_read && <span className="bg-[#a5c250] text-white text-xs px-2 py-0.5 rounded-full animate-pulse">New</span>}
                            {notif.is_read && <span className="text-gray-400 text-xs"> Read</span>}
                          </div>
                          <span className="text-xs text-gray-400">{new Date(notif.created_at).toLocaleString('en-NG')}</span>
                        </div>
                        <p className="font-semibold text-gray-800 mb-1">{notif.title}</p>
                        <p className="text-gray-600 text-sm">{notif.message}</p>
                        {notif.from_user && <p className="mt-2 text-xs text-gray-500">From: {notif.from_user}</p>}
                        {notif.status && <div className="mt-2"><StatusBadge status={notif.status} /></div>}
                        {!notif.is_read && <p className="mt-2 text-xs text-[#a5c250]">Click to mark as read</p>}
                        {notif.related_type === 'ticket' && notif.related_id && (
                          <button onClick={(e) => { e.stopPropagation(); setActiveTab('tickets'); loadTicketDetail(notif.related_id); }} className="mt-2 text-xs text-blue-600 hover:underline">View Ticket </button>
                        )}
                        <div className="mt-3 flex justify-end">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); deleteAdminNotification(notif.id); }}
                            disabled={!!deletingNotifIds[notif.id]}
                            className="text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50"
                          >
                            {deletingNotifIds[notif.id] ? 'Deleting...' : 'Delete'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
            {notifSubTab === 'sent' && (
              <>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-gray-500">Notifications initiated by admins and sent to tenants or admins</p>
                  <button onClick={loadSentNotifications} className="text-xs text-[#294c79] hover:underline"> Refresh</button>
                </div>
                {sentNotifications.length === 0 ? (
                  <div className="bg-white p-10 rounded-2xl shadow-sm text-center"><p className="text-gray-500 text-lg">No notifications sent yet</p><p className="text-gray-400 text-sm mt-2">Click "Send Notification" above to send one to a recipient</p></div>
                ) : (
                  <div className="space-y-3">
                    {sentNotifications.map(notif => (
                      <div key={notif.id} className="bg-white p-5 rounded-2xl shadow-sm border border-gray-200">
                        <div className="flex flex-wrap justify-between items-start gap-3 mb-3">
                          <div className="flex items-center gap-2">
                            <span className="w-8 h-8 bg-[#a5c250] rounded-full flex items-center justify-center text-white text-sm font-bold"></span>
                            <div>
                              <p className="font-semibold text-[#294c79] text-sm">{notif.title}</p>
                              <p className="text-xs text-gray-500">To: <span className="font-medium">{notif.tenant_name || notif.tenant_email || `Recipient #${notif.user_id}`}</span></p>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="text-xs px-2 py-1 rounded-full font-medium capitalize bg-blue-100 text-blue-700">{notif.type}</span>
                            <p className="text-xs text-gray-400 mt-1">{new Date(notif.created_at).toLocaleString('en-NG')}</p>
                          </div>
                        </div>
                        <p className="text-sm text-gray-700 bg-gray-50 rounded-xl px-4 py-3">{notif.message}</p>
                        <div className="mt-3 flex items-center gap-2 flex-wrap">
                          <span className="text-xs text-gray-400">Sent by:</span>
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{notif.from_user || 'Admin'}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${notif.is_read ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{notif.is_read ? ' Read' : ' Unread'}</span>
                          <button
                            type="button"
                            onClick={() => deleteAdminNotification(notif.id, true)}
                            disabled={!!deletingNotifIds[notif.id]}
                            className="ml-auto text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50"
                          >
                            {deletingNotifIds[notif.id] ? 'Deleting...' : 'Delete'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'contact-messages' && (
          <div>
            <div className="flex flex-wrap gap-3 justify-between items-center mb-6 sm:mb-8">
              <div>
                <h1 className="text-2xl sm:text-4xl font-bold text-[#294c79]">Contact Messages</h1>
                <p className="text-sm text-gray-500 mt-2">Messages submitted from the public contact page now land here for admin follow-up.</p>
              </div>
              <button onClick={loadContactMessages} className="px-4 py-2 bg-white border border-gray-200 text-[#294c79] rounded-xl hover:bg-gray-50 font-medium text-sm"> Refresh</button>
            </div>
            <div className="flex gap-2 mb-6 flex-wrap">
              {['all', 'new', 'read', 'resolved'].map(status => (
                <button
                  key={status}
                  onClick={() => setContactStatusFilter(status)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium capitalize transition ${contactStatusFilter === status ? 'bg-[#294c79] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                >
                  {status}
                </button>
              ))}
            </div>
            {loading ? <Spinner /> : contactMessages.length === 0 ? (
              <div className="bg-white p-10 rounded-2xl shadow-sm text-center"><p className="text-4xl mb-4"></p><p className="text-gray-500">No contact messages yet</p></div>
            ) : (
              <div className="space-y-4">
                {contactMessages.map(item => (
                  <div key={item.id} className={`bg-white p-5 sm:p-6 rounded-2xl shadow-sm border ${item.is_read ? 'border-gray-200' : 'border-[#a5c250]'}`}>
                    <div className="flex flex-wrap justify-between items-start gap-3 mb-3">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h3 className="text-lg font-semibold text-[#294c79]">{item.name}</h3>
                          <span className={`text-xs px-2 py-1 rounded-full font-medium capitalize ${item.status === 'resolved' ? 'bg-green-100 text-green-700' : item.is_read ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>{item.status}</span>
                          {!item.is_read && <span className="text-xs px-2 py-1 rounded-full bg-[#a5c250] text-white">New</span>}
                        </div>
                        <p className="text-sm text-gray-600">{item.email}</p>
                        <p className="text-xs text-gray-400 mt-1">{new Date(item.created_at).toLocaleString('en-NG')}</p>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {!item.is_read && (
                          <button onClick={() => markContactMessageRead(item.id, 'read')} className="px-3 py-2 rounded-xl bg-[#294c79] text-white text-xs font-medium hover:bg-[#1f3a5c]">Mark Read</button>
                        )}
                        {item.status !== 'resolved' && (
                          <button onClick={() => markContactMessageRead(item.id, 'resolved')} className="px-3 py-2 rounded-xl bg-green-600 text-white text-xs font-medium hover:bg-green-700">Resolve</button>
                        )}
                        <button onClick={() => deleteContactMessage(item.id)} disabled={!!deletingContactIds[item.id]} className="px-3 py-2 rounded-xl border border-red-200 text-red-600 text-xs font-medium hover:bg-red-50 disabled:opacity-50">
                          {deletingContactIds[item.id] ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-2xl px-4 py-4 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{item.message}</div>
                    {item.admin_reply && (
                      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-xl text-sm text-[#294c79]">
                        <p className="text-xs font-semibold text-blue-500 mb-1">Admin Reply:</p>
                        <p className="whitespace-pre-wrap">{item.admin_reply}</p>
                      </div>
                    )}
                    {!item.admin_reply && (
                      <ContactReplyForm
                        itemId={item.id}
                        headers={headers}
                        API_URL={API_URL}
                        onReplied={() => loadContactMessages()}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/*  TENANT SUBMISSIONS  */}
        {activeTab === 'tenant-submissions' && (
          <div>
            <h1 className="text-2xl sm:text-4xl font-bold text-[#294c79] mb-6 sm:mb-8">Tenant House Submissions</h1>
            <p className="text-gray-500 mb-6 text-sm">Houses submitted by tenants for admin review. Click any submission to expand it. Mark as Approved to notify the tenant they can apply.</p>
            <div className="flex gap-2 mb-6 flex-wrap">
              {['all', 'submitted', 'reviewing', 'approved', 'rejected'].map(s => (
                <button key={s} onClick={() => setSubmissionsFilter(s)} className={`px-4 py-2 rounded-xl text-sm font-medium capitalize transition ${submissionsFilter === s ? 'bg-[#294c79] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                  {s === 'all' ? ' All' : s === 'submitted' ? ' Submitted' : s === 'reviewing' ? ' Reviewing' : s === 'approved' ? ' Approved' : ' Rejected'}
                </button>
              ))}
            </div>
            {loading ? <Spinner /> : tenantSubmissions.filter(s => submissionsFilter === 'all' || s.status === submissionsFilter).length === 0 ? (
              <div className="bg-white p-10 rounded-2xl shadow-sm text-center"><p className="text-4xl mb-4"></p><p className="text-gray-500">No tenant house submissions yet</p></div>
            ) : (
              <div className="space-y-3">
                {tenantSubmissions.filter(s => submissionsFilter === 'all' || s.status === submissionsFilter).map(sub => {
                  const statusColors = { submitted: 'bg-blue-100 text-blue-700', reviewing: 'bg-yellow-100 text-yellow-700', approved: 'bg-green-100 text-green-700', rejected: 'bg-red-100 text-red-700' };
                  const images = sub.images || [];
                  const isOpen = expandedSubId === sub.id;
                  return (
                    <div key={sub.id} className={`rounded-2xl border transition-all overflow-hidden ${isOpen ? 'border-[#294c79] shadow-lg' : 'border-gray-200 bg-white shadow-sm hover:border-[#294c79]/40 hover:shadow-md'}`}>
                      {/*  Clickable Summary Row  */}
                      <button onClick={() => setExpandedSubId(prev => prev === sub.id ? null : sub.id)} className="w-full text-left px-5 py-4 bg-white flex items-center justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <span className="text-lg"></span>
                            <span className="font-bold text-[#294c79] text-base truncate">{sub.address}</span>
                          </div>
                          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-400">
                            <span> {sub.location}</span>
                            {sub.landlord_rent && <span> {Number(sub.landlord_rent).toLocaleString()}/yr</span>}
                            <span> {sub.tenant_name || 'Unknown'}</span>
                            <span> {new Date(sub.created_at).toLocaleDateString('en-NG')}</span>
                            <span> {images.length} photo{images.length !== 1 ? 's' : ''}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className={`text-xs px-2 py-1 rounded-full font-semibold capitalize ${statusColors[sub.status] || 'bg-gray-100 text-gray-600'}`}>{sub.status}</span>
                          <svg className={`w-5 h-5 text-[#294c79] transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </button>

                      {/*  Expanded Detail  */}
                      {isOpen && (
                        <div className="border-t border-gray-100 bg-[#fbfcfe] px-5 py-5 space-y-5">
                          {/* Property info */}
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                            {[
                              ['Address', sub.address],
                              ['Location', sub.location],
                              ['Rent', sub.landlord_rent ? `${Number(sub.landlord_rent).toLocaleString()}/yr` : ''],
                              ['Landlord Contact', sub.landlord_contact || ''],
                              ['Submitted By', sub.tenant_name || ''],
                              ['Tenant Phone', sub.tenant_phone || ''],
                            ].map(([k, v]) => (
                              <div key={k} className="rounded-xl bg-white border border-[#edf2f7] p-3">
                                <p className="text-xs text-gray-400 mb-0.5">{k}</p>
                                <p className="font-semibold text-[#1a1a1a] text-sm">{v}</p>
                              </div>
                            ))}
                          </div>

                          {sub.notes && <p className="text-sm text-gray-500 italic bg-white rounded-xl border border-gray-200 p-3"> Tenant note: "{sub.notes}"</p>}
                          {sub.admin_notes && <p className="text-sm text-[#294c79] bg-blue-50 border border-blue-200 px-3 py-2.5 rounded-xl"> Admin Note: {sub.admin_notes}</p>}

                          {/* Images */}
                          {images.length > 0 && (
                            <div>
                              <p className="text-xs font-bold text-[#294c79] uppercase tracking-wider mb-3">Property Photos ({images.length})</p>
                              <div className="flex gap-2 flex-wrap">
                                {images.map((img, idx) => (
                                  <button key={idx} onClick={() => openLightbox(images, idx)} className="relative w-24 h-24 rounded-xl overflow-hidden border-2 border-gray-200 hover:border-[#294c79] transition group flex-shrink-0">
                                    <img src={img} alt={`Property ${idx + 1}`} className="w-full h-full object-cover" onError={e => { e.target.src = '/Logo.jpg'; }} />
                                    <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 flex items-center justify-center transition">
                                      <span className="text-white text-xs font-semibold"> View</span>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Status update + approved message */}
                          <div className="space-y-3">
                            <div className="flex flex-wrap gap-3 items-center">
                              <span className="text-sm font-semibold text-[#294c79]">Update Status:</span>
                              {['submitted','reviewing','approved','rejected'].map(s => (
                                <button
                                  key={s}
                                  onClick={async () => {
                                    const note = window.prompt(`Add admin note for "${s}" status (optional):`);
                                    if (note === null) return;
                                    await handleUpdateSubmission(sub.id, s, note || undefined);
                                  }}
                                  className={`px-4 py-2 rounded-xl text-xs font-bold transition border ${sub.status === s ? (s === 'approved' ? 'bg-green-600 text-white border-green-600' : s === 'rejected' ? 'bg-red-500 text-white border-red-500' : s === 'reviewing' ? 'bg-yellow-500 text-white border-yellow-500' : 'bg-blue-500 text-white border-blue-500') : 'bg-white text-gray-600 border-gray-200 hover:border-[#294c79]'}`}
                                >
                                  {s === 'approved' ? ' Approve' : s === 'rejected' ? ' Reject' : s === 'reviewing' ? ' Reviewing' : ' Reset'}
                                </button>
                              ))}
                            </div>
                            {sub.status === 'approved' && (
                              <div className="p-3 rounded-xl bg-green-50 border border-green-200 text-sm text-green-800">
                                 <strong>Approved.</strong> The tenant ({sub.tenant_name}) has been notified to apply for this house from their dashboard.
                              </div>
                            )}
                            {sub.status === 'rejected' && (
                              <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-800 flex flex-wrap justify-between items-center gap-3">
                                <span> <strong>Rejected.</strong> You can delete this submission to free up storage.</span>
                                <button
                                  onClick={() => handleDeleteTenantSubmission(sub)}
                                  className="px-3 py-1.5 rounded-lg border border-red-300 text-red-700 text-xs font-semibold hover:bg-red-100 transition whitespace-nowrap"
                                >
                                   Delete Submission
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}


        {/*  DIRECT DEBIT AUDIT LOG  */}
        {activeTab === 'debit-audit' && (
          <DebitAuditLog API_URL={API_URL} headers={headers} />
        )}

        {activeTab === 'debit-requests' && (
          <DebitChangeRequests API_URL={API_URL} headers={headers} />
        )}

        {activeTab === 'tenant-logs' && (
          <TenantActivityLogs API_URL={API_URL} headers={headers} />
        )}

        {activeTab === 'audit-logs' && (
          <AuditLogsTab API_URL={API_URL} headers={headers} />
        )}

        {/*  ADMIN ACTIVITY LOG (SuperAdmin only)  */}
        {activeTab === 'admin-logs' && (
          <div>
            <div className="flex flex-wrap justify-between items-center mb-8 gap-4">
              <div>
                <h1 className="text-2xl sm:text-4xl font-bold text-[#294c79]">Admin Activity Log</h1>
                <p className="text-sm text-gray-500 mt-1">All actions taken by admin accounts. Visible to Super Admin only.</p>
              </div>
              <button onClick={loadAdminLogs} className="px-4 py-2.5 rounded-xl border border-[#294c79] text-[#294c79] text-sm font-medium hover:bg-[#f3f7fb] transition"> Refresh</button>
            </div>
            {adminLogsLoading ? (
              <div className="flex items-center justify-center py-20"><div className="animate-spin h-10 w-10 border-4 border-[#294c79] border-t-transparent rounded-full" /></div>
            ) : adminLogs.length === 0 ? (
              <div className="text-center py-20 text-gray-400">
                <div className="text-5xl mb-4"></div>
                <p className="font-semibold text-lg">No admin activity recorded yet</p>
                <p className="text-sm">Actions like approvals, stage changes, and deletions will appear here.</p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <table className="min-w-max w-full text-sm">
                  <thead>
                    <tr className="bg-[#f7f9fc] border-b border-[#e8edf5]">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#294c79] uppercase tracking-wide">Admin</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#294c79] uppercase tracking-wide">Action</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#294c79] uppercase tracking-wide hidden md:table-cell">Detail</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#294c79] uppercase tracking-wide">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminLogs.map((log, i) => (
                      <tr key={log.id} className={`border-b border-[#f0f4fa] ${i % 2 === 0 ? 'bg-white' : 'bg-[#fafbfd]'} hover:bg-[#f3f7fb] transition`}>
                        <td className="px-4 py-3">
                          <div className="font-semibold text-[#294c79]">{log.admin_name || 'Admin'}</div>
                          <div className="text-xs text-gray-400">{log.admin_email}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-block px-2.5 py-1 rounded-full text-xs font-semibold bg-[#e8f2d1] text-[#294c79]">{log.event_title}</span>
                        </td>
                        <td className="px-4 py-3 text-gray-500 hidden md:table-cell max-w-xs truncate">{log.event_detail || ''}</td>
                        <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">
                          {new Date(log.created_at).toLocaleString('en-NG', { dateStyle: 'medium', timeStyle: 'short' })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/*  MANAGE ADMINS  */}
        {activeTab === 'manage-admins' && (
          <div>
            <h1 className="text-2xl sm:text-4xl font-bold text-[#294c79] mb-6 sm:mb-10">Manage Admins</h1>
            {!isSuperAdmin ? (
              <div className="bg-red-50 p-10 rounded-2xl shadow-sm text-center"><p className="text-xl text-red-700"> Only the Super Admin can access this section</p></div>
            ) : (
              <div className="space-y-6 sm:space-y-8">
                <div className="bg-white p-5 sm:p-8 rounded-2xl shadow-sm">
                  <h2 className="text-xl sm:text-2xl font-semibold text-[#294c79] mb-5">Create New Admin</h2>
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    const fd = new FormData(e.target);
                    try {
                      await axios.post(`${API_URL}/api/admin/create-admin`, { fullName: fd.get('fullName'), phone: fd.get('phone'), email: fd.get('email'), password: fd.get('password'), role: fd.get('role') }, { headers });
                      alert(' Admin created!');
                      e.target.reset();
                      await loadUsers();
                    } catch (err) { alert(err.response?.data?.error || 'Failed to create admin'); }
                  }} className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    {[['fullName', 'Full Name', 'text', true], ['phone', 'Phone', 'tel', true], ['email', 'Email', 'email', false], ['password', 'Password', 'password', true]].map(([name, label, type, req]) => (
                      <div key={name}>
                        <label className="block text-sm font-medium text-[#294c79] mb-2">{label}{req ? ' *' : ''}</label>
                        <input name={name} type={type} required={req} minLength={name === 'password' ? 8 : undefined} className="w-full px-4 py-3 border-2 border-[#afc09e] rounded-xl focus:outline-none focus:border-[#a5c250]" />
                      </div>
                    ))}
                    <div>
                      <label className="block text-sm font-medium text-[#294c79] mb-2">Role *</label>
                      <select name="role" required className="w-full px-4 py-3 border-2 border-[#afc09e] rounded-xl focus:outline-none focus:border-[#a5c250]">
                        <option value="admin">Admin</option>
                        <option value="super_admin">Super Admin</option>
                      </select>
                    </div>
                    <div className="flex items-end">
                      <button type="submit" className="w-full px-5 py-3 bg-[#a5c250] text-white rounded-xl hover:bg-[#8bb83a] font-medium">Create Admin</button>
                    </div>
                  </form>
                </div>
                <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                  <div className="p-4 sm:p-6 border-b"><h2 className="text-xl sm:text-2xl font-semibold text-[#294c79]">All Admins</h2></div>
                  <div className="overflow-x-auto">
                    <table className="min-w-max w-full text-sm">
                      <thead className="bg-[#e8f2d1]">
                        <tr>{['Name', 'Phone', 'Email', 'Role', 'Permissions', 'Actions'].map(h => <th key={h} className="px-4 sm:px-6 py-4 text-left font-semibold text-[#294c79] whitespace-nowrap">{h}</th>)}</tr>
                      </thead>
                      <tbody>
                        {users.filter(u => u.role === 'admin' || u.role === 'super_admin').map(admin => (
                          <tr key={admin.id} className="border-t border-gray-100 hover:bg-gray-50">
                            <td className="px-4 sm:px-6 py-3 font-medium whitespace-nowrap">{admin.full_name}{admin.id === currentUser?.id && <span className="ml-1 text-xs bg-blue-100 text-blue-600 px-1 rounded">You</span>}</td>
                            <td className="px-4 sm:px-6 py-3 whitespace-nowrap">{admin.phone}</td>
                            <td className="px-4 sm:px-6 py-3">{admin.email || ''}</td>
                            <td className="px-4 sm:px-6 py-3"><span className={`px-2 py-1 rounded-full text-xs font-semibold ${admin.role === 'super_admin' ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}`}>{admin.role}</span></td>
                            <td className="px-4 sm:px-6 py-3">
                              {admin.role === 'super_admin' ? <span className="text-xs text-gray-500 italic">All permissions</span> : (
                                <div className="flex flex-wrap gap-1">
                                  {(admin.permissions || []).length === 0 ? <span className="text-xs text-gray-400 italic">No permissions</span> : (admin.permissions || []).slice(0, 2).map(p => (<span key={p} className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">{ALL_PERMISSIONS.find(ap => ap.key === p)?.label || p}</span>))}
                                  {(admin.permissions || []).length > 2 && <span className="text-xs text-gray-500">+{admin.permissions.length - 2} more</span>}
                                </div>
                              )}
                            </td>
                            <td className="px-4 sm:px-6 py-3">
                              <div className="flex gap-2">
                                {admin.role !== 'super_admin' && <button onClick={() => openPermissionModal(admin)} className="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700">Permissions</button>}
                                {admin.id !== currentUser?.id && <button onClick={() => handleDeleteAdmin(admin.id)} className="px-3 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700">Delete</button>}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/*  LANDLORDS  */}
        {activeTab === 'landlords' && (
          <LandlordsTab API_URL={API_URL} headers={headers}
            landlords={landlords} setLandlords={setLandlords}
            selectedLandlord={selectedLandlord} setSelectedLandlord={setSelectedLandlord}
            landlordPayments={landlordPayments} setLandlordPayments={setLandlordPayments}
            loadLandlords={loadLandlords} loadLandlordPayments={loadLandlordPayments}
            setSensitiveConfirm={setSensitiveConfirm}
          />
        )}

        {/*  MICROFINANCE BANKS  */}
        {activeTab === 'microfinance' && (
          <MicrofinanceTab API_URL={API_URL} headers={headers}
            mfbs={mfbs} setMFBs={setMFBs}
            selectedMFB={selectedMFB} setSelectedMFB={setSelectedMFB}
            mfbDisbursements={mfbDisbursements} setMFBDisbursements={setMFBDisbursements}
            loadMFBs={loadMFBs} loadMFBDisbursements={loadMFBDisbursements}
            setSensitiveConfirm={setSensitiveConfirm}
          />
        )}

      </main>

      {/*  EDIT HOUSE MODAL  */}
      {showEditModal && editingHouse && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full sm:max-w-2xl max-h-[95vh] overflow-y-auto">
            <div className="p-5 sm:p-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-[#294c79]">Edit House</h2>
                <button onClick={() => { setShowEditModal(false); setEditingHouse(null); }} className="text-3xl text-gray-400 hover:text-red-600 leading-none" aria-label="Close edit modal">x</button>
              </div>
              <form onSubmit={handleEditSubmit} className="space-y-5">
                {[['Title', 'title', 'text'], ['Annual Rent (NGN)', 'annual_rent', 'number'], ['Location', 'location', 'text'], ['Address', 'address', 'text']].map(([label, field, type]) => (
                  <div key={field}>
                    <label className="block text-sm font-medium text-[#294c79] mb-2">{label}</label>
                    <input type={type} value={editForm[field]} onChange={e => setEditForm({ ...editForm, [field]: e.target.value })} className="w-full px-4 py-3 border-2 border-[#afc09e] rounded-xl focus:outline-none focus:border-[#a5c250]" required={['title', 'annual_rent'].includes(field)} />
                  </div>
                ))}
                <div>
                  <label className="block text-sm font-medium text-[#294c79] mb-2">Description</label>
                  <textarea value={editForm.description} onChange={e => setEditForm({ ...editForm, description: e.target.value })} className="w-full px-4 py-3 border-2 border-[#afc09e] rounded-xl h-24 focus:outline-none focus:border-[#a5c250]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#294c79] mb-2">Amenities (comma-separated)</label>
                  <input type="text" value={editForm.amenities} onChange={e => setEditForm({ ...editForm, amenities: e.target.value })} className="w-full px-4 py-3 border-2 border-[#afc09e] rounded-xl focus:outline-none focus:border-[#a5c250]" />
                </div>

                {/*  COVER PHOTO PICKER  */}
                {editingHouse?.images?.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-[#294c79] mb-2">
                      Cover Photo
                      <span className="ml-2 text-xs font-normal text-gray-500">(the image shown on the browse page)</span>
                    </label>
                    <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                      {editingHouse.images.map((img, idx) => (
                        <button
                          type="button"
                          key={idx}
                          onClick={() => setEditForm({ ...editForm, cover_image_index: idx })}
                          className={`relative rounded-xl overflow-hidden border-3 transition group ${
                            parseInt(editForm.cover_image_index) === idx
                              ? 'border-[#a5c250] ring-2 ring-[#a5c250] ring-offset-1'
                              : 'border-gray-200 hover:border-[#a5c250]/50'
                          }`}
                          style={{ aspectRatio: '1', borderWidth: '3px' }}
                        >
                          <img
                            src={img.startsWith('http') ? img : `${API_URL}${img}`}
                            alt={`Preview ${idx + 1}`}
                            className="w-full h-full object-cover"
                          />
                          {parseInt(editForm.cover_image_index) === idx && (
                            <div className="absolute inset-0 bg-[#a5c250]/20 flex items-center justify-center">
                              <span className="bg-[#a5c250] text-white text-xs font-bold px-1.5 py-0.5 rounded-full"> Cover</span>
                            </div>
                          )}
                          {parseInt(editForm.cover_image_index) !== idx && (
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                              <span className="bg-white/90 text-gray-700 text-xs font-medium px-1.5 py-0.5 rounded-full">Set cover</span>
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-gray-400 mt-1.5">
                       Click any photo to set it as the cover image. Currently showing photo {parseInt(editForm.cover_image_index) + 1} of {editingHouse.images.length}.
                    </p>
                  </div>
                )}

                {/*  DISPLAY ORDER IN EDIT MODAL  */}
                <div>
                  <label className="block text-sm font-medium text-[#294c79] mb-2">
                    Display Order
                    <span className="ml-2 text-xs font-normal text-gray-500">(position on Browse page)</span>
                  </label>
                  <input
                    type="number" min="1"
                    value={editForm.display_order}
                    onChange={e => setEditForm({ ...editForm, display_order: e.target.value })}
                    placeholder="e.g. 1 = appears first"
                    className="w-full px-4 py-3 border-2 border-[#afc09e] rounded-xl focus:outline-none focus:border-[#a5c250]"
                  />
                  <p className="text-xs text-gray-400 mt-1.5"> Enter <strong>1</strong> to show this house first. You can also use the <strong>"Set Display Order"</strong> button on the Houses page to drag and reorder all houses visually.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#294c79] mb-2">House Status</label>
                  <select value={editForm.status} onChange={e => setEditForm({ ...editForm, status: e.target.value })} className="w-full px-4 py-3 border-2 border-[#afc09e] rounded-xl focus:outline-none focus:border-[#a5c250]">
                    <option value="pending">Pending (hidden from browse page)</option>
                    <option value="verified">Verified (visible on browse page)</option>
                    <option value="sample">Sample House</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1"> Only "Verified" houses appear on the public browse page</p>
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={() => { setShowEditModal(false); setEditingHouse(null); }} className="flex-1 py-3 border-2 border-gray-300 rounded-xl font-medium">Cancel</button>
                  <button type="submit" disabled={loading} className="flex-1 py-3 bg-[#a5c250] text-white rounded-xl font-medium hover:bg-[#8bb83a] disabled:bg-gray-400">{loading ? 'Saving' : 'Save Changes'}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/*  PERMISSION MODAL  */}
      {showPermissionModal && editingAdmin && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-md w-full p-8 max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-[#294c79] mb-2">Assign Permissions</h2>
            <p className="text-sm text-gray-500 mb-6">Admin: <strong>{editingAdmin.full_name}</strong></p>
            <div className="space-y-3">
              {ALL_PERMISSIONS.map(perm => (
                <label key={perm.key} className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded-xl transition">
                  <input type="checkbox" checked={selectedPermissions.includes(perm.key)} onChange={() => togglePermission(perm.key)} className="w-5 h-5 accent-[#a5c250]" />
                  <span className="text-lg">{perm.icon}</span>
                  <span className="text-gray-700">{perm.label}</span>
                </label>
              ))}
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setSelectedPermissions(ALL_PERMISSIONS.map(p => p.key))} className="text-xs text-blue-600 hover:underline">Select All</button>
              <span className="text-gray-300">|</span>
              <button onClick={() => setSelectedPermissions([])} className="text-xs text-red-600 hover:underline">Clear All</button>
            </div>
            <div className="flex gap-3 mt-8">
              <button onClick={() => setShowPermissionModal(false)} className="flex-1 py-4 border-2 border-gray-300 rounded-2xl font-medium">Cancel</button>
              <button onClick={savePermissions} className="flex-1 py-4 bg-[#a5c250] text-white rounded-2xl font-medium hover:bg-[#8bb83a]">Save Permissions</button>
            </div>
          </div>
        </div>
      )}

      {/*  SEND NOTIFICATION MODAL  */}
      {showNotifModal && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-[#294c79]">Send Notification</h2>
              <button onClick={() => { setShowNotifModal(false); setTenantSearch(''); setNotifForm({ userId: '', type: 'general', title: '', message: '' }); }} className="text-3xl text-gray-400 hover:text-red-600 leading-none" aria-label="Close notification modal">x</button>
            </div>
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-[#294c79] mb-2">Select Recipient <span className="text-red-500">*</span></label>
                <input type="text" value={tenantSearch} onChange={e => { setTenantSearch(e.target.value); setNotifForm(prev => ({ ...prev, userId: '' })); }} placeholder="Search by name, phone or email..." className="w-full px-4 py-3 border-2 border-[#afc09e] rounded-xl focus:outline-none focus:border-[#a5c250] mb-2 text-sm" />
                {selectedTenant && (
                  <div className="flex items-center gap-2 mb-2 p-3 bg-[#e8f2d1] rounded-xl">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[#294c79] text-sm truncate">{selectedTenant.full_name}</p>
                      <p className="text-xs text-gray-500 truncate">{selectedTenant.role?.replace('_', ' ') || 'recipient'}{selectedTenant.phone ? ` - ${selectedTenant.phone}` : ''}{selectedTenant.email ? ` - ${selectedTenant.email}` : ''}</p>
                    </div>
                    <button onClick={() => setNotifForm(prev => ({ ...prev, userId: '' }))} className="text-red-500 hover:text-red-700 text-lg font-bold flex-shrink-0" aria-label="Remove selected recipient">x</button>
                  </div>
                )}
                {!selectedTenant && (
                  <div className="border-2 border-[#afc09e] rounded-xl overflow-hidden max-h-48 overflow-y-auto">
                    {tenantsLoading ? (
                      <div className="flex items-center justify-center py-8 gap-2"><div className="animate-spin h-5 w-5 border-2 border-[#a5c250] border-t-transparent rounded-full" /><span className="text-sm text-gray-500">Loading recipients...</span></div>
                    ) : filteredTenants.length === 0 ? (
                      <div className="py-8 text-center text-sm text-gray-400">{tenantSearch ? 'No recipients match your search' : 'No recipients found'}</div>
                    ) : (
                      filteredTenants.map(tenant => (
                        <button key={tenant.id} onClick={() => { setNotifForm(prev => ({ ...prev, userId: String(tenant.id) })); setTenantSearch(''); }} className="w-full text-left px-4 py-3 hover:bg-[#f0f7e4] border-b border-gray-100 last:border-0 transition">
                          <p className="font-medium text-[#294c79] text-sm">{tenant.full_name}</p>
                          <p className="text-xs text-gray-500">{tenant.role?.replace('_', ' ') || 'recipient'}{tenant.phone ? ` - ${tenant.phone}` : ''}{tenant.email ? ` - ${tenant.email}` : ''}</p>
                        </button>
                      ))
                    )}
                  </div>
                )}
                <p className="text-xs text-gray-400 mt-1">{tenantsList.length} recipient(s) available</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#294c79] mb-2">Notification Type</label>
                <select value={notifForm.type} onChange={e => setNotifForm({ ...notifForm, type: e.target.value })} className="w-full px-4 py-3 border-2 border-[#afc09e] rounded-xl focus:outline-none focus:border-[#a5c250]">
                  <option value="general">General</option>
                  <option value="payment">Payment</option>
                  <option value="application">Application</option>
                  <option value="ticket">Ticket</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#294c79] mb-2">Title <span className="text-red-500">*</span></label>
                <input type="text" value={notifForm.title} onChange={e => setNotifForm({ ...notifForm, title: e.target.value })} placeholder="Notification title..." className="w-full px-4 py-3 border-2 border-[#afc09e] rounded-xl focus:outline-none focus:border-[#a5c250]" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#294c79] mb-2">Message <span className="text-red-500">*</span></label>
                <textarea value={notifForm.message} onChange={e => setNotifForm({ ...notifForm, message: e.target.value })} placeholder="Message body..." className="w-full px-4 py-3 border-2 border-[#afc09e] rounded-xl h-24 focus:outline-none focus:border-[#a5c250] resize-none" />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => { setShowNotifModal(false); setTenantSearch(''); setNotifForm({ userId: '', type: 'general', title: '', message: '' }); }} className="flex-1 py-4 border-2 border-gray-300 rounded-2xl font-medium hover:bg-gray-50">Cancel</button>
              <button onClick={handleSendNotification} disabled={!notifForm.userId || !notifForm.title || !notifForm.message} className="flex-1 py-4 bg-[#a5c250] text-white rounded-2xl font-medium hover:bg-[#8bb83a] disabled:bg-gray-300 disabled:cursor-not-allowed transition"> Send</button>
            </div>
          </div>
        </div>
      )}

      {/*  IMAGE LIGHTBOX  */}
      {showLightbox && lightboxImages.length > 0 && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setShowLightbox(false)}>
          <div className="relative max-w-3xl w-full" onClick={e => e.stopPropagation()}>
            <button onClick={() => setShowLightbox(false)} className="absolute -top-10 right-0 text-white text-3xl hover:text-gray-300 font-bold" aria-label="Close image viewer">x</button>
            <img src={lightboxImages[lightboxIdx]?.startsWith('http') ? lightboxImages[lightboxIdx] : `${API_URL}${lightboxImages[lightboxIdx]}`} alt={`Property ${lightboxIdx + 1}`} className="w-full max-h-[80vh] object-contain rounded-2xl" />
            <div className="flex justify-center gap-3 mt-4 flex-wrap">
              {lightboxImages.map((img, idx) => (
                <button key={idx} onClick={() => setLightboxIdx(idx)} className={`w-14 h-14 rounded-xl overflow-hidden border-2 transition ${lightboxIdx === idx ? 'border-[#a5c250]' : 'border-white/30'}`}>
                  <img src={img?.startsWith('http') ? img : `${API_URL}${img}`} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
            <div className="flex justify-between mt-4">
              <button onClick={() => setLightboxIdx(prev => Math.max(0, prev - 1))} disabled={lightboxIdx === 0} className="px-4 py-2 bg-white/20 text-white rounded-xl disabled:opacity-30 hover:bg-white/30 transition"> Prev</button>
              <span className="text-white text-sm self-center">{lightboxIdx + 1} / {lightboxImages.length}</span>
              <button onClick={() => setLightboxIdx(prev => Math.min(lightboxImages.length - 1, prev + 1))} disabled={lightboxIdx === lightboxImages.length - 1} className="px-4 py-2 bg-white/20 text-white rounded-xl disabled:opacity-30 hover:bg-white/30 transition">Next </button>
            </div>
          </div>
        </div>
      )}

      {/*  TENANT DOCUMENTS MODAL  */}
      {docsModal.open && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setDocsModal({ open: false, loading: false, docs: null, appId: null, tenantId: null })}>
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-[#294c79] text-white px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <div>
                <h2 className="text-lg font-bold">Submitted Documents</h2>
                {docsModal.docs && <p className="text-sm text-white/70">{docsModal.docs.full_name}  {docsModal.docs.email}</p>}
              </div>
              <button onClick={() => setDocsModal({ open: false, loading: false, docs: null, appId: null, tenantId: null })} className="text-white/80 hover:text-white text-2xl font-bold leading-none" aria-label="Close documents modal">x</button>
            </div>
            <div className="p-6 space-y-6">
              {docsModal.loading ? (
                <div className="text-center py-10 text-[#294c79] font-medium">Loading documents</div>
              ) : docsModal.docs ? (
                <>
                  {/* Standard documents */}
                  <div>
                    <p className="text-xs font-semibold text-[#294c79] uppercase tracking-wide mb-3">Standard Documents</p>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {[
                        ['Government-issued ID', docsModal.docs.id_doc_url],
                        ['Passport Photo', docsModal.docs.passport_photo_url],
                        ['Utility Bill', docsModal.docs.utility_bill_url],
                        ['Guarantor 1 ID', docsModal.docs.guarantor1_id_url],
                        ['Guarantor 2 ID', docsModal.docs.guarantor2_id_url],
                        ...(docsModal.docs.applicant_type === 'student' ? [
                          ['Student ID', docsModal.docs.student_id_url],
                          ['Admission Letter', docsModal.docs.admission_letter_url],
                          ['School Fees / Course Registration', docsModal.docs.school_fees_receipt_url],
                          ['Parent/Guardian ID', docsModal.docs.parent_id_url],
                          ['Parent/Guardian Consent', docsModal.docs.parent_consent_url],
                          ['Proof of Parent Employment/Business', docsModal.docs.parent_proof_of_employment_url],
                          ['Parent Bank Statement', docsModal.docs.parent_bank_statement_url],
                        ] : docsModal.docs.applicant_type === 'nysc' ? [
                          ['NYSC Call-Up Letter', docsModal.docs.nysc_call_up_letter_url],
                          ['NYSC Deployment Letter', docsModal.docs.nysc_deployment_letter_url],
                          ['NYSC ID Card', docsModal.docs.nysc_id_card_url],
                        ] : [
                          ['Proof of Income', docsModal.docs.proof_of_income_url],
                          ['Bank Statement', docsModal.docs.bank_statement_url],
                          ['Employment Letter', docsModal.docs.employment_letter_url],
                        ]),
                      ].map(([label, url]) => (
                        <div key={label} className="rounded-xl border border-[#edf2f7] bg-[#fbfcfe] p-4">
                          <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">{label}</p>
                          <DocLink url={url} label="Open" />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Signed verification forms */}
                  {[docsModal.docs.signed_guarantor1_form_url, docsModal.docs.signed_guarantor2_form_url,
                    docsModal.docs.signed_employer_form_url, docsModal.docs.signed_business_owner_form_url].some(Boolean) && (
                    <div>
                      <p className="text-xs font-semibold text-[#294c79] uppercase tracking-wide mb-3">Signed Verification Forms</p>
                      <div className="grid sm:grid-cols-2 gap-4">
                        {[
                          [' Signed Guarantor 1 Form', docsModal.docs.signed_guarantor1_form_url],
                          [' Signed Guarantor 2 Form', docsModal.docs.signed_guarantor2_form_url],
                          [' Signed Employer Letter', docsModal.docs.signed_employer_form_url],
                          [' Signed Business Owner Form', docsModal.docs.signed_business_owner_form_url],
                        ].filter(([, url]) => url).map(([label, url]) => (
                          <div key={label} className="rounded-xl border border-[#a5c250]/40 bg-[#f8fbf2] p-4">
                            <p className="text-xs text-[#294c79] uppercase tracking-wide mb-2 font-semibold">{label}</p>
                            <DocLink url={url} label="Open" />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Requested documents  uploaded by tenant */}
                  {docRequests.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-[#294c79] uppercase tracking-wide mb-3">Requested Documents</p>
                      <div className="space-y-3">
                        {docRequests.map(req => (
                          <div key={req.id} className={`rounded-xl border p-4 flex items-start justify-between gap-3 ${req.status === 'uploaded' ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
                            <div className="flex-1">
                              <p className="font-semibold text-sm text-[#294c79]">{req.document_name}</p>
                              {req.description && <p className="text-xs text-gray-500 mt-0.5">{req.description}</p>}
                              <p className="text-xs text-gray-400 mt-1">Requested {new Date(req.created_at).toLocaleDateString('en-NG')}</p>
                              {req.status === 'uploaded' ? (
                                <div className="mt-2">
                                  <span className="text-xs text-green-600 font-semibold"> Uploaded {new Date(req.uploaded_at).toLocaleDateString('en-NG')}</span>
                                  {req.uploaded_url && (
                                    <button onClick={() => openDoc(req.uploaded_url)} className="ml-3 text-xs text-[#294c79] underline font-semibold">Open Document </button>
                                  )}
                                </div>
                              ) : (
                                <span className="text-xs text-amber-600 font-semibold mt-1 inline-block"> Awaiting upload from tenant</span>
                              )}
                            </div>
                            <button onClick={() => handleDeleteDocRequest(req.id)} className="text-red-400 hover:text-red-600 text-lg flex-shrink-0" title="Remove request" aria-label="Remove request">x</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Request additional document form */}
                  <div className="border-t border-gray-100 pt-5">
                    <p className="text-xs font-semibold text-[#294c79] uppercase tracking-wide mb-3"> Request Additional Document from Tenant</p>
                    <div className="space-y-3">
                      <input
                        type="text"
                        placeholder="Document name (e.g. Recent utility bill, Letter of employment)"
                        value={docRequestForm.name}
                        onChange={e => setDocRequestForm(p => ({ ...p, name: e.target.value }))}
                        className="w-full px-4 py-3 border border-[#d9e5ef] rounded-xl text-sm focus:outline-none focus:border-[#294c79]"
                      />
                      <textarea
                        rows={2}
                        placeholder="Description / instructions for tenant (optional)"
                        value={docRequestForm.description}
                        onChange={e => setDocRequestForm(p => ({ ...p, description: e.target.value }))}
                        className="w-full px-4 py-3 border border-[#d9e5ef] rounded-xl text-sm focus:outline-none focus:border-[#294c79] resize-none"
                      />
                      <button
                        onClick={handleRequestDocument}
                        disabled={docRequestLoading || !docRequestForm.name.trim()}
                        className="w-full px-5 py-3 bg-[#294c79] text-white rounded-xl font-semibold text-sm hover:bg-[#1f3a5c] disabled:opacity-50 transition"
                      >
                        {docRequestLoading ? ' Sending' : ' Send Document Request to Tenant'}
                      </button>
                      <p className="text-xs text-gray-400 text-center">Tenant will receive an in-app notification and email with upload instructions.</p>
                    </div>
                  </div>

                  {/* Tenant self-uploaded PDFs */}
                  <TenantPdfViewer
                    tenantId={docsModal.tenantId}
                    API_URL={API_URL}
                    headers={headers}
                  />
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* FIX #5: Password-confirm modal for sensitive admin actions. */}
      {sensitiveConfirm && (
        <PasswordConfirmModal
          title={sensitiveConfirm.title}
          message={sensitiveConfirm.message}
          confirmButtonLabel={sensitiveConfirm.confirmButtonLabel}
          authToken={localStorage.getItem('adminAuthToken')}
          onConfirm={sensitiveConfirm.onConfirm}
          onCancel={() => setSensitiveConfirm(null)}
        />
      )}

    </div>
  );
}


